import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, Plus, Search, LogOut, Hash, Copy, Check,
  Send, Image, Mic, Smile, X, MoreVertical, Trash2, DoorOpen, ChevronLeft,
  Menu, Reply, Loader2, Edit2, Settings, Users, MessageSquare,
  CheckCheck, Play, Pause, RotateCcw, AlertCircle, Video, Paperclip, Maximize2, Download
} from 'lucide-react';
import axios from 'axios';
import { createPortal, flushSync } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useToast } from '../components/Toast';
import {
  captureChatLayout,
  runChatLayoutTransition,
  animateMessageEntry,
  animateTypingEntry,
  spawnTypingExitGhost,
  dismissTypingGhost,
  accelerateEntriesExcept,
  hasActiveEntries,
  registerEntryAnimation,
  consumeEntryAnimation,
  cancelAllChatAnimations,
  CHAT_ANIM,
  TYPING_ROW_KEY,
} from '../utils/chatLayoutAnimation';
import {
  advanceBoundary,
  compareBoundaries,
  decodeCacheEnvelope,
  encodeCacheEnvelope,
  getMonotonicDeliveryPatch,
  messageIsAtOrBeforeBoundary,
  normalizeBoundary,
  rememberBoundedRealtimeId,
} from '../utils/dmReliability';

// Emoji picker is heavy (@emoji-mart data is large) and only appears on click, so
// load it lazily into its own chunk — kept out of the initial bundle. The dynamic
// import pulls BOTH the picker component and its data only when first needed.
const LazyEmojiPicker = lazy(() =>
  Promise.all([import('@emoji-mart/react'), import('@emoji-mart/data')]).then(
    ([pickerMod, dataMod]) => {
      const Picker = pickerMod.default;
      const data = dataMod.default;
      return {
        default: (props) => <Picker data={data} {...props} />,
      };
    }
  )
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Purely-presentational message entrance animations. Default ON (missing/unset var
// keeps current behavior); set REACT_APP_ENABLE_MESSAGE_ANIMATIONS=false to disable
// all message-bubble animation and render exactly like the non-animated baseline.
const MESSAGE_ANIMATIONS_ENABLED = process.env.REACT_APP_ENABLE_MESSAGE_ANIMATIONS !== 'false';
const TYPING_START_EMIT_INTERVAL_MS = 1200;
const MAX_REACTIONS_PER_USER = 3;
const LIST_CACHE_MAX_ROWS = 80;
const LIST_CACHE_MAX_BYTES = 120000;
const APP_SYNC_TIMEOUT_MS = 12000;

const withRequestTimeout = (promise, timeoutMs, label, onTimeout) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => {
    try { onTimeout?.(); } catch {}
    reject(new Error(`${label} timed out after ${timeoutMs}ms`));
  }, timeoutMs);
  Promise.resolve(promise).then(
    (value) => { clearTimeout(timer); resolve(value); },
    (error) => { clearTimeout(timer); reject(error); }
  );
});

const isBootTimingDebug = () => {
  try {
    return localStorage.getItem('whisper_debug_boot_timing') === 'true';
  } catch {
    return false;
  }
};

const logBootTiming = (event, extra = {}) => {
  if (!isBootTimingDebug()) return;
  console.info('[whisper boot]', event, {
    t: Math.round(performance.now()),
    ...extra,
  });
};

const resolveMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

// True only when the app is genuinely in front of the user right now: the tab is
// visible AND the window is focused. Used to decide whether a DM is "actually being
// viewed" before we mark it read — receiving a message, a background reload, a
// reconnect, or a hidden/mounted chat must NOT count as reading. Defaults to true if
// the APIs are unavailable so we never over-suppress a genuine read.
const isDocumentActivelyViewed = () => {
  try {
    if (typeof document === 'undefined') return true;
    const visible = document.visibilityState ? document.visibilityState === 'visible' : true;
    const focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    return visible && focused;
  } catch {
    return true;
  }
};

// Quick reactions for messages
const QUICK_REACTIONS = ['\u{1F44D}', '\u{2764}\u{FE0F}', '\u{1F602}', '\u{1F62E}', '\u{1F622}', '\u{1F525}'];

// Clean a stored preview string for display WITHOUT ever stripping legitimate
// emoji. Emoji-only and emoji-leading messages are valid text — removing their
// emoji was blanking/garbling DM-list previews. We only:
//   - drop U+FFFD replacement characters (genuine decode garbage),
//   - collapse an accidental doubled media label ("Photo Photo" -> "Photo"),
//   - normalize whitespace.
// All other content (emoji, ZWJ sequences, skin tones, flags, Hindi, etc.) is
// preserved exactly.
const stripLegacyMediaEmoji = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/�/gu, '')
    .replace(/^(Photo|Image|Video|Voice message)\s+(Photo|Image|Video|Voice message)$/i, '$2')
    .replace(/\s+/gu, ' ')
    .trim();
};

// Grapheme-safe truncation: never splits an astral emoji surrogate pair, a ZWJ
// sequence, a skin-tone/variation modifier, or a flag. Uses Intl.Segmenter when
// available (no new dependency) and falls back to Array-from spreading (which is
// code-point aware) so it degrades gracefully.
const truncateGraphemes = (text, max) => {
  if (typeof text !== 'string' || text.length <= max) return text;
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
      const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      const out = [];
      for (const { segment } of seg.segment(text)) {
        if (out.length >= max) return `${out.join('')}…`;
        out.push(segment);
      }
      return out.join('');
    }
  } catch {
    // fall through to the code-point fallback
  }
  const chars = Array.from(text); // code-point aware (won't split surrogate pairs)
  return chars.length > max ? `${chars.slice(0, max).join('')}…` : text;
};

const normalizeReactionUsers = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  if (typeof value === 'string') return [value];
  if (typeof value === 'object') {
    if (Array.isArray(value.users)) return value.users.filter(Boolean);
    if (Array.isArray(value.user_ids)) return value.user_ids.filter(Boolean);
    return Object.values(value).filter((item) => typeof item === 'string');
  }
  return [];
};

const normalizeReactions = (reactions = {}) => {
  if (!reactions || typeof reactions !== 'object' || Array.isArray(reactions)) return {};

  return Object.entries(reactions).reduce((acc, [emoji, users]) => {
    const normalizedUsers = normalizeReactionUsers(users);
    if (normalizedUsers.length > 0) acc[emoji] = normalizedUsers;
    return acc;
  }, {});
};


// Draft storage helpers
const getDraft = (conversationType, conversationId) => {
  try {
    const drafts = JSON.parse(localStorage.getItem('whisper_drafts') || '{}');
    return drafts[`${conversationType}_${conversationId}`] || '';
  } catch {
    return '';
  }
};

const saveDraft = (conversationType, conversationId, text) => {
  try {
    const drafts = JSON.parse(localStorage.getItem('whisper_drafts') || '{}');
    const key = `${conversationType}_${conversationId}`;
    if (text.trim()) {
      drafts[key] = text;
    } else {
      delete drafts[key];
    }
    localStorage.setItem('whisper_drafts', JSON.stringify(drafts));
  } catch (e) {
    console.error('Failed to save draft:', e);
  }
};

// Debounced draft persistence: localStorage is synchronous and the draft effect
// fires on EVERY keystroke. Instead of re-parsing + re-writing the whole drafts
// object per keystroke, hold the pending write in memory and flush at most once
// per 400ms of silence. Pending writes are CANCELLED (not flushed) on unmount
// so a logout — which wipes whisper_* storage — can never be resurrected by a
// stale timer belonging to the previous account.
const pendingDraftSaves = new Map(); // key -> { timer, conversationType, conversationId }

const scheduleDraftSave = (conversationType, conversationId, text, delayMs = 400) => {
  const key = `${conversationType}_${conversationId}`;
  const existing = pendingDraftSaves.get(key);
  if (existing) clearTimeout(existing.timer);
  const timer = setTimeout(() => {
    pendingDraftSaves.delete(key);
    saveDraft(conversationType, conversationId, text);
  }, delayMs);
  pendingDraftSaves.set(key, { timer, conversationType, conversationId });
};

const clearScheduledDraft = (conversationType, conversationId) => {
  const key = `${conversationType}_${conversationId}`;
  const existing = pendingDraftSaves.get(key);
  if (existing) {
    clearTimeout(existing.timer);
    pendingDraftSaves.delete(key);
  }
};

const cancelPendingDraftSaves = () => {
  pendingDraftSaves.forEach(({ timer }) => clearTimeout(timer));
  pendingDraftSaves.clear();
};


const sanitizeListCacheRows = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row) => row && typeof row === 'object' && row.id)
    .slice(0, LIST_CACHE_MAX_ROWS)
    .map((row) => ({
      ...row,
      other_user: row.other_user
        ? { ...row.other_user, profile_picture: undefined }
        : row.other_user,
    }));
};

const deferStorageTask = (task) => {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    return window.requestIdleCallback(task, { timeout: 1200 });
  }
  return setTimeout(task, 0);
};

// Per-user cache namespacing: all localStorage cache keys are scoped to the
// currently authenticated immutable user id, so a different account signing in
// on the same browser can never read the previous account's cached lists/history.
let _cacheUserId = null;
const setCacheUserId = (id) => { _cacheUserId = id ? String(id) : null; };
const nsKey = (key) => (_cacheUserId ? `${key}::u_${_cacheUserId}` : key);

const getListCache = (key) => {
  try {
    const storageKey = nsKey(key);
    const raw = localStorage.getItem(storageKey);
    if (!raw || raw.length > LIST_CACHE_MAX_BYTES) return [];
    const decoded = decodeCacheEnvelope(raw);
    const rows = sanitizeListCacheRows(decoded.data);
    if (decoded.migrated || !decoded.valid) {
      localStorage.setItem(storageKey, encodeCacheEnvelope(rows));
    }
    return rows;
  } catch {
    return [];
  }
};

const saveListCache = (key, value) => {
  const rows = sanitizeListCacheRows(value);
  const storageKey = nsKey(key);
  deferStorageTask(() => {
    try {
      localStorage.setItem(storageKey, encodeCacheEnvelope(rows));
    } catch (e) {
      console.error(`Failed to save cache for ${key}:`, e);
    }
  });
};

const getMessageCache = (key) => {
  try {
    const storageKey = nsKey(key);
    const decoded = decodeCacheEnvelope(localStorage.getItem(storageKey));
    if (decoded.migrated || !decoded.valid) {
      localStorage.setItem(storageKey, encodeCacheEnvelope(decoded.data));
    }
    return decoded.data;
  } catch {
    return [];
  }
};

const saveMessageCache = (key, messages, maxItems = 50) => {
  try {
    const trimmed = Array.isArray(messages) ? messages.slice(-maxItems) : [];
    localStorage.setItem(nsKey(key), encodeCacheEnvelope(trimmed));
  } catch (e) {
    console.error(`Failed to save message cache for ${key}:`, e);
  }
};

// Remove a message from a persisted (namespaced) message cache by ANY identity
// field (id / tempId / client_temp_id). Used on delete so a refresh cannot
// resurrect a deleted message from localStorage. No-op if the key is absent.
const removeMessageFromCache = (cacheKey, messageId) => {
  if (!cacheKey || !messageId) return;
  try {
    const raw = localStorage.getItem(nsKey(cacheKey));
    if (!raw) return;
    const arr = decodeCacheEnvelope(raw).data;
    if (!Array.isArray(arr)) return;
    const target = String(messageId);
    const next = arr.filter((m) => {
      if (!m) return false;
      return ![m.id, m.tempId, m.client_temp_id].filter(Boolean).map(String).includes(target);
    });
    if (next.length !== arr.length) {
      localStorage.setItem(nsKey(cacheKey), encodeCacheEnvelope(next));
    }
  } catch {
    // Cache mutation must never throw into the delete flow.
  }
};

const sanitizeMessagesForCache = (msgs) => {
  return (msgs || []).filter((msg) => {
    if (!msg) return false;
    if (msg.pending) return false;
    if (msg.tempId) return false;
    if (!msg.id) return false;
    if (String(msg.id).startsWith('temp_')) return false;
    return true;
  }).map((msg) => {
    // Never persist the non-durable realtime guard flag into localStorage.
    if (msg && msg._realtime_received_at != null) {
      const { _realtime_received_at, ...rest } = msg;
      return rest;
    }
    return msg;
  });
};

const isTempMessageId = (id) => Boolean(id) && String(id).startsWith('temp_');

// Parse a server timestamp safely. Backend emits UTC (usually with a +00:00
// offset), but if a value ever arrives naive (no Z / no offset) it is treated as
// UTC by appending 'Z' — never as browser-local. Returned Date is then rendered
// in the user's own device timezone by the callers.
const parseServerTimestamp = (value) => {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  const raw = String(value).trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  return new Date(hasTimezone ? raw : `${raw}Z`);
};

const normalizeMessageForState = (message) => {
  if (!message) return null;
  return {
    ...message,
    reactions: normalizeReactions(message.reactions),
  };
};

const normalizeMessagesForState = (messages) => {
  return (Array.isArray(messages) ? messages : [])
    .map(normalizeMessageForState)
    .filter(Boolean);
};

const getMessageIdentityKeys = (message) => {
  return [message?.id, message?.tempId, message?.client_temp_id]
    .filter(Boolean)
    .map(String);
};

const filterMessagesByDeletedIds = (messages, deletedIdsRef) => {
  const deletedIds = deletedIdsRef?.current;
  if (!deletedIds?.size) return messages || [];

  // Hot path: runs on socket handlers, cache effects and merges. Inline the
  // identity check instead of building an array + String map per message.
  // Falsy entries are kept (they have no identity keys) — identical to the
  // original semantics.
  return (messages || []).filter((message) => {
    if (!message) return true;
    const { id, tempId, client_temp_id: clientTempId } = message;
    if (id != null && deletedIds.has(String(id))) return false;
    if (tempId != null && deletedIds.has(String(tempId))) return false;
    if (clientTempId != null && deletedIds.has(String(clientTempId))) return false;
    return true;
  });
};

const rememberDeletedMessage = (deletedIdsRef, message) => {
  const keys = getMessageIdentityKeys(message);
  if (keys.length === 0 && message) keys.push(String(message));
  keys.forEach((key) => deletedIdsRef.current.add(key));
  return keys;
};

const forgetDeletedMessage = (deletedIdsRef, keys) => {
  (keys || []).forEach((key) => deletedIdsRef.current.delete(key));
};

const preventComposerBlur = (event) => {
  event.preventDefault();
};

// Writes a CSS variable on <html> only when the value actually changed, so
// rapid resyncs never invalidate style for the whole app for nothing.
const setWhisperViewportVar = (name, value) => {
  const next = `${Math.round(value)}px`;
  if (document.documentElement.style.getPropertyValue(name) !== next) {
    document.documentElement.style.setProperty(name, next);
  }
};

const syncWhisperViewportHeight = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const viewport = window.visualViewport;
  // Keyboard-driven viewport changes happen at scale 1. While the user is
  // intentionally pinch-zooming, keep the app shell stable and leave panning to
  // the browser instead of resizing/translating the complete interface.
  const isPinchZoomed = Boolean(viewport && Math.abs((viewport.scale || 1) - 1) > 0.01);
  const height = isPinchZoomed
    ? window.innerHeight
    : (viewport?.height || window.innerHeight);
  const width = isPinchZoomed
    ? window.innerWidth
    : (viewport?.width || window.innerWidth);
  if (!height) return;
  setWhisperViewportVar('--whisper-app-height', height);
  if (width) {
    setWhisperViewportVar('--whisper-app-width', width);
    setWhisperViewportVar('--whisper-viewport-width', width);
  }
  // Keyboard-open detection: when the on-screen keyboard shrinks the visual
  // viewport, the app height already excludes the keyboard, so the composer must
  // NOT add extra safe-area padding (that would leave a gap above the keyboard).
  const layoutHeight = window.innerHeight || height;
  const activeElement = document.activeElement;
  const editableFocused = Boolean(
    activeElement
    && (
      activeElement.tagName === 'TEXTAREA'
      || activeElement.tagName === 'SELECT'
      || (activeElement.tagName === 'INPUT' && activeElement.type !== 'file')
    )
  );
  const screenHeight = window.screen?.height || layoutHeight;
  const keyboardOpen = !isPinchZoomed && editableFocused && (
    layoutHeight - height > 120
    || screenHeight - height > 180
  );
  // Some iOS/WebKit in-app browsers scroll the layout viewport to reveal a
  // focused composer. Apply only a non-negative vertical offset while the keyboard
  // is truly open. Never consume offsetLeft: that causes sideways drift/cropping.
  const viewportTop = keyboardOpen ? Math.max(0, viewport?.offsetTop || 0) : 0;
  setWhisperViewportVar('--whisper-viewport-top', viewportTop);
  const kbInset = keyboardOpen ? '0px' : 'env(safe-area-inset-bottom)';
  if (document.documentElement.style.getPropertyValue('--whisper-kb-inset') !== kbInset) {
    document.documentElement.style.setProperty('--whisper-kb-inset', kbInset);
  }
};

let whisperViewportRafId = null;
let whisperViewportTimerIds = [];

const clearScheduledWhisperViewportSync = () => {
  if (typeof window === 'undefined') return;
  if (whisperViewportRafId !== null) {
    window.cancelAnimationFrame(whisperViewportRafId);
    whisperViewportRafId = null;
  }
  whisperViewportTimerIds.forEach((timerId) => window.clearTimeout(timerId));
  whisperViewportTimerIds = [];
};

const scheduleWhisperViewportSync = () => {
  if (typeof window === 'undefined') return;
  // Perf: never sync synchronously inside the triggering event. Scroll/resize
  // events fire at high frequency on mobile and each sync used to write CSS
  // variables on <html> immediately, invalidating layout for the whole app on
  // every event. Now coalesced to at most one sync per animation frame, with a
  // couple of trailing timeouts for the late keyboard settle.
  clearScheduledWhisperViewportSync();
  whisperViewportRafId = window.requestAnimationFrame(() => {
    whisperViewportRafId = null;
    syncWhisperViewportHeight();
  });
  whisperViewportTimerIds = [120, 300, 520].map((delay) => window.setTimeout(() => {
    syncWhisperViewportHeight();
  }, delay));
};

const useWhisperViewportHeight = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const viewport = window.visualViewport;
    scheduleWhisperViewportSync();
    viewport?.addEventListener('resize', scheduleWhisperViewportSync);
    viewport?.addEventListener('scroll', scheduleWhisperViewportSync);
    window.addEventListener('resize', scheduleWhisperViewportSync);
    window.addEventListener('orientationchange', scheduleWhisperViewportSync);
    window.addEventListener('pageshow', scheduleWhisperViewportSync);
    return () => {
      viewport?.removeEventListener('resize', scheduleWhisperViewportSync);
      viewport?.removeEventListener('scroll', scheduleWhisperViewportSync);
      window.removeEventListener('resize', scheduleWhisperViewportSync);
      window.removeEventListener('orientationchange', scheduleWhisperViewportSync);
      window.removeEventListener('pageshow', scheduleWhisperViewportSync);
      clearScheduledWhisperViewportSync();
      document.documentElement.style.removeProperty('--whisper-app-height');
      document.documentElement.style.removeProperty('--whisper-viewport-top');
      document.documentElement.style.removeProperty('--whisper-app-width');
      document.documentElement.style.removeProperty('--whisper-viewport-width');
      document.documentElement.style.removeProperty('--whisper-kb-inset');
    };
  }, []);
};

const focusComposer = (textareaRef) => {
  const textarea = textareaRef.current;
  if (!textarea) {
    scheduleWhisperViewportSync();
    return;
  }
  if (typeof document !== 'undefined' && document.activeElement === textarea) return;
  scheduleWhisperViewportSync();
  requestAnimationFrame(() => {
    const textarea = textareaRef.current;
    if (!textarea || (typeof document !== 'undefined' && document.activeElement === textarea)) return;
    try {
      textarea.focus({ preventScroll: true });
    } catch {
      textarea.focus();
    }
    scheduleWhisperViewportSync();
  });
};

const getSendTimingNow = () => (
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
);

const isSendTimingDebugEnabled = () => {
  try {
    return localStorage.getItem('whisper_debug_send_timing') === 'true';
  } catch {
    return false;
  }
};

const logDmSendTiming = (event, item, startedAt, details = {}) => {
  if (!isSendTimingDebugEnabled()) return;
  const tempId = typeof item === 'string' ? item : item?.tempId;
  const elapsedMs = Number((getSendTimingNow() - startedAt).toFixed(2));
  console.debug('[dm-send-timing]', { event, tempId, elapsed_ms: elapsedMs, ...details });
};

const IMAGE_UPLOAD_TARGET_BYTES = 900 * 1024;
const IMAGE_COMPRESSION_STEPS = [
  { maxDim: 1600, quality: 0.78 },
  { maxDim: 1200, quality: 0.72 },
  { maxDim: 960, quality: 0.64 },
];
// Media selection limits (frontend). Keep these in sync with server.py:
//   VIDEO_UPLOAD_MAX_BYTES = 1 GB. The reverse proxy (Nginx client_max_body_size)
//   must be >= 1024M (e.g. 1100M with margin) or large uploads 413 at the proxy.
const MAX_MEDIA_SELECTION = 10;              // images (photos) per batch
const MAX_VIDEOS_PER_BATCH = 2;              // videos per selection/batch
const MAX_VIDEO_SIZE_BYTES = 1024 * 1024 * 1024;  // 1 GB per video
const MAX_VIDEO_BATCH_BYTES = MAX_VIDEOS_PER_BATCH * MAX_VIDEO_SIZE_BYTES; // 2 GB
const VIDEO_ALLOWED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const formatMb = (bytes) => {
  if (!Number.isFinite(bytes)) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
};
const PROFILE_PICTURE_TARGET_BYTES = 220 * 1024;
const PROFILE_PICTURE_COMPRESSION_STEPS = [
  { maxDim: 384, quality: 0.82 },
  { maxDim: 320, quality: 0.74 },
  { maxDim: 256, quality: 0.68 },
];

const isLikelyHeicImage = (file) => {
  const type = (file?.type || '').toLowerCase();
  const name = (file?.name || '').toLowerCase();
  return type.includes('heic') || type.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
};

const toUploadableImage = (blob, fileName = 'profile-picture.jpg') => {
  if (!blob) return blob;
  try {
    return new File([blob], fileName, { type: blob.type || 'image/jpeg', lastModified: Date.now() });
  } catch {
    return blob;
  }
};

const compressImageFile = (file, maxDim, quality) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    const img = document.createElement('img');
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      if (width > height && width > maxDim) {
        height = Math.round((height * maxDim) / width);
        width = maxDim;
      } else if (height > maxDim) {
        width = Math.round((width * maxDim) / height);
        height = maxDim;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not prepare this image for upload.'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Could not compress this image.'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', quality);
    };
    img.onerror = () => reject(new Error('This image format could not be previewed for upload.'));
    img.src = event.target.result;
  };
  reader.onerror = () => reject(new Error('Could not read this image file.'));
  reader.readAsDataURL(file);
});

const prepareImageForUpload = async (file) => {
  if (!file) {
    throw new Error('No image selected.');
  }

  if (file.type && !file.type.startsWith('image/')) {
    throw new Error('Please choose a valid image file.');
  }

  if (file.size <= IMAGE_UPLOAD_TARGET_BYTES && !isLikelyHeicImage(file)) return file;

  let bestCompressed = null;
  try {
    for (const step of IMAGE_COMPRESSION_STEPS) {
      const compressed = await compressImageFile(file, step.maxDim, step.quality);
      if (!bestCompressed || compressed.size < bestCompressed.size) bestCompressed = compressed;
      if (compressed.size <= IMAGE_UPLOAD_TARGET_BYTES) return compressed;
    }
  } catch (error) {
    if (isLikelyHeicImage(file)) {
      throw new Error('This browser could not convert that HEIC/HEIF photo. Please choose a JPEG/PNG photo or use iPhone camera settings: Formats -> Most Compatible.');
    }
    throw error;
  }

  if (bestCompressed && bestCompressed.size < file.size) return bestCompressed;
  throw new Error('This image is too large to upload. Try a smaller JPEG or PNG photo.');
};

const prepareProfilePictureForUpload = async (file) => {
  if (!file) {
    throw new Error('No photo selected.');
  }

  const isImage = file.type?.startsWith('image/') || isLikelyHeicImage(file);
  if (!isImage) {
    throw new Error('Please choose a valid image file.');
  }

  let bestCompressed = null;
  try {
    for (const step of PROFILE_PICTURE_COMPRESSION_STEPS) {
      const compressed = await compressImageFile(file, step.maxDim, step.quality);
      if (!bestCompressed || compressed.size < bestCompressed.size) bestCompressed = compressed;
      if (compressed.size <= PROFILE_PICTURE_TARGET_BYTES) {
        return toUploadableImage(compressed);
      }
    }
  } catch (error) {
    if (isLikelyHeicImage(file)) {
      throw new Error('This browser could not convert that HEIC/HEIF photo. Please choose a JPEG/PNG photo or use iPhone camera settings: Formats -> Most Compatible.');
    }
    throw error;
  }

  if (bestCompressed && bestCompressed.size < file.size) {
    return toUploadableImage(bestCompressed);
  }

  throw new Error('This profile photo is too large to upload. Try a smaller JPEG or PNG photo.');
};

const getImageUploadErrorMessage = (error) => {
  if (error?.response?.status === 413) {
    return 'This photo is too large for the server upload limit. Try a smaller photo or increase client_max_body_size on the VPS.';
  }
  return error?.response?.data?.detail || error?.message || 'Image upload failed. Please try again.';
};

const getProfilePictureUploadErrorMessage = (error) => {
  if (error?.response?.status === 413) {
    return 'This profile photo is still too large after optimization. Please try a smaller JPEG or PNG photo.';
  }
  return error?.response?.data?.detail || error?.message || 'Failed to upload profile picture. Please try again.';
};

const getVoiceUploadErrorMessage = (error) => {
  if (error?.response?.status === 413) {
    return 'This voice note is too large to upload.';
  }
  return error?.response?.data?.detail || error?.message || 'Voice upload failed. Please try again.';
};

const getVideoUploadErrorMessage = (error) => {
  if (error?.response?.status === 413) {
    return 'This video is too large to upload.';
  }
  if (error?.response?.status === 415) {
    return 'This video format is not supported.';
  }
  return error?.response?.data?.detail || error?.message || 'Video upload failed. Please try again.';
};

const isSupportedVideoFile = (file) => {
  if (!file) return false;
  if (VIDEO_ALLOWED_TYPES.includes(file.type)) return true;
  // Some Android pickers report an empty/quirky MIME; fall back to extension.
  const name = (file.name || '').toLowerCase();
  return name.endsWith('.mp4') || name.endsWith('.webm') || name.endsWith('.mov');
};

const formatVideoDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Read video duration/dimensions from a local File via a throwaway <video> element,
// using its own object URL that is revoked as soon as metadata loads. Never loads
// the full file — only metadata. Resolves to nulls on any error (best-effort).
const readVideoMetadata = (file) => new Promise((resolve) => {
  if (typeof document === 'undefined' || !file) { resolve({}); return; }
  let url = null;
  const el = document.createElement('video');
  el.preload = 'metadata';
  el.muted = true;
  const cleanup = () => { if (url) { try { URL.revokeObjectURL(url); } catch {} url = null; } };
  el.onloadedmetadata = () => {
    const meta = {
      duration: Number.isFinite(el.duration) ? el.duration : null,
      width: el.videoWidth || null,
      height: el.videoHeight || null,
    };
    cleanup();
    resolve(meta);
  };
  el.onerror = () => { cleanup(); resolve({}); };
  try { url = URL.createObjectURL(file); el.src = url; } catch { resolve({}); }
});

// Download a video only when the user explicitly asks. Fetches as a blob (with
// credentials, since media is same-origin-authenticated), triggers a save with a
// safe filename, then revokes the blob URL. Never runs on message arrival.
const sanitizeDownloadName = (message) => {
  const raw = message?.original_filename;
  if (typeof raw === 'string') {
    const base = raw.replace(/\\/g, '/').split('/').pop() || '';
    const cleaned = base.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
    if (cleaned && /\.(mp4|webm|mov)$/i.test(cleaned)) return cleaned;
  }
  const id = message?.id || message?.client_temp_id || 'video';
  return `whisper-video-${String(id).replace(/[^a-zA-Z0-9._-]/g, '')}.mp4`;
};

const downloadVideoMessage = async (message, { onStart, onError, onDone } = {}) => {
  const url = resolveMediaUrl(message?.content);
  if (!url) { onError?.(new Error('No video URL')); return; }
  onStart?.();
  let objectUrl = null;
  try {
    const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
    objectUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = sanitizeDownloadName(message);
    document.body.appendChild(a);
    a.click();
    a.remove();
    onDone?.();
  } catch (error) {
    onError?.(error);
  } finally {
    if (objectUrl) { setTimeout(() => { try { URL.revokeObjectURL(objectUrl); } catch {} }, 4000); }
  }
};

const VOICE_AUDIO_BITS_PER_SECOND = 96000;

const getUserAgent = () => {
  try {
    return navigator.userAgent || '';
  } catch {
    return '';
  }
};

const isAppleRecorderBrowser = () => {
  const ua = getUserAgent();
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIOS || isSafari;
};

const getVoiceMimeTypeCandidates = () => {
  const appleFirst = ['audio/mp4', 'audio/aac'];
  const opusFirst = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg'];
  return isAppleRecorderBrowser()
    ? [...appleFirst, ...opusFirst, '']
    : [...opusFirst, ...appleFirst, ''];
};

const chooseVoiceMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  return getVoiceMimeTypeCandidates().find((type) => !type || MediaRecorder.isTypeSupported(type)) || '';
};

const getVoiceCaptureConstraints = () => ({
  audio: {
    echoCancellation: { ideal: true },
    noiseSuppression: { ideal: true },
    autoGainControl: { ideal: true },
    channelCount: { ideal: 1 },
    sampleRate: { ideal: 48000 },
    sampleSize: { ideal: 16 },
  },
});

const getVoiceRecordingErrorMessage = (error) => {
  if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    return 'Voice recording is not supported in this browser.';
  }
  if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') {
    return 'Microphone permission was blocked. Allow microphone access and try again.';
  }
  if (error?.name === 'NotFoundError') {
    return 'No microphone was found on this device.';
  }
  return 'Voice recording could not start on this browser.';
};

const getVoiceRecordingStream = async () => {
  try {
    return await navigator.mediaDevices.getUserMedia(getVoiceCaptureConstraints());
  } catch (error) {
    if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') throw error;
    return navigator.mediaDevices.getUserMedia({ audio: true });
  }
};

const createVoiceRecorder = (stream) => {
  const mimeType = chooseVoiceMimeType();
  const optionAttempts = [
    mimeType ? { mimeType, audioBitsPerSecond: VOICE_AUDIO_BITS_PER_SECOND } : { audioBitsPerSecond: VOICE_AUDIO_BITS_PER_SECOND },
    mimeType ? { mimeType } : null,
    {},
  ].filter(Boolean);

  let lastError = null;
  for (const options of optionAttempts) {
    try {
      return {
        recorder: new MediaRecorder(stream, options),
        mimeType: options.mimeType || '',
      };
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error('MediaRecorder could not be created');
};

const VOICE_MIME_BY_EXTENSION = {
  m4a: 'audio/mp4',
  mp4: 'audio/mp4',
  aac: 'audio/aac',
  webm: 'audio/webm;codecs=opus',
  ogg: 'audio/ogg;codecs=opus',
  oga: 'audio/ogg;codecs=opus',
  wav: 'audio/wav',
  mp3: 'audio/mpeg',
};

const inferVoiceMimeType = (url) => {
  if (!url || typeof url !== 'string') return '';
  const dataMatch = url.match(/^data:([^;,]+)/i);
  if (dataMatch?.[1]) return dataMatch[1].toLowerCase();

  const cleanUrl = url.split(/[?#]/)[0].toLowerCase();
  const extension = cleanUrl.includes('.') ? cleanUrl.split('.').pop() : '';
  return VOICE_MIME_BY_EXTENSION[extension] || '';
};

const canBrowserPlayVoice = (mimeType) => {
  if (!mimeType || typeof document === 'undefined') return true;
  const audio = document.createElement('audio');
  if (!audio?.canPlayType) return true;
  return audio.canPlayType(mimeType) !== '';
};

const formatVoiceTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const wholeSeconds = Math.floor(seconds);
  const minutes = Math.floor(wholeSeconds / 60);
  const remainingSeconds = wholeSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const shouldUseCredentialedMedia = (url) => {
  if (!url || typeof window === 'undefined' || /^(blob:|data:)/i.test(url)) return false;
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
};

const VoiceMessagePlayer = ({ src, isPending = false, isFailed = false, isUploading = false, retryUnavailable = false }) => {
  const mediaUrl = useMemo(() => resolveMediaUrl(src), [src]);
  const mimeType = useMemo(() => inferVoiceMimeType(mediaUrl), [mediaUrl]);
  const canPlay = useMemo(() => canBrowserPlayVoice(mimeType), [mimeType]);
  const audioRef = useRef(null);
  const [playbackError, setPlaybackError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    setPlaybackError(false);
    setIsPlaying(false);
    setIsLoading(false);
    setDuration(0);
    setCurrentTime(0);
  }, [mediaUrl]);

  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      }
    };
  }, []);

  const stopVoiceEvent = (event) => {
    event.stopPropagation();
  };

  const handlePlayPause = (event) => {
    stopVoiceEvent(event);
    const audio = audioRef.current;
    if (!audio || !mediaUrl || playbackError || !canPlay || retryUnavailable) return;

    if (audio.paused) {
      setIsLoading(true);
      audio.play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        })
        .catch(() => {
          setPlaybackError(true);
          setIsPlaying(false);
          setIsLoading(false);
        });
    } else {
      audio.pause();
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const handleRetryLoad = (event) => {
    stopVoiceEvent(event);
    const audio = audioRef.current;
    if (!audio) {
      setPlaybackError(false);
      return;
    }
    setPlaybackError(false);
    setIsLoading(true);
    audio.load();
  };

  const handleSeek = (event) => {
    stopVoiceEvent(event);
    const audio = audioRef.current;
    const nextTime = Number(event.target.value);
    if (!audio || !Number.isFinite(nextTime)) return;
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const showError = !mediaUrl || playbackError || !canPlay || retryUnavailable;

  if (showError) {
    const errorText = retryUnavailable
      ? 'This local voice note cannot be retried after refresh.'
      : canPlay
        ? 'Voice message failed to load.'
        : 'This voice message is not supported on this device.';

    return (
      <div className="flex max-w-[260px] items-center gap-2 rounded-2xl bg-zinc-900/35 px-3 py-2 text-[12px] leading-4 text-zinc-200">
        <AlertCircle className="h-4 w-4 shrink-0 text-amber-300" />
        <span className="min-w-0 flex-1">{errorText}</span>
        {mediaUrl && canPlay && !retryUnavailable && (
          <button
            type="button"
            onClick={handleRetryLoad}
            onPointerDown={stopVoiceEvent}
            onMouseDown={stopVoiceEvent}
            className="rounded-full bg-zinc-800 p-1.5 text-zinc-100 transition-colors hover:bg-zinc-700"
            title="Retry voice playback"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex w-[235px] max-w-[68vw] items-center gap-2.5">
      <audio
        ref={audioRef}
        src={mediaUrl}
        preload="metadata"
        crossOrigin={shouldUseCredentialedMedia(mediaUrl) ? 'use-credentials' : undefined}
        className="hidden"
        onLoadedMetadata={(event) => {
          const nextDuration = event.currentTarget.duration;
          setDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
          setIsLoading(false);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
        onWaiting={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
        onPlaying={() => {
          setIsPlaying(true);
          setIsLoading(false);
        }}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setIsLoading(false);
          setCurrentTime(0);
        }}
        onError={() => {
          setPlaybackError(true);
          setIsPlaying(false);
          setIsLoading(false);
        }}
      />

      <button
        type="button"
        onClick={handlePlayPause}
        onPointerDown={stopVoiceEvent}
        onMouseDown={stopVoiceEvent}
        disabled={isPending && !mediaUrl}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 disabled:opacity-50"
        title={isPlaying ? 'Pause voice message' : 'Play voice message'}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isPlaying ? (
          <Pause className="h-4 w-4 fill-current" />
        ) : (
          <Play className="ml-0.5 h-4 w-4 fill-current" />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <input
          type="range"
          min="0"
          max={duration || Math.max(currentTime, 1)}
          step="0.01"
          value={Math.min(currentTime, duration || currentTime)}
          onChange={handleSeek}
          onClick={stopVoiceEvent}
          onPointerDown={stopVoiceEvent}
          onMouseDown={stopVoiceEvent}
          onTouchStart={stopVoiceEvent}
          className="h-1.5 w-full cursor-pointer accent-white"
          aria-label="Voice message progress"
        />
        <div className="mt-1 flex items-center justify-between text-[10px] leading-none text-white/75">
          <span>{formatVoiceTime(currentTime)}</span>
          <span>{isFailed ? 'Failed' : isUploading ? 'Uploading...' : isPending ? 'Sending...' : formatVoiceTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

const ImageLightbox = ({ src, alt = 'Shared image', onClose }) => {
  const imageUrl = useMemo(() => resolveMediaUrl(src), [src]);

  useEffect(() => {
    if (!imageUrl) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [imageUrl, onClose]);

  if (!imageUrl || typeof document === 'undefined') return null;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-3 backdrop-blur-sm"
      onClick={onClose}
      data-chat-interactive="true"
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClose?.();
        }}
        className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white shadow-lg transition-colors hover:bg-white/20"
        aria-label="Close image viewer"
      >
        <X className="h-5 w-5" />
      </button>
      <motion.img
        initial={{ scale: 0.96 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.96 }}
        src={imageUrl}
        alt={alt}
        className="max-h-[92dvh] max-w-[96vw] rounded-2xl object-contain shadow-2xl"
        style={{ touchAction: 'pinch-zoom' }}
        onClick={(event) => event.stopPropagation()}
        draggable={false}
      />
    </motion.div>,
    document.body
  );
};

const VideoViewer = ({ src, message, onClose, canDelete = false, onDelete }) => {
  const videoUrl = useMemo(() => resolveMediaUrl(src), [src]);
  const videoRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!videoUrl) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    // Lock background scroll while the viewer is open; restore on close.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [videoUrl, onClose]);

  // Chrome blocks autoplay-with-sound, so we do NOT autoplay on metadata load.
  // Instead, unmute as part of the user-initiated play so audio is never lost.
  const handleVideoPlay = (event) => {
    const video = event.currentTarget;
    if (!video) return;
    video.defaultMuted = false;
    video.muted = false;
    if (video.volume === 0) video.volume = 1;
  };

  const openFullscreen = async (event) => {
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    try {
      if (video.requestFullscreen) {
        await video.requestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      }
    } catch {
      // Fullscreen can be rejected (permissions / gesture); ignore, inline stays usable.
    }
  };

  if (!videoUrl || typeof document === 'undefined') return null;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-3 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Video viewer"
      data-chat-interactive="true"
    >
      {/* Toolbar: evenly spaced, same-size icon buttons, respects the notch */}
      <div
        className="absolute right-3 z-10 flex items-center gap-2"
        style={{ top: 'calc(0.75rem + env(safe-area-inset-top))' }}
        onClick={(event) => event.stopPropagation()}
      >
        {canDelete && (
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); onDelete?.(); onClose?.(); }}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-red-500/80"
            aria-label="Delete video"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        )}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            if (downloading) return;
            downloadVideoMessage(message || { content: src }, {
              onStart: () => setDownloading(true),
              onDone: () => setDownloading(false),
              onError: () => setDownloading(false),
            });
          }}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          aria-label="Download video"
        >
          {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
        </button>
        <button
          type="button"
          onClick={openFullscreen}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          aria-label="Fullscreen"
        >
          <Maximize2 className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); onClose?.(); }}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          aria-label="Close video viewer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {/* Native controls provide play/seek/volume/mute/fullscreen. Starts unmuted. */}
      <video
        ref={videoRef}
        src={videoUrl}
        controls
        playsInline
        preload="metadata"
        onPlay={handleVideoPlay}
        className="max-h-[92dvh] max-w-[96vw] rounded-2xl bg-black object-contain shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      />
    </motion.div>,
    document.body
  );
};

const mergeMessageRecords = (existingMessage, incomingMessage) => {
  const merged = existingMessage
    ? {
        ...existingMessage,
        ...incomingMessage,
        ...getMonotonicDeliveryPatch(existingMessage, incomingMessage),
      }
    : incomingMessage;

  // Preserve the stable local identity across reconciliation: client_temp_id is
  // the row's React render key, so an incoming copy that lacks it (or carries
  // null) must never erase the one we already have — losing it would remount the
  // DOM node mid-animation. Client-side only; the wire format is untouched.
  if (existingMessage && existingMessage.client_temp_id && !merged.client_temp_id) {
    merged.client_temp_id = existingMessage.client_temp_id;
  }

  if (incomingMessage?.id && !isTempMessageId(incomingMessage.id) && !incomingMessage.pending) {
    const confirmedMessage = { ...merged };
    delete confirmedMessage.tempId;
    return {
      ...confirmedMessage,
      pending: false,
      failed: false,
      reactions: normalizeReactions(confirmedMessage.reactions),
    };
  }

  return {
    ...merged,
    reactions: normalizeReactions(merged.reactions),
  };
};

const getMessageTime = (message) => {
  const time = parseServerTimestamp(message?.created_at).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const getMessageStableId = (message) => {
  const id = message?.id;
  if (!id || isTempMessageId(id)) return null;
  return String(id);
};

const compareMessagesForDisplay = (a, b) => {
  const timeDiff = getMessageTime(a) - getMessageTime(b);
  if (timeDiff !== 0) return timeDiff;

  const aStableId = getMessageStableId(a);
  const bStableId = getMessageStableId(b);
  if (aStableId && bStableId) return aStableId < bStableId ? -1 : aStableId > bStableId ? 1 : 0;
  if (aStableId && !bStableId) return -1;
  if (!aStableId && bStableId) return 1;
  return 0;
};

const sortMessagesForDisplay = (messages) => {
  return [...(messages || [])]
    .map((message, index) => ({ message, index }))
    .sort((a, b) => compareMessagesForDisplay(a.message, b.message) || a.index - b.index)
    .map((item) => item.message);
};

const mergeMessagesByIdentity = (oldMessages, newMessages) => {
  const records = [];              // live + consumed records in insertion order
  const removedRecords = new Set(); // records consumed by a merge (dead)
  const keyToRecord = new Map();    // identity key -> live record

  const isLive = (record) => !removedRecords.has(record);

  const addMessage = (rawMessage) => {
    const message = normalizeMessageForState(rawMessage);
    if (!message) return;
    const keys = getMessageIdentityKeys(message);
    if (keys.length === 0) return;

    // Collect the records this message must merge with, in ORIGINAL order,
    // using the identity index (O(1) per key) instead of scanning every
    // accumulated record (the previous implementation was quadratic for long
    // histories / large pagination batches).
    const matched = new Set();
    keys.forEach((key) => {
      const existing = keyToRecord.get(key);
      if (!existing) return;
      if (isLive(existing)) matched.add(existing);
      else keyToRecord.delete(key); // stale mapping to a consumed record
    });

    let merged = message;
    if (matched.size > 0) {
      const matchedInOrder = [];
      for (const record of records) {
        if (matched.has(record)) matchedInOrder.push(record);
      }
      matchedInOrder.forEach((record) => {
        merged = mergeMessageRecords(record, merged);
        removedRecords.add(record);
        getMessageIdentityKeys(record).forEach((recordKey) => {
          if (keyToRecord.get(recordKey) === record) keyToRecord.delete(recordKey);
        });
      });
    }

    records.push(merged);
    getMessageIdentityKeys(merged).forEach((mergedKey) => {
      keyToRecord.set(mergedKey, merged);
    });
  };

  normalizeMessagesForState(oldMessages).forEach(addMessage);
  normalizeMessagesForState(newMessages).forEach(addMessage);

  const liveRecords = removedRecords.size ? records.filter(isLive) : records;
  return sortMessagesForDisplay(liveRecords);
};

const MESSAGE_PAGE_SIZE = 30;
const OLDER_MESSAGES_SCROLL_THRESHOLD = 96;
const UNREAD_CLEAR_GUARD_MS = 5000;
const PRESENCE_LOCAL_GUARD_MS = 10000;
const DM_HISTORY_RECENT_MS = 30000;
// Coalesce burst focus/visibility/online events: a full reconcile fetches three
// endpoints AND re-fetches the open conversation, so allow at most one per 15s.
// The socket is authoritative for realtime changes in between.
const FOREGROUND_SYNC_MIN_INTERVAL_MS = 15000;
const dmHistoryValidatedAt = new Map();
const dmHistoryHasMore = new Map();
const dmHistoryRequests = new Map();
const dmMessageSessionCache = new Map();

// Bounded in-memory caches (per-session growth would otherwise be unbounded for
// users with many DMs). Maps preserve insertion order, so evicting the oldest
// key after a re-set gives a cheap recency-ordered cap.
const DM_SESSION_CACHE_MAX_DMS = 40;
const DM_HISTORY_META_CACHE_MAX = 100;

const evictOldest = (map, max) => {
  while (map.size > max) map.delete(map.keys().next().value);
};

const getDmHistorySessionKey = (dmId) => `${_cacheUserId || 'anonymous'}:${dmId}`;

const requestLatestDmHistory = (dmId) => {
  const key = getDmHistorySessionKey(dmId);
  const existing = dmHistoryRequests.get(key);
  if (existing) return existing;

  const entry = { requestedAt: Date.now(), promise: null };
  entry.promise = axios.get(`${BACKEND_URL}/api/dms/${dmId}/messages`, {
    withCredentials: true,
    params: { limit: MESSAGE_PAGE_SIZE },
  }).finally(() => {
    if (dmHistoryRequests.get(key) === entry) dmHistoryRequests.delete(key);
  });
  dmHistoryRequests.set(key, entry);
  evictOldest(dmHistoryRequests, DM_HISTORY_META_CACHE_MAX);
  return entry;
};

const normalizeMessagesResponse = (payload, fallbackPageSize = MESSAGE_PAGE_SIZE) => {
  if (Array.isArray(payload)) {
    return {
      messages: normalizeMessagesForState(payload),
      hasMore: payload.length >= fallbackPageSize,
    };
  }

  return {
    messages: normalizeMessagesForState(payload?.messages),
    hasMore: Boolean(payload?.has_more),
  };
};

const getOldestMessageCursor = (messages) => {
  const confirmedMessages = (messages || []).filter((message) => {
    if (!message?.created_at) return false;
    if (message.pending) return false;
    if (!message.id) return false;
    if (isTempMessageId(message.id)) return false;
    return true;
  });

  if (confirmedMessages.length === 0) return null;

  const oldestMessage = sortMessagesForDisplay(confirmedMessages)[0];
  return {
    before: oldestMessage.created_at,
    before_id: getMessageStableId(oldestMessage),
  };
};

const getOlderCursorKey = (cursor) => {
  if (!cursor?.before) return null;
  return `${cursor.before}:${cursor.before_id || ''}`;
};

const getConversationActivityMs = (conversation) => {
  const activity = conversation?.last_message?.created_at || conversation?.created_at;
  const time = new Date(activity).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortConversationsByActivity = (conversations = []) => {
  return [...conversations]
    .map((conversation, index) => ({ conversation, index }))
    .sort((a, b) => {
      const diff = getConversationActivityMs(b.conversation) - getConversationActivityMs(a.conversation);
      return diff || a.index - b.index;
    })
    .map((item) => item.conversation);
};

const buildConversationPreview = (message) => {
  if (!message) return null;
  const messageType = message.message_type || 'text';

  return {
    id: message.id,
    client_temp_id: message.client_temp_id,
    content: messageType === 'image' ? 'Image' : messageType === 'voice' ? 'Voice message' : (message.content || ''),
    sender_username: message.sender_username,
    sender_id: message.sender_id,
    created_at: message.created_at,
    message_type: messageType,
    status: message.status,
    delivered_at: message.delivered_at,
    seen_at: message.seen_at,
  };
};

const mergeConversationPreview = (previousPreview, serverPreview) => {
  if (!previousPreview) return serverPreview;
  if (!serverPreview) return previousPreview;
  if (!isSameConversationPreview(previousPreview, serverPreview)) {
    return isConversationPreviewNewer(previousPreview, serverPreview) ? previousPreview : serverPreview;
  }
  return {
    ...previousPreview,
    ...serverPreview,
    ...getMonotonicDeliveryPatch(previousPreview, serverPreview),
  };
};

const getPreviewIdentityKeys = (preview) => (
  [preview?.id, preview?.message_id, preview?.client_temp_id]
    .filter(Boolean)
    .map(String)
);

const isSameConversationPreview = (a, b) => {
  const aKeys = getPreviewIdentityKeys(a);
  const bKeys = getPreviewIdentityKeys(b);
  return aKeys.length > 0 && bKeys.some((key) => aKeys.includes(key));
};

const getPreviewServerId = (preview) => (
  [preview?.id, preview?.message_id]
    .filter((id) => id && !isTempMessageId(id))
    .map(String)[0] || null
);

const isConversationPreviewNewer = (incoming, existing) => {
  if (!incoming) return false;
  if (!existing) return true;
  const incomingTime = parseServerTimestamp(incoming.created_at).getTime();
  const existingTime = parseServerTimestamp(existing.created_at).getTime();
  if (Number.isNaN(incomingTime)) return false;
  if (Number.isNaN(existingTime)) return true;
  if (incomingTime !== existingTime) return incomingTime > existingTime;
  if (isSameConversationPreview(incoming, existing)) return true;
  const incomingId = getPreviewServerId(incoming);
  const existingId = getPreviewServerId(existing);
  if (incomingId && existingId) return incomingId > existingId;
  if (incomingId && !existingId) return true;
  return false;
};

const getPresenceOnlyPatch = (data = {}) => {
  const patch = {};
  if (Object.prototype.hasOwnProperty.call(data, 'is_online')) {
    patch.is_online = data.is_online;
  }
  // Only merge last_seen when actually supplied — never blank a known value with
  // an undefined/null incoming one.
  if (data.last_seen != null) {
    patch.last_seen = data.last_seen;
  }
  patch.presence_updated_at = Date.now();
  return patch;
};

const overlayUserPresence = (user, presenceByUser) => {
  if (!user?.id) return user;
  const presence = presenceByUser?.[String(user.id)];
  if (!presence) return user;
  return {
    ...user,
    ...getPresenceOnlyPatch(presence),
    is_online: Boolean(presence.is_online),
    presence_updated_at: presence.updated_at,
  };
};

const getProfileMetaPatch = (data) => {
  const patch = {};
  if (typeof data?.username === 'string') patch.username = data.username;
  if (Object.prototype.hasOwnProperty.call(data || {}, 'profile_picture_thumb')) {
    patch.profile_picture_thumb = data.profile_picture_thumb;
  }
  if (Object.prototype.hasOwnProperty.call(data || {}, 'profile_picture_updated_at')) {
    patch.profile_picture_updated_at = data.profile_picture_updated_at;
  }
  patch.profile_realtime_updated_at = Date.now();
  return patch;
};

const applyProfileUpdateToConversationUsers = (conversations, data) => {
  if (!data?.user_id) return conversations;
  const patch = getProfileMetaPatch(data);
  if (Object.keys(patch).length === 0) return conversations;
  return conversations.map((conversation) => (
    conversation.other_user?.id === data.user_id
      ? { ...conversation, other_user: { ...conversation.other_user, ...patch } }
      : conversation
  ));
};

const applyProfileUpdateToMessages = (messages, data) => {
  if (!data?.user_id) return messages;
  const patch = {};
  if (typeof data.username === 'string') patch.sender_username = data.username;
  if (Object.prototype.hasOwnProperty.call(data, 'profile_picture_thumb')) {
    patch.sender_profile_picture_thumb = data.profile_picture_thumb;
  }
  if (Object.keys(patch).length === 0) return messages;
  return messages.map((message) => (
    message.sender_id === data.user_id ? { ...message, ...patch } : message
  ));
};

const applyProfileUpdateToMembers = (members, data) => {
  if (!data?.user_id || !Array.isArray(members)) return members;
  const patch = getProfileMetaPatch(data);
  if (Object.keys(patch).length === 0) return members;
  return members.map((member) => (
    member.id === data.user_id ? { ...member, ...patch } : member
  ));
};

// `shouldPreserveLocalUnread(id)` (optional): return true when a realtime unread
// update for this conversation happened AFTER the current server request began — in
// that case a lower server count is stale and must not roll the local unread down.
// When it returns false, the latest non-superseded server count is authoritative
// (cross-device reads, genuine reconciliation, reading from another session).
const mergeConversationLists = (
  previousRows,
  serverRows,
  isUnreadCleared,
  shouldPreserveLocalUnread,
  requestStartedAt = 0
) => {
  const previousList = Array.isArray(previousRows) ? previousRows : [];
  const serverList = Array.isArray(serverRows) ? serverRows : [];
  const previousById = new Map(previousList.filter(Boolean).map((row) => [row.id, row]));
  const serverIds = new Set();
  const now = Date.now();
  const preserveLocalUnread = typeof shouldPreserveLocalUnread === 'function'
    ? shouldPreserveLocalUnread
    : () => false;

  const mergedRows = serverList.filter(Boolean).map((serverRow) => {
    serverIds.add(serverRow.id);
    const previousRow = previousById.get(serverRow.id);
    if (!previousRow) {
      return {
        ...serverRow,
        unread_count: isUnreadCleared(serverRow.id) ? 0 : (serverRow.unread_count || 0),
      };
    }

    const localPreviewIsNewer = isConversationPreviewNewer(previousRow.last_message, serverRow.last_message)
      && !isSameConversationPreview(previousRow.last_message, serverRow.last_message);
    const localPresenceIsFresh = Boolean(
      previousRow.other_user?.presence_updated_at
      && now - previousRow.other_user.presence_updated_at < PRESENCE_LOCAL_GUARD_MS
    );
    const localProfileIsFresh = Boolean(
      previousRow.other_user?.profile_realtime_updated_at
      && now - previousRow.other_user.profile_realtime_updated_at < PRESENCE_LOCAL_GUARD_MS
    );
    const otherUser = serverRow.other_user || previousRow.other_user
      ? {
          ...(previousRow.other_user || {}),
          ...(serverRow.other_user || {}),
          ...(localPresenceIsFresh ? {
            is_online: previousRow.other_user?.is_online,
            last_seen: previousRow.other_user?.last_seen,
            presence_updated_at: previousRow.other_user?.presence_updated_at,
          } : {}),
          ...(localProfileIsFresh ? {
            username: previousRow.other_user?.username,
            profile_picture: previousRow.other_user?.profile_picture,
            profile_picture_thumb: previousRow.other_user?.profile_picture_thumb,
            profile_picture_updated_at: previousRow.other_user?.profile_picture_updated_at,
            profile_realtime_updated_at: previousRow.other_user?.profile_realtime_updated_at,
          } : {}),
        }
      : undefined;

    const localUnread = previousRow.unread_count || 0;
    const serverUnread = serverRow.unread_count || 0;
    // Unread resolution:
    //  - genuinely opened/read on THIS device (isUnreadCleared) → 0, always wins.
    //  - realtime unread changed AFTER this server request began → the server count
    //    is stale; keep the local (higher) value so a slow /api/dms cannot erase a
    //    just-arrived unread. We only floor to local (never force above it).
    //  - otherwise → trust the latest non-superseded server count, EVEN IF it is
    //    lower with an equal preview. That is required so a cross-device read (which
    //    zeroes unread without changing last_message) is honored here.
    let resolvedUnread;
    if (isUnreadCleared(serverRow.id)) {
      resolvedUnread = 0;
    } else {
      const realtimeResolution = preserveLocalUnread(serverRow.id, serverRow);
      if (typeof realtimeResolution === 'number') {
        resolvedUnread = serverUnread + Math.max(0, realtimeResolution);
      } else if (realtimeResolution) {
        resolvedUnread = Math.max(localUnread, serverUnread);
      } else {
        resolvedUnread = serverUnread;
      }
    }

    return {
      ...previousRow,
      ...serverRow,
      other_user: otherUser,
      last_message: localPreviewIsNewer
        ? previousRow.last_message
        : mergeConversationPreview(previousRow.last_message, serverRow.last_message),
      my_read_boundary: advanceBoundary(previousRow.my_read_boundary, serverRow.my_read_boundary),
      peer_read_boundary: advanceBoundary(previousRow.peer_read_boundary, serverRow.peer_read_boundary),
      unread_count: resolvedUnread,
    };
  });

  previousById.forEach((row, id) => {
    // A missing server row is normally authoritative (left/deleted conversation).
    // Preserve only a row created locally/realtime after this request began.
    if (!serverIds.has(id) && (row?._local_created_at || 0) > requestStartedAt) {
      mergedRows.push(row);
    }
  });

  return sortConversationsByActivity(mergedRows);
};

// Shared Intl formatters, created once. `toLocaleTimeString`/`toLocaleDateString`
// re-create their formatter on every call and are ~100x slower than reusing a
// cached instance — with hundreds of rows re-rendering per keystroke this was a
// measurable chunk of the main-thread cost.
const MESSAGE_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
const WEEKDAY_FORMATTER = new Intl.DateTimeFormat([], { weekday: 'short' });
const MONTH_DAY_FORMATTER = new Intl.DateTimeFormat([], { month: 'short', day: 'numeric' });
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat([], { weekday: 'short', month: 'short', day: 'numeric' });
const LOCALE_DATE_FORMATTER = new Intl.DateTimeFormat();

// Stable sentinel identities so memoized bubbles whose reply target is deleted
// or unavailable don't receive a fresh object every render (which would defeat
// React.memo for every reply bubble).
const UNAVAILABLE_REPLY_TARGET = Object.freeze({ unavailable: true });
const DELETED_REPLY_TARGET = Object.freeze({ deleted: true });

// Format last seen time
const formatLastSeen = (lastSeen, nowValue = Date.now()) => {
  if (!lastSeen) return 'Unknown';
  const date = parseServerTimestamp(lastSeen);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  const now = new Date(nowValue);
  const diff = Math.max(0, now - date); // clamp negative (clock skew) to 0

  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return LOCALE_DATE_FORMATTER.format(date);
};

// Compact timestamp for conversation-list rows (WhatsApp/Telegram style)
const formatConversationTimestamp = (dateString) => {
  if (!dateString) return '';
  const date = parseServerTimestamp(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor((startOfToday - new Date(date.getFullYear(), date.getMonth(), date.getDate())) / 86400000);
  if (diffDays <= 0) {
    return MESSAGE_TIME_FORMATTER.format(date);
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return WEEKDAY_FORMATTER.format(date);
  return MONTH_DAY_FORMATTER.format(date);
};

const MESSAGE_GROUP_WINDOW_MS = 5 * 60 * 1000;

const isSameMessageGroup = (previousMessage, currentMessage) => {
  if (!previousMessage || !currentMessage) return false;
  if (previousMessage.sender_id !== currentMessage.sender_id) return false;

  const previousDate = parseServerTimestamp(previousMessage.created_at);
  const currentDate = parseServerTimestamp(currentMessage.created_at);

  if (Number.isNaN(previousDate.getTime()) || Number.isNaN(currentDate.getTime())) return false;
  if (previousDate.toDateString() !== currentDate.toDateString()) return false;

  return Math.abs(currentDate - previousDate) < MESSAGE_GROUP_WINDOW_MS;
};

const shouldShowDateDivider = (messages, index) => {
  if (index === 0) return true;

  const currentDate = parseServerTimestamp(messages[index]?.created_at);
  const previousDate = parseServerTimestamp(messages[index - 1]?.created_at);

  if (Number.isNaN(currentDate.getTime()) || Number.isNaN(previousDate.getTime())) return false;
  return currentDate.toDateString() !== previousDate.toDateString();
};

const formatMessageDateDivider = (dateString) => {
  const date = parseServerTimestamp(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return SHORT_DATE_FORMATTER.format(date);
};

const formatMessageTime = (dateString) => {
  const date = parseServerTimestamp(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return MESSAGE_TIME_FORMATTER.format(date);
};

const isSameMetaMinuteGroup = (currentMessage, nextMessage) => {
  if (!currentMessage || !nextMessage) return false;
  if (currentMessage.sender_id !== nextMessage.sender_id) return false;

  const currentDate = parseServerTimestamp(currentMessage.created_at);
  const nextDate = parseServerTimestamp(nextMessage.created_at);

  if (Number.isNaN(currentDate.getTime()) || Number.isNaN(nextDate.getTime())) return false;
  if (currentDate.toDateString() !== nextDate.toDateString()) return false;

  return (
    currentDate.getHours() === nextDate.getHours() &&
    currentDate.getMinutes() === nextDate.getMinutes()
  );
};

const shouldShowMessageMeta = (messages, index) => {
  const currentMessage = messages[index];
  const nextMessage = messages[index + 1];
  return !isSameMetaMinuteGroup(currentMessage, nextMessage);
};

const getMessageStatusLabel = (message) => {
  if (message?.failed || message?.status === 'failed') return 'Failed';
  if (message?.pending) return 'Sending';
  if (message?.status === 'seen') return 'Seen';
  if (message?.status === 'delivered') return 'Delivered';
  return 'Sent';
};

const getDmReadBoundaryTime = (data) => {
  const boundary = data?.last_read_created_at || data?.read_at || data?.seen_at;
  const time = new Date(boundary).getTime();
  return Number.isNaN(time) ? null : time;
};

const isSameMessageIdentity = (message, id) => {
  if (!message || !id) return false;
  const targetId = String(id);
  return [message.id, message.tempId, message.client_temp_id]
    .filter(Boolean)
    .map(String)
    .includes(targetId);
};

const applyDmSeenBoundary = (messages, data, currentUserId) => {
  if (!currentUserId || !data) return messages;

  const boundaryTime = getDmReadBoundaryTime(data);
  const readAt = data.read_at || data.seen_at || data.last_read_created_at || new Date().toISOString();
  const lastReadMessageId = data.last_read_message_id;
  const exactBoundary = normalizeBoundary(data);
  const lastReadIndex = lastReadMessageId
    ? messages.findIndex((message) => isSameMessageIdentity(message, lastReadMessageId))
    : -1;

  if (lastReadIndex < 0 && boundaryTime === null) return messages;

  return messages.map((message, index) => {
    if (message.sender_id !== currentUserId) return message;
    if (message.pending || message.failed || message.status === 'seen') return message;
    if (isTempMessageId(message.id)) return message;

    const messageTime = getMessageTime(message);
    const messageStableId = getMessageStableId(message);
    const isWithinBoundary = exactBoundary && messageIsAtOrBeforeBoundary(message, exactBoundary)
      ? true
      : lastReadIndex >= 0
      ? index <= lastReadIndex
      : messageTime > 0 && (
          messageTime < boundaryTime
          || (
            messageTime === boundaryTime
            && lastReadMessageId
            && messageStableId
            && messageStableId <= String(lastReadMessageId)
          )
        );

    return isWithinBoundary
      ? { ...message, status: 'seen', seen_at: readAt }
      : message;
  });
};

const applyDmDeliveredBoundary = (messages, data, currentUserId) => {
  if (!currentUserId || !data) return messages;
  const boundaryId = data.last_delivered_message_id;
  const boundaryTimeValue = data.last_delivered_created_at || data.created_at;
  const boundaryTime = boundaryTimeValue ? parseServerTimestamp(boundaryTimeValue).getTime() : null;
  const boundaryIndex = boundaryId
    ? messages.findIndex((message) => isSameMessageIdentity(message, boundaryId))
    : -1;
  const deliveredAt = data.delivered_at || new Date().toISOString();

  return (messages || []).map((message, index) => {
    if (String(message?.sender_id) !== String(currentUserId)) return message;
    if (message.pending || message.failed || message.status === 'seen' || message.status === 'delivered') return message;
    if (!message.id || isTempMessageId(message.id)) return message;

    const messageTime = getMessageTime(message);
    const stableId = getMessageStableId(message);
    const withinBoundary = boundaryIndex >= 0
      ? index <= boundaryIndex
      : Number.isFinite(boundaryTime)
        ? messageTime > 0 && (
            messageTime < boundaryTime
            || (messageTime === boundaryTime && boundaryId && stableId && stableId <= String(boundaryId))
          )
        : true;

    return withinBoundary ? { ...message, status: 'delivered', delivered_at: deliveredAt } : message;
  });
};

const getDmSessionCacheKey = (dmId) => `${_cacheUserId || 'anonymous'}:${dmId}`;

const getDmSessionMessages = (dmId) => {
  if (!dmId) return null;
  const value = dmMessageSessionCache.get(getDmSessionCacheKey(dmId));
  return Array.isArray(value) ? value : null;
};

const setDmSessionMessages = (dmId, messages) => {
  if (!dmId) return;
  const key = getDmSessionCacheKey(dmId);
  // Re-set to refresh recency (Map preserves insertion order; eviction drops
  // the least-recently-updated DM first).
  dmMessageSessionCache.delete(key);
  dmMessageSessionCache.set(key, sortMessagesForDisplay(messages || []));
  evictOldest(dmMessageSessionCache, DM_SESSION_CACHE_MAX_DMS);
};

const updateDmCachedMessages = (dmId, updater) => {
  if (!dmId || typeof updater !== 'function') return;
  const cacheKey = `whisper_dm_messages_${dmId}`;
  const current = getDmSessionMessages(dmId)
    || sortMessagesForDisplay(normalizeMessagesForState(sanitizeMessagesForCache(getMessageCache(cacheKey))));
  if (!current.length) return;
  const next = sortMessagesForDisplay(updater(current));
  setDmSessionMessages(dmId, next);
  // This runs on socket events for INACTIVE DMs (new/edit/delete/seen). Persist
  // on idle instead of synchronously per event: only the final state lands in
  // localStorage, and the write never blocks the event loop.
  const clean = sanitizeMessagesForCache(next);
  deferStorageTask(() => {
    saveMessageCache(cacheKey, clean);
  });
};

const getLatestIncomingConfirmedMessage = (messages, currentUserId) => {
  if (!currentUserId) return null;
  const incoming = (messages || []).filter((message) => (
    String(message?.sender_id) !== String(currentUserId)
    && message?.id
    && !message.pending
    && !message.failed
    && !isTempMessageId(message.id)
  ));
  return incoming.length ? sortMessagesForDisplay(incoming)[incoming.length - 1] : null;
};

const getUserReactionCount = (reactions, userId) => {
  if (!userId) return 0;
  return Object.values(normalizeReactions(reactions)).filter((users) => users.includes(userId)).length;
};

const canToggleReaction = (reactions, emoji, userId) => {
  if (!userId || !emoji) return false;
  const safeReactions = normalizeReactions(reactions);
  const currentUsers = normalizeReactionUsers(safeReactions[emoji]);
  if (currentUsers.includes(userId)) return true;
  return getUserReactionCount(safeReactions, userId) < MAX_REACTIONS_PER_USER;
};

const getReactionAction = (reactions, emoji, userId) => {
  const currentUsers = normalizeReactionUsers(normalizeReactions(reactions)[emoji]);
  return currentUsers.includes(userId) ? 'remove' : 'add';
};

const getReactionRequestKey = (messageId, emoji) => `${messageId || ''}:${emoji || ''}`;

const createReactionRequestId = () => `reaction_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const applyOptimisticReactionUpdate = (messages, messageId, emoji, userId) => {
  if (!userId || !messageId || isTempMessageId(messageId)) return messages;

  return (messages || []).map((message) => {
    if (message.id !== messageId) return message;

    const nextReactions = normalizeReactions(message.reactions);
    const currentUsers = normalizeReactionUsers(nextReactions[emoji]);
    const alreadyReacted = currentUsers.includes(userId);
    if (!alreadyReacted && getUserReactionCount(nextReactions, userId) >= MAX_REACTIONS_PER_USER) {
      return message;
    }

    const updatedUsers = alreadyReacted
      ? currentUsers.filter((id) => id !== userId)
      : [...currentUsers, userId];

    if (updatedUsers.length) nextReactions[emoji] = updatedUsers;
    else delete nextReactions[emoji];

    return {
      ...message,
      reactions: nextReactions,
    };
  });
};

const REALTIME_GUARD_MS = 90 * 1000;

const mergeServerMessagesPreservingLocal = (
  previousMessages,
  serverMessages,
  mergeFn,
  { requestStartedAt = null } = {}
) => {
  const safePrevious = Array.isArray(previousMessages) ? previousMessages : [];
  const safeServer = Array.isArray(serverMessages) ? serverMessages : [];
  const serverIds = new Set(safeServer.map((message) => message?.id).filter(Boolean).map(String));
  const sortedServer = safeServer.length ? sortMessagesForDisplay(safeServer) : [];
  const oldestServerMessage = sortedServer.length ? sortedServer[0] : null;
  const newestServerMessage = sortedServer.length ? sortedServer[sortedServer.length - 1] : null;
  const now = Date.now();

  // Keep a PREVIOUS message that is absent from this authoritative server
  // response ONLY if it is genuinely local-only (never confirmed) or was received
  // via realtime moments ago (a stale GET must not erase it). Everything else that
  // is confirmed-but-server-absent is dropped — that is real deletion sync.
  const preservablePrevious = safePrevious.filter((message) => {
    if (!message) return false;
    if (message.id && serverIds.has(String(message.id))) return true; // server has it → merge below
    const isLocalOnly = message.pending || message.failed
      || !message.id || isTempMessageId(message.id);
    if (isLocalOnly) return true;
    const rt = message._realtime_received_at;
    // A confirmed socket message received after THIS history request started is
    // protected for the full request lifetime, however slow the network is.
    if (rt && requestStartedAt != null && rt >= requestStartedAt) return true;
    if (rt && requestStartedAt == null && (now - rt) < REALTIME_GUARD_MS) return true;
    // The response is only the newest paginated page, not the whole history.
    if (oldestServerMessage && compareMessagesForDisplay(message, oldestServerMessage) < 0) return true;
    // A snapshot cannot testify about messages NEWER than its own newest row: a
    // GET that raced a just-sent (already ACK-confirmed, so no realtime stamp)
    // message was snapshotted BEFORE that send and must never erase it. The
    // protection is TIME-BOUNDED to the send/fetch race window so a "delete my
    // newest message" performed while this client was offline still syncs on the
    // next fetch instead of ghosting until a new message arrives.
    if (
      newestServerMessage
      && compareMessagesForDisplay(message, newestServerMessage) > 0
      && (now - getMessageTime(message)) < REALTIME_GUARD_MS
    ) return true;
    return false; // confirmed, not recent, absent from server → drop (deleted)
  });

  const merged = mergeFn(preservablePrevious, safeServer);
  return merged.map((message) => (
    message?.id && serverIds.has(String(message.id))
      ? { ...message, localEcho: false }
      : message
  ));
};

// Conversation-list / reply preview: renders a Lucide icon + clean UTF-8 label
// (never emoji-in-string). `count` supports "3 Photos" for image batches.
// `thumbnailUrl` (optional) renders a tiny media thumbnail before the label —
// used by reply previews; never full-size media, and guarded against a missing src.
const MediaPreview = ({ messageType, fallbackText, youPrefix = false, count = 1, thumbnailUrl = null }) => {
  let Icon = null;
  let label = null;
  if (messageType === 'image') {
    Icon = Image;
    label = count > 1 ? `${count} Photos` : 'Photo';
  } else if (messageType === 'video') {
    Icon = Video;
    label = 'Video';
  } else if (messageType === 'voice') {
    Icon = Mic;
    label = 'Voice message';
  }

  if (Icon) {
    const showThumb = Boolean(thumbnailUrl) && (messageType === 'image' || messageType === 'video');
    return (
      <span className="inline-flex items-center gap-1 min-w-0">
        {youPrefix && <span className="text-zinc-500 shrink-0">You:</span>}
        {showThumb ? (
          <img
            src={thumbnailUrl}
            alt=""
            aria-hidden="true"
            className="h-5 w-5 shrink-0 rounded object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <Icon size={14} className="shrink-0 opacity-80" aria-hidden="true" />
        )}
        <span className="truncate">{label}</span>
      </span>
    );
  }

  return (
    <span className="truncate">
      {youPrefix && <span className="text-zinc-500">You: </span>}
      {stripLegacyMediaEmoji(fallbackText)}
    </span>
  );
};

// Reply-preview helpers. Derive a small (never full-size) thumbnail URL and a
// short text summary for a resolved original message. No network, no per-message
// profile fetch — everything comes off the already-loaded message object.
const getReplyPreviewThumb = (msg) => {
  if (!msg) return null;
  if (msg.message_type === 'image') {
    return resolveMediaUrl(msg.thumbnail_url || msg.content) || null;
  }
  if (msg.message_type === 'video') {
    return resolveMediaUrl(msg.thumbnail_url || msg.poster_url) || null;
  }
  return null;
};
const getReplyPreviewSummary = (msg) => {
  if (!msg) return '';
  if (msg.message_type === 'image') return 'Photo';
  if (msg.message_type === 'video') return 'Video';
  if (msg.message_type === 'voice') return 'Voice message';
  const text = stripLegacyMediaEmoji(msg.content || '');
  return truncateGraphemes(text, 80);
};

const DateDivider = ({ label }) => (
  <div className="flex items-center gap-3 py-3">
    <div className="h-px flex-1 bg-white/[0.06]" />
    <span className="rounded-full bg-surface-2 px-3 py-1 text-[11px] font-semibold text-zinc-400 shadow-elev-sm">
      {label}
    </span>
    <div className="h-px flex-1 bg-white/[0.06]" />
  </div>
);

const MessagesLoadingSkeleton = () => (
  <div className="space-y-5" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((item) => (
      <div key={item} className={`flex gap-2.5 ${item % 2 === 0 ? '' : 'flex-row-reverse'}`}>
        <div className="h-9 w-9 shrink-0 rounded-full ui-skeleton" />
        <div className={`max-w-[75%] ${item % 2 === 0 ? '' : 'items-end'} flex flex-col gap-2`}>
          <div className={`ui-skeleton h-3 ${item % 2 === 0 ? 'w-24' : 'w-16 self-end'}`} />
          <div className={`ui-skeleton ${item % 2 === 0 ? 'w-52 rounded-tl-md' : 'w-44 self-end rounded-tr-md'} h-11 rounded-2xl`} />
        </div>
      </div>
    ))}
  </div>
);


const OlderMessagesLoader = () => (
  <div className="pointer-events-none absolute left-0 right-0 top-2 z-10 flex justify-center">
    <div className="flex items-center gap-2 rounded-full border border-white/10 bg-surface-2/95 px-3 py-1.5 text-xs text-zinc-400 shadow-elev-md backdrop-blur">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span>Loading older messages…</span>
    </div>
  </div>
);

const EmptyConversationState = ({ title, subtitle }) => (
  <div className="flex min-h-[320px] items-center justify-center px-6">
    <div className="mx-auto max-w-sm text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-gradient-soft ring-1 ring-inset ring-white/[0.08]">
        <MessageCircle className="h-7 w-7 text-cyan-300" />
      </div>
      <h3 className="text-lg font-semibold text-white font-heading tracking-tight">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-zinc-500">{subtitle}</p>
    </div>
  </div>
);

const AVATAR_SIZE_CLASSES = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-xl'
};

const getAvatarInitials = (name) => name?.charAt(0).toUpperCase() || '?';

const AvatarVisual = React.memo(({ src, username, size = 'md' }) => (
  <div className={`${AVATAR_SIZE_CLASSES[size]} rounded-full overflow-hidden bg-zinc-700 flex-shrink-0`}>
    {src ? (
      <img src={src} alt={username} className="w-full h-full object-cover" loading="lazy" decoding="async" />
    ) : (
      <div className="w-full h-full flex items-center justify-center text-white font-medium bg-gradient-to-br from-blue-500 to-teal-500">
        {getAvatarInitials(username)}
      </div>
    )}
  </div>
));

// Avatar component
const Avatar = React.memo(({ src, username, size = 'md', isOnline, showStatus = true }) => {
  return (
    <div className={`relative ${AVATAR_SIZE_CLASSES[size]} flex-shrink-0`}>
      <AvatarVisual src={src} username={username} size={size} />
      {showStatus && isOnline !== undefined && (
        <motion.div
          animate={{
            backgroundColor: isOnline ? '#10b981' : '#71717a',
            scale: isOnline ? 1 : 0.82,
          }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          className="absolute bottom-0 right-0 w-3 h-3 rounded-full ring-2 ring-[hsl(var(--surface-1))]"
        />
      )}
    </div>
  );
});

const getTinyMessageAvatarSrc = (message, senderMeta) => (
  message?.sender_profile_picture_thumb ||
  message?.sender_avatar_thumb ||
  message?.sender_thumbnail_url ||
  senderMeta?.profile_picture_thumb ||
  senderMeta?.avatar_thumb ||
  senderMeta?.thumbnail_url ||
  undefined
);

// Profile Settings Modal with Username Change Limits
const ProfileSettingsModal = ({ isOpen, onClose }) => {
  const { user, updateProfile, updateProfilePicture, checkAuth } = useAuth();
  const [username, setUsername] = useState(user?.username || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [pictureUploading, setPictureUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null); // {available, reason}
  const [checkingUsername, setCheckingUsername] = useState(false);
  const fileInputRef = useRef(null);
  const usernameCheckTimeoutRef = useRef(null);

  const changesRemaining = user?.username_changes_remaining ?? 3;
  const changeLimit = user?.username_change_limit ?? 3;
  const changeWindowDays = user?.username_change_window_days ?? 30;

  useEffect(() => {
    if (isOpen && user) {
      setUsername(user.username || '');
      setBio(user.bio || '');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setUsernameStatus(null);
    }
  }, [isOpen, user]);

  // Debounced username availability check
  useEffect(() => {
    if (usernameCheckTimeoutRef.current) {
      clearTimeout(usernameCheckTimeoutRef.current);
    }

    if (!username || username === user?.username) {
      setUsernameStatus(null);
      return;
    }

    usernameCheckTimeoutRef.current = setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const response = await axios.post(`${BACKEND_URL}/api/auth/check-username`, 
          { username }, 
          { withCredentials: true }
        );
        setUsernameStatus(response.data);
      } catch (err) {
        setUsernameStatus({ available: false, reason: 'Error checking username' });
      } finally {
        setCheckingUsername(false);
      }
    }, 500);

    return () => {
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
    };
  }, [username, user?.username]);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    
    if (newPassword && newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (username !== user.username && usernameStatus && !usernameStatus.available) {
      setError(usernameStatus.reason);
      return;
    }
    
    setLoading(true);
    try {
      const data = {};
      if (username !== user.username) data.username = username;
      if (bio !== (user.bio || '')) data.bio = bio;
      if (newPassword) {
        data.current_password = currentPassword;
        data.new_password = newPassword;
      }
      
      if (Object.keys(data).length > 0) {
        await updateProfile(data);
        await checkAuth(); // Refresh user data to get updated changes_remaining
        setSuccess('Profile updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setUsernameStatus(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || pictureUploading) return;
    
    setPictureUploading(true);
    setError('');
    setSuccess('');
    try {
      const optimizedPhoto = await prepareProfilePictureForUpload(file);
      await updateProfilePicture(optimizedPhoto);
      setSuccess('Profile picture updated');
    } catch (err) {
      setError(getProfilePictureUploadErrorMessage(err));
    } finally {
      setPictureUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        className="bg-[hsl(var(--card))] border border-white/10 shadow-elev-lg rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white font-heading">Profile Settings</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Profile Picture */}
        <div className="flex justify-center mb-6">
          <div
            className={`relative cursor-pointer group ${pictureUploading ? 'pointer-events-none opacity-80' : ''}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <Avatar src={user?.profile_picture} username={user?.username} size="xl" showStatus={false} />
            <div className={`absolute inset-0 bg-black/50 rounded-full flex items-center justify-center transition-opacity ${pictureUploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
              {pictureUploading ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <Edit2 className="w-6 h-6 text-white" />}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePictureChange}
              accept="image/*,.heic,.heif"
              disabled={pictureUploading}
              className="hidden"
            />
          </div>
        </div>
        {pictureUploading && (
          <p className="-mt-4 mb-4 text-center text-xs text-zinc-400">
            Optimizing and uploading photo...
          </p>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 rounded-xl mb-4 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-2 rounded-xl mb-4 text-sm">
            {success}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-zinc-300">Username</label>
              <span className="text-xs text-zinc-500">
                {changesRemaining}/{changeLimit} changes left ({changeWindowDays} days)
              </span>
            </div>
            <div className="relative">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                className={`w-full bg-zinc-800 border rounded-xl px-4 py-3 text-white placeholder:text-zinc-500 outline-none pr-10 ${
                  usernameStatus 
                    ? usernameStatus.available 
                      ? 'border-emerald-500/50' 
                      : 'border-red-500/50'
                    : 'border-zinc-700 focus:border-cyan-500/50'
                }`}
                placeholder="your_username"
                data-testid="profile-username-input"
              />
              {checkingUsername && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" />
                </div>
              )}
              {!checkingUsername && usernameStatus && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {usernameStatus.available ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <X className="w-4 h-4 text-red-400" />
                  )}
                </div>
              )}
            </div>
            {usernameStatus && (
              <p className={`text-xs mt-1 ${usernameStatus.available ? 'text-emerald-400' : 'text-red-400'}`}>
                {usernameStatus.reason}
              </p>
            )}
            {changesRemaining === 0 && username !== user?.username && (
              <p className="text-xs text-amber-400 mt-1">
                You've reached the username change limit. Try again in a few days.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell us about yourself..."
              rows={3}
              maxLength={200}
              className="w-full bg-white/[0.04] border border-white/[0.08] focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/25 rounded-2xl px-4 py-3 text-white placeholder:text-zinc-500 outline-none resize-none"
              data-testid="profile-bio-input"
            />
            <p className="text-xs text-zinc-500 mt-1">{bio.length}/200</p>
          </div>

          <div className="border-t border-zinc-800 pt-4 mt-4">
            <h3 className="text-sm font-medium text-zinc-300 mb-3">Change Password</h3>
            <div className="space-y-3">
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current password"
                className="w-full bg-white/[0.04] border border-white/[0.08] focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/25 rounded-2xl px-4 py-3 text-white placeholder:text-zinc-500 outline-none"
                data-testid="current-password-input"
              />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                className="w-full bg-white/[0.04] border border-white/[0.08] focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/25 rounded-2xl px-4 py-3 text-white placeholder:text-zinc-500 outline-none"
                data-testid="new-password-input"
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full bg-white/[0.04] border border-white/[0.08] focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/25 rounded-2xl px-4 py-3 text-white placeholder:text-zinc-500 outline-none"
                data-testid="confirm-new-password-input"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-3 rounded-xl border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors">
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            disabled={loading || (username !== user?.username && changesRemaining === 0) || (usernameStatus && !usernameStatus.available)}
            className="ui-btn ui-btn-primary flex-1"
            data-testid="save-profile-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Create Channel Modal
const CreateChannelModal = ({ isOpen, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdChannel, setCreatedChannel] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/channels`, { name: name.trim() }, { withCredentials: true });
      setCreatedChannel(response.data);
      onCreated(response.data);
    } catch (error) {
      console.error('Create channel error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(createdChannel.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setName('');
    setCreatedChannel(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }} className="bg-[hsl(var(--card))] border border-white/10 shadow-elev-lg rounded-2xl p-6 w-full max-w-md">
        {!createdChannel ? (
          <>
            <h2 className="text-xl font-bold text-white mb-4 font-heading tracking-tight">Create Channel</h2>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Channel name" className="ui-input mb-4" data-testid="channel-name-input" onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
            <div className="flex gap-3">
              <button onClick={handleClose} className="ui-btn ui-btn-secondary flex-1">Cancel</button>
              <button onClick={handleCreate} disabled={loading || !name.trim()} className="ui-btn ui-btn-primary flex-1" data-testid="create-channel-btn">{loading ? 'Creating…' : 'Create'}</button>
            </div>
          </>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-500/15 rounded-2xl flex items-center justify-center mx-auto mb-4 ring-1 ring-inset ring-emerald-500/25"><Check className="w-8 h-8 text-emerald-400" /></div>
            <h2 className="text-xl font-bold text-white mb-2 font-heading tracking-tight">Channel Created!</h2>
            <p className="text-zinc-400 mb-6 text-sm">Share this secret code with others to let them join:</p>
            <div className="bg-surface-2 rounded-xl p-4 flex items-center justify-between mb-6">
              <span className="text-2xl font-mono font-bold text-white tracking-widest" data-testid="channel-code">{createdChannel.code}</span>
              <button onClick={copyCode} className="ui-icon-btn ui-icon-btn-md" aria-label="Copy code" data-testid="copy-code-btn">{copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}</button>
            </div>
            <button onClick={handleClose} className="ui-btn ui-btn-primary w-full">Done</button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Join Channel Modal
const JoinChannelModal = ({ isOpen, onClose, onJoined }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleJoin = async () => {
    if (code.length !== 6) {
      setError('Code must be 6 characters');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/channels/join`, { code: code.toUpperCase() }, { withCredentials: true });
      onJoined(response.data);
      setCode('');
      onClose();
    } catch (error) {
      setError(error.response?.data?.detail || 'Channel not found');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }} className="bg-[hsl(var(--card))] border border-white/10 shadow-elev-lg rounded-2xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-white mb-4 font-heading">Join Channel</h2>
        <p className="text-zinc-400 mb-4">Enter the 6-letter secret code to join a channel</p>
        <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))} placeholder="ABCDEF" className="w-full bg-white/[0.04] border border-white/[0.08] focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/25 rounded-2xl px-4 py-3 text-white text-center text-2xl font-mono tracking-widest placeholder:text-zinc-600 outline-none mb-2" data-testid="join-code-input" onKeyDown={(e) => e.key === 'Enter' && handleJoin()} />
        {error && <p className="text-red-400 text-sm mb-4" data-testid="join-error">{error}</p>}
        <div className="flex gap-3 mt-4">
          <button onClick={handleClose} className="flex-1 px-4 py-3 rounded-xl border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors">Cancel</button>
          <button onClick={handleJoin} disabled={loading || code.length !== 6} className="ui-btn ui-btn-primary flex-1" data-testid="join-channel-btn">{loading ? 'Joining…' : 'Join'}</button>
        </div>
      </motion.div>
    </div>
  );
};

// New DM Modal with User Search
const NewDmModal = ({ isOpen, onClose, onDmCreated }) => {
  const { presenceByUser, presenceNow } = useSocket();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSearching(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/users/search?q=${encodeURIComponent(query)}`, { 
          withCredentials: true 
        });
        if (!cancelled) {
          setSearchResults(Array.isArray(response.data) ? response.data.slice(0, 1) : []);
        }
      } catch (error) {
        console.error('Search error:', error);
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleSelectUser = async (userId) => {
    setCreating(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/dms`, { user_id: userId }, { withCredentials: true });
      onDmCreated(response.data);
      onClose();
    } catch (error) {
      console.error('Create DM error:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearching(false);
    onClose();
  };

  if (!isOpen) return null;

  const trimmedSearchQuery = searchQuery.trim();
  const showNoResults = trimmedSearchQuery && !searching && searchResults.length === 0;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }} className="bg-[hsl(var(--card))] border border-white/10 shadow-elev-lg rounded-2xl p-6 w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white font-heading">New Message</h2>
          <button onClick={handleClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Enter full username..."
            className="w-full bg-white/[0.04] border border-white/[0.08] focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/25 rounded-2xl pl-10 pr-4 py-3 text-white placeholder:text-zinc-500 outline-none"
            data-testid="dm-search-input"
            autoFocus
          />
          {searching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" />
            </div>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {showNoResults ? (
            <div className="text-center py-8">
              <Search className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">No user found for "{trimmedSearchQuery}"</p>
              <p className="text-zinc-600 text-sm mt-1">Enter the complete username exactly</p>
            </div>
          ) : searchResults.length === 0 && !trimmedSearchQuery ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">Enter full username to search</p>
              <p className="text-zinc-600 text-sm mt-1">No suggestions or recent profiles are shown for privacy</p>
            </div>
          ) : (
            <>
              {trimmedSearchQuery && searchResults.length > 0 && (
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2 px-1">
                  Exact match
                </p>
              )}
              <div className="space-y-1">
                {searchResults.map((contact) => {
                  const liveContact = overlayUserPresence(contact, presenceByUser);
                  return (
                  <button
                    key={liveContact.id}
                    onClick={() => handleSelectUser(liveContact.id)}
                    disabled={creating}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-zinc-800 transition-colors text-left disabled:opacity-50"
                    data-testid={`user-${liveContact.id}`}
                  >
            <Avatar src={liveContact.profile_picture_thumb || liveContact.avatar_thumb || liveContact.thumbnail_url} username={liveContact.username} size="md" isOnline={liveContact.is_online} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">@{liveContact.username}</p>
                      <p className="text-xs text-zinc-500 truncate">
                        {liveContact.bio || (liveContact.is_online ? 'Online' : `Last seen ${formatLastSeen(liveContact.last_seen, presenceNow)}`)}
                      </p>
                    </div>
                    {creating && (
                      <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" />
                    )}
                  </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 text-center">
            For privacy, only exact username matches are shown
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// User Profile Modal (for viewing other users)
const UserProfileModal = ({ user: profileUser, isOpen, onClose, onStartDm }) => {
  const { presenceByUser, presenceNow } = useSocket();
  if (!isOpen || !profileUser) return null;
  const liveProfileUser = overlayUserPresence(profileUser, presenceByUser);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }} className="bg-[hsl(var(--card))] border border-white/10 shadow-elev-lg rounded-2xl p-6 w-full max-w-sm">
        <div className="flex justify-end mb-4">
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        <div className="text-center">
          <Avatar src={liveProfileUser.profile_picture} username={liveProfileUser.username} size="xl" isOnline={liveProfileUser.is_online} />
          <h3 className="text-xl font-bold text-white mt-4 font-heading">{liveProfileUser.username}</h3>
          <p className="text-sm text-zinc-400 mt-1">
            {liveProfileUser.is_online ? (
              <span className="text-emerald-400">Online</span>
            ) : (
              `Last seen ${formatLastSeen(liveProfileUser.last_seen, presenceNow)}`
            )}
          </p>
          {liveProfileUser.bio && (
            <p className="text-zinc-300 mt-3 text-sm">{liveProfileUser.bio}</p>
          )}
        </div>
        
        {onStartDm && (
          <button 
            onClick={() => { onStartDm(liveProfileUser.id); onClose(); }}
            className="ui-btn ui-btn-primary w-full mt-6"
            data-testid="start-dm-btn"
          >
            <MessageSquare className="w-5 h-5" /> Message
          </button>
        )}
      </motion.div>
    </div>
  );
};

// Video message player — native controls, metadata preload, pauses others on play
const VideoMessagePlayer = ({ message, src, isOwn = false, isPending = false, isFailed = false, isUploading = false, onRetry, onCancel, onOpen, canDelete = false, onDelete }) => {
  const showOverlay = (isUploading || isPending) && !isFailed;
  const isReady = Boolean(src) && !showOverlay && !isFailed; // upload confirmed / received
  const progress = Number.isFinite(message?.upload_progress) ? message.upload_progress : null;

  // Receiver-side lazy download: nothing is streamed from the server until the
  // user taps Download. After download we hold a local blob URL and play from it.
  const [dlState, setDlState] = useState('idle'); // idle | downloading | ready | error
  const [dlProgress, setDlProgress] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const blobUrlRef = useRef(null);
  useEffect(() => () => { if (blobUrlRef.current) { try { URL.revokeObjectURL(blobUrlRef.current); } catch {} } }, []);

  const sizeLabel = Number.isFinite(message?.file_size) ? formatMb(message.file_size) : null;
  const durationLabel = Number.isFinite(message?.duration) ? formatVideoDuration(message.duration) : null;
  const infoParts = ['Video', sizeLabel, durationLabel].filter(Boolean);

  // Sender always plays from the real URL; receiver plays only once downloaded.
  const playableSrc = isOwn ? src : blobUrl;
  const canPlay = isReady && Boolean(playableSrc);

  const handleDeleteClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onDelete?.();
  };

  const startDownload = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (dlState === 'downloading') return;
    const url = resolveMediaUrl(message?.content);
    if (!url) { setDlState('error'); return; }
    setDlState('downloading'); setDlProgress(0);
    try {
      const res = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob',
        onDownloadProgress: (evt) => {
          const total = evt.total || message?.file_size;
          if (total) setDlProgress(Math.min(100, Math.round((evt.loaded / total) * 100)));
        },
      });
      const objectUrl = URL.createObjectURL(res.data);
      blobUrlRef.current = objectUrl;
      setBlobUrl(objectUrl);
      setDlState('ready');
    } catch {
      setDlState('error');
    }
  };

  const handleCardClick = (event) => {
    event.stopPropagation();
    if (!isReady) return;
    if (isOwn) { onOpen?.(src); return; }
    // Receiver: play only after a successful download.
    if (dlState === 'ready' && blobUrl) onOpen?.(blobUrl);
  };

  // ── Receiver, not yet downloaded: lightweight placeholder (no auto-stream) ──
  const showReceiverPlaceholder = isReady && !isOwn && dlState !== 'ready';

  return (
    <div className="relative overflow-hidden rounded-xl bg-black/40" onClick={handleCardClick} data-testid="video-message">
      {showReceiverPlaceholder ? (
        <div className="flex min-h-[9rem] w-64 max-w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-zinc-800 to-zinc-900 px-4 py-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15">
            <Video className="h-6 w-6 text-zinc-200" aria-hidden="true" />
          </div>
          <p className="text-[13px] font-medium text-zinc-200">{infoParts.join('  •  ')}</p>
          <button
            type="button"
            onClick={startDownload}
            onPointerDown={(e) => e.stopPropagation()}
            className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${dlState === 'error' ? 'bg-red-500/80 text-white' : 'bg-brand-gradient text-white'}`}
            aria-label="Download video"
            data-chat-interactive="true"
            data-testid="video-download-btn"
            disabled={dlState === 'downloading'}
          >
            {dlState === 'downloading' ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> {dlProgress}%</>
            ) : dlState === 'error' ? (
              <><RotateCcw className="h-4 w-4" /> Retry download</>
            ) : (
              <><Download className="h-4 w-4" /> Download</>
            )}
          </button>
          {dlState === 'downloading' && (
            <div className="mt-1 h-1 w-40 overflow-hidden rounded-full bg-white/15">
              <div className="h-full bg-white transition-[width] duration-200" style={{ width: `${dlProgress}%` }} />
            </div>
          )}
        </div>
      ) : (playableSrc || (isOwn && src)) ? (
        // Poster frame. Muted + no controls; playback happens in the viewer on tap.
        <video
          src={playableSrc || src}
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
          className="max-h-72 max-w-full rounded-xl bg-black pointer-events-none"
          style={{ display: 'block' }}
        />
      ) : (
        <div className="flex h-40 w-56 items-center justify-center text-zinc-500">
          <Video className="h-8 w-8" />
        </div>
      )}

      {canPlay && (
        <button
          type="button"
          onClick={handleCardClick}
          aria-label="Play video"
          className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors hover:bg-black/25"
          data-chat-interactive="true"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm ring-1 ring-white/20">
            <Play className="h-6 w-6 translate-x-0.5 text-white" fill="currentColor" />
          </span>
        </button>
      )}

      {/* Delete: sender's own confirmed video only */}
      {isReady && canDelete && (
        <button
          type="button"
          onClick={handleDeleteClick}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Delete video"
          className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm ring-1 ring-white/20 transition-colors hover:bg-red-500/80"
          data-chat-interactive="true"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}

      {/* Info bar on a playable card (sender, or receiver after download) */}
      {canPlay && (
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex items-center gap-1.5 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 text-[11px] font-medium text-white">
          <Video className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden="true" />
          <span className="truncate">{infoParts.join('  •  ')}</span>
        </div>
      )}

      {showOverlay && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/55 text-xs font-medium text-white backdrop-blur-[1px]">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>{progress != null && progress > 0 ? `Uploading ${progress}%` : 'Uploading…'}</span>
          {progress != null && (
            <div className="h-1 w-28 overflow-hidden rounded-full bg-white/20">
              <div className="h-full bg-white transition-[width] duration-200" style={{ width: `${progress}%` }} />
            </div>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onCancel?.(e); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="mt-1 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold text-white"
            data-chat-interactive="true"
          >
            Cancel
          </button>
        </div>
      )}

      {isFailed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 px-3 text-center text-xs font-medium text-white backdrop-blur-[1px]">
          <span>Failed to send video</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRetry}
              className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-zinc-950"
              data-chat-interactive="true"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold text-white"
              data-chat-interactive="true"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Message Status Indicator
const MessageStatus = ({ status, isOwn }) => {
  if (!isOwn) return null;

  if (status === 'failed') {
    return <X className="w-3.5 h-3.5 text-red-400" />;
  }
  
  if (status === 'seen') {
    return <CheckCheck className="w-3.5 h-3.5 text-cyan-300" />;
  } else if (status === 'delivered') {
    return <CheckCheck className="w-3.5 h-3.5 text-zinc-400" />;
  } else {
    return <Check className="w-3.5 h-3.5 text-zinc-500" />;
  }
};

// Reactions Display
const ReactionsDisplay = ({ reactions, onReact, currentUserId, isOwn = false }) => {
  const safeReactions = normalizeReactions(reactions);
  const entries = Object.entries(safeReactions);
  if (entries.length === 0) return null;

  return (
    <div className={`mt-0.5 flex max-w-full flex-wrap gap-1 ${isOwn ? 'self-end justify-end' : 'self-start justify-start'} w-fit`}>
      {entries.map(([emoji, userIds]) => (
        <button
          key={emoji}
          onClick={() => onReact(emoji)}
          className={`px-2 py-[3px] rounded-full text-[13px] flex items-center gap-1 transition-colors active:scale-95 ${
            userIds.includes(currentUserId)
              ? 'bg-brand-gradient-soft ring-1 ring-inset ring-white/[0.12] text-white'
              : 'bg-surface-2 ring-1 ring-inset ring-white/[0.06] hover:bg-surface-3'
          }`}
        >
          <span>{emoji}</span>
          <span className={`text-[11px] font-medium ${userIds.includes(currentUserId) ? 'text-cyan-200' : 'text-zinc-400'}`}>{userIds.length}</span>
        </button>
      ))}
    </div>
  );
};

// Swipe-to-reply. The bubble follows the finger via a DIRECT transform on a ref
// (no React state per pointer move → no message-list re-render), with a resistance
// curve past ~36px and a clamped max. Reply activates ONLY on release once the peak
// crossed the threshold; then the bubble animates smoothly back. Vertical scrolling
// is preserved (we lock to horizontal only after clear horizontal intent, and the
// wrapper keeps touch-action: pan-y). Uses pointer events (covers desktop + mobile)
// with a touch fallback. No new dependency.
const SWIPE_REPLY_THRESHOLD = 44;   // release past this → activate reply
const SWIPE_MAX_DRAG_PX = 56;       // hard clamp on visible travel
const SWIPE_RESIST_AFTER_PX = 36;   // beyond this, movement is dampened
const SWIPE_LOCK_PX = 10;           // engage only after a clear horizontal move
const SWIPE_SNAP_EASE = 'transform 200ms cubic-bezier(0.22, 1, 0.36, 1)';

const prefersReducedMotion = () => {
  try {
    return typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
};

// Apply resistance so travel eases toward the clamp instead of stopping hard.
const swipeCurve = (dx) => {
  if (dx <= SWIPE_RESIST_AFTER_PX) return dx;
  const over = dx - SWIPE_RESIST_AFTER_PX;
  return Math.min(SWIPE_MAX_DRAG_PX, SWIPE_RESIST_AFTER_PX + over * 0.35);
};

// targetRef: the element that visually moves. iconRef: the reveal icon (opacity/scale).
const useSwipeToReply = ({ onReply, disabled, targetRef, iconRef }) => {
  const stateRef = useRef({ startX: 0, startY: 0, active: false, locked: false, peak: 0, vibrated: false });
  const rafRef = useRef(0);
  const suppressClickRef = useRef(false);

  const paint = (dx) => {
    const el = targetRef.current;
    if (el) el.style.transform = dx > 0 ? `translate3d(${dx}px,0,0)` : 'translate3d(0,0,0)';
    const icon = iconRef.current;
    if (icon) {
      const progress = Math.max(0, Math.min(1, dx / SWIPE_REPLY_THRESHOLD));
      icon.style.opacity = String(progress);
      icon.style.transform = `translateY(-50%) scale(${0.6 + progress * 0.4})`;
    }
  };

  const settleBack = () => {
    const el = targetRef.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      el.style.transform = 'translate3d(0,0,0)';
      el.style.willChange = '';
    } else {
      el.style.transition = SWIPE_SNAP_EASE;
      el.style.transform = 'translate3d(0,0,0)';
      const clear = () => {
        el.style.transition = '';
        el.style.willChange = '';
        el.removeEventListener('transitionend', clear);
      };
      el.addEventListener('transitionend', clear);
    }
    const icon = iconRef.current;
    if (icon) { icon.style.opacity = '0'; icon.style.transform = 'translateY(-50%) scale(0.6)'; }
  };

  const reset = () => {
    stateRef.current.active = false;
    stateRef.current.locked = false;
    stateRef.current.peak = 0;
    stateRef.current.vibrated = false;
  };

  const isInteractive = (target) =>
    target?.closest?.('[data-chat-interactive="true"], a, button, video, audio, input, textarea');

  const onPointerDown = (event) => {
    if (disabled) return;
    // Only primary button / touch / pen — ignore right-click etc.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (isInteractive(event.target)) { stateRef.current.active = false; return; }
    stateRef.current = { startX: event.clientX, startY: event.clientY, active: true, locked: false, peak: 0, vibrated: false };
  };

  const onPointerMove = (event) => {
    const s = stateRef.current;
    if (!s.active || disabled) return;
    const dx = event.clientX - s.startX;
    const dy = event.clientY - s.startY;
    if (!s.locked) {
      // Vertical intent → release so the list scrolls normally.
      if (Math.abs(dy) > Math.abs(dx)) { s.active = false; return; }
      if (dx < SWIPE_LOCK_PX) return; // ignore taps / tiny / opposite-direction moves
      s.locked = true;
      const el = targetRef.current;
      if (el && !prefersReducedMotion()) { el.style.transition = ''; el.style.willChange = 'transform'; }
    }
    if (dx <= 0) { s.peak = 0; cancelAnimationFrame(rafRef.current); rafRef.current = requestAnimationFrame(() => paint(0)); return; }
    const shown = swipeCurve(dx);
    s.peak = Math.max(s.peak, dx);
    if (!s.vibrated && dx >= SWIPE_REPLY_THRESHOLD) {
      s.vibrated = true;
      try { navigator.vibrate?.(8); } catch { /* haptics optional */ }
    }
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => paint(shown));
  };

  const onPointerUp = () => {
    const s = stateRef.current;
    const crossed = s.locked && s.peak >= SWIPE_REPLY_THRESHOLD;
    if (s.locked) suppressClickRef.current = true; // swallow the synthetic click
    cancelAnimationFrame(rafRef.current);
    settleBack();
    reset();
    if (crossed) onReply?.(); // activate ONLY on release
  };

  const onClickCapture = (event) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return {
    swipeHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onClickCapture,
    },
  };
};

// ── FLIP stack-insertion animation (presentational only) ────────────────────
// ONE stable identity per logical message, used as BOTH the React render key and
// the animation key: client_temp_id survives the temp→real id swap on server ACK
// (the server echoes it back and mergeMessageRecords preserves it), so the
// optimistic bubble and its confirmed version are the SAME DOM node — server
// fields simply merge into it. No remount, no restarted animation. Incoming and
// history messages without a client_temp_id key by their immutable server id.
const getStableMessageKey = (message) =>
  String(message?.client_temp_id || message?.tempId || message?.id || '');

// Renders its children untouched (no extra DOM). On commits where the message
// list or the typing flag changes, it captures the pre-mutation VISUAL geometry
// in getSnapshotBeforeUpdate — the only React lifecycle that runs after render
// but BEFORE the DOM mutates, so the rects include any in-flight animation
// transforms and can never be stale. Immediately after the mutation
// (componentDidUpdate, still before paint) it pins the bottom instantly when the
// user was near it and FLIPs the visible stack from exactly what was on screen.
// Entry eligibility is consumed here in the commit phase — once per registration,
// never during render — so StrictMode double-rendering cannot eat an entry.
// Everything is wrapped: any failure degrades to the non-animated baseline.
class ChatLayoutTransition extends React.Component {
  componentWillUnmount() {
    try { cancelAllChatAnimations(); } catch { /* noop */ }
  }

  isEnabled() {
    // While the tab is hidden, browsers throttle/pause requestAnimationFrame,
    // so animations would only burn CPU and appear frozen. Skip the FLIP/entry
    // work entirely; messages simply render in place and are correct when the
    // tab becomes visible again.
    const documentVisible = typeof document === 'undefined'
      || typeof document.visibilityState !== 'string'
      || document.visibilityState !== 'hidden';
    return Boolean(this.props.enabled)
      && typeof document !== 'undefined'
      && documentVisible
      && !prefersReducedMotion();
  }

  // Cheap O(1) identity of the visible-stack SHAPE. New arrivals, pagination
  // and deletions change length/first/last keys; delivery-status churn (sent →
  // delivered → seen), reaction patches and content edits do NOT. Gating the
  // snapshot on this signature means the frequent status/receipt updates no
  // longer trigger a full querySelectorAll + getBoundingClientRect capture and
  // a FLIP pass over every visible row. (Entry-eligibility consumption below
  // still runs on any messages change, so genuinely new bubbles still animate.)
  messagesShapeSignature(messages) {
    if (!Array.isArray(messages) || messages.length === 0) return '0';
    const last = messages.length - 1;
    return `${messages.length}|${getStableMessageKey(messages[0])}|${getStableMessageKey(messages[last])}`;
  }

  getSnapshotBeforeUpdate(prevProps) {
    try {
      if (!this.isEnabled()) return null;
      // Conversation switch: never FLIP one conversation's rows toward another's.
      if (prevProps.conversationId !== this.props.conversationId) return null;
      const messagesShapeChanged = this.messagesShapeSignature(this.props.messages) !== this._lastShapeSig;
      const typingChanged = Boolean(prevProps.showTyping) !== Boolean(this.props.showTyping);
      if (!messagesShapeChanged && !typingChanged) return null;
      const scroller = this.props.scrollerRef.current;
      if (!scroller) return null;
      const snapshot = captureChatLayout(scroller);
      if (!snapshot) return null;
      // Typing stop: clone the indicator content BEFORE React removes the row so
      // a ghost can fade out in place (the real row must leave layout at once so
      // the stack can settle with no leftover gap).
      if (prevProps.showTyping && !this.props.showTyping) {
        try {
          const rowEl = scroller.querySelector(`[data-chat-layout-row="${TYPING_ROW_KEY}"]`);
          const entryEl = rowEl && rowEl.firstElementChild;
          if (entryEl) {
            const rect = entryEl.getBoundingClientRect();
            const viewportH = (typeof window !== 'undefined' && window.innerHeight) || 0;
            if (rect.bottom > 0 && rect.top < viewportH) {
              snapshot.typingGhost = { node: entryEl.cloneNode(true), rect };
            }
          }
        } catch { /* the exit ghost is optional polish */ }
      }
      return snapshot;
    } catch { return null; }
  }

  componentDidUpdate(prevProps, _prevState, snapshot) {
    try {
      const { messages, showTyping, scrollerRef, entryRefs, typingEntryRef, composerGetter } = this.props;
      if (prevProps.conversationId !== this.props.conversationId) {
        this._lastShapeSig = null;
        try { cancelAllChatAnimations(); } catch { /* noop */ }
        return;
      }
      // Record the committed shape so the next snapshot decision diffes against
      // what is actually on screen.
      this._lastShapeSig = this.messagesShapeSignature(messages);
      const scroller = scrollerRef.current;
      const messagesChanged = prevProps.messages !== messages;

      // Entry eligibility is EXPLICIT: the optimistic-send and socket-accept code
      // paths registered 'outgoing-new' / 'incoming-new' under the message's
      // stable key at the moment it entered this conversation. Consuming here is
      // once-only, so ACK reconciliation, status/seen updates, history, cache,
      // pagination, reconnect syncs and re-renders can never re-trigger an entry.
      // Consumption also runs when animations are disabled/reduced so a stale
      // registration can never replay later.
      const freshBatch = [];
      if (messagesChanged) {
        for (const m of (messages || [])) {
          if (!m) continue;
          const key = getStableMessageKey(m);
          const direction = consumeEntryAnimation(key);
          if (direction) {
            freshBatch.push({ key, side: direction === 'outgoing-new' ? 'outgoing' : 'incoming' });
          }
        }
      }

      if (!snapshot || !this.isEnabled() || !scroller) return;

      const typingAppeared = Boolean(showTyping) && !prevProps.showTyping;
      const typingGone = !showTyping && Boolean(prevProps.showTyping);
      if (freshBatch.length === 0 && !typingAppeared && !typingGone) return;

      // A new bubble may occupy the typing row's old spot — never overlap a ghost.
      if (freshBatch.length > 0) dismissTypingGhost();

      // Typing toggling mid-entry (they sent the instant they stopped typing) is
      // part of the SAME gesture: settle over the full stack duration so the
      // descend never rushes the still-entering bubble.
      const typingMidEntry = (typingAppeared || typingGone) && hasActiveEntries();
      const layoutResult = runChatLayoutTransition(scroller, snapshot, {
        duration: freshBatch.length > 0 || typingMidEntry ? CHAT_ANIM.STACK_MS : CHAT_ANIM.TYPING_STACK_MS,
      });

      if (freshBatch.length > 0) {
        // Older bubbles still entering finish quickly so rapid sends never show
        // several bubbles hovering over each other near the composer.
        accelerateEntriesExcept(new Set(freshBatch.map((b) => b.key)));
        const composerEl = (() => { try { return composerGetter ? composerGetter() : null; } catch { return null; } })();
        // The vacating row still covers the new slot at t=0 (FLIP continuity), so
        // when rows actually had to move, let them glide clear first — the bubble
        // stays hidden during its delay and pops into an already-open gap.
        const clearDelay = layoutResult.moved > 0 ? CHAT_ANIM.ENTRY_CLEAR_DELAY_MS : 0;
        freshBatch.forEach(({ key, side }, i) => {
          const inner = entryRefs.current.get(key);
          if (!inner) return;
          animateMessageEntry(inner, key, {
            side,
            composerEl,
            delay: clearDelay + Math.min(i, CHAT_ANIM.STAGGER_MAX) * CHAT_ANIM.STAGGER_MS,
          });
        });
      }

      if (typingAppeared && typingEntryRef.current) {
        animateTypingEntry(typingEntryRef.current);
      }

      // Typing stopped with no replacement message: fade the ghost out in place
      // while the stack settles down (only if the user could see it).
      // No ghost while a bubble is still entering — a just-arrived message takes
      // the typing row's spot, and a fading clone there reads as a collision.
      if (typingGone && snapshot.typingGhost && freshBatch.length === 0 && !typingMidEntry && snapshot.wasNearBottom) {
        spawnTypingExitGhost(snapshot.typingGhost.node, snapshot.typingGhost.rect);
      }
    } catch (err) {
      try { console.debug('[chat-anim] transition skipped:', err); } catch { /* noop */ }
    }
  }

  render() { return this.props.children; }
}

// Message Bubble with all features
// timeLabel/statusLabel are precomputed by the parent's row memo so a bubble
// re-render never re-runs Date/Intl formatting (fallbacks keep it safe if a
// future caller omits them).
const MessageBubble = React.memo(({ message, isOwn, showAvatar, isGrouped = false, showMeta = true, timeLabel, statusLabel, onReply, onDelete, onEdit, onReact, onRetry, onCancelLocal, onFocusComposer, onShouldPreserveComposerFocus, replyToMessage, onQuoteClick, currentUserId, senderMeta }) => {
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [showFullEmojiPicker, setShowFullEmojiPicker] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showVideoViewer, setShowVideoViewer] = useState(false);
  const [videoViewerSrc, setVideoViewerSrc] = useState(null);
  const popupRef = useRef(null);
  const bubbleRef = useRef(null);
  const fullEmojiPickerRef = useRef(null);
  const restoreComposerAfterDeleteRef = useRef(false);
  const isImage = message.message_type === 'image';
  const isVoice = message.message_type === 'voice';
  const isVideo = message.message_type === 'video';
  const isPending = message.pending;
  const isFailed = message.failed || message.status === 'failed';
  const canEdit = isOwn && !isImage && !isVoice && !isVideo;
  const imageUrl = isImage ? resolveMediaUrl(message.content) : null;
  const videoUrl = isVideo ? resolveMediaUrl(message.content) : null;

  useEffect(() => {
    if (!showActionPopup && !showFullEmojiPicker) return;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (popupRef.current?.contains(target) || bubbleRef.current?.contains(target) || fullEmojiPickerRef.current?.contains(target)) return;
      setShowActionPopup(false);
      setShowFullEmojiPicker(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [showActionPopup, showFullEmojiPicker]);

  const closeMenus = () => {
    setShowActionPopup(false);
    setShowFullEmojiPicker(false);
  };

  const handleReact = (emoji) => {
    if (!emoji) return;
    onReact(message.id, emoji);
    closeMenus();
  };

  const preserveActionFocus = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const preserveDeleteActionFocus = (event) => {
    restoreComposerAfterDeleteRef.current = onShouldPreserveComposerFocus?.() === true;
    event.preventDefault();
    event.stopPropagation();
  };

  const stopPickerEvent = (event) => {
    event.stopPropagation();
  };

  const handleBubblePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const shouldPreserveComposer = onShouldPreserveComposerFocus?.() === true;
    if (shouldPreserveComposer) {
      event.preventDefault();
    }
  };

  const toggleActionPopup = () => {
    if (isFailed && isOwn) {
      onRetry?.(message);
      return;
    }
    if (isPending) return;
    setShowActionPopup((prev) => !prev);
    if (showFullEmojiPicker) setShowFullEmojiPicker(false);
  };

  const openImageViewer = () => {
    if (!imageUrl) return;
    closeMenus();
    setShowImageViewer(true);
  };

  const handleBubbleClick = () => {
    if (isImage) {
      openImageViewer();
      return;
    }
    toggleActionPopup();
  };

  const openImageActions = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isPending && !isFailed) return;
    setShowActionPopup((prev) => !prev);
    if (showFullEmojiPicker) setShowFullEmojiPicker(false);
  };

  const handleImageRetry = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRetry?.(message);
  };

  const handleImageCancel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onCancelLocal?.(message);
    if (restoreComposerAfterDeleteRef.current) {
      requestAnimationFrame(() => onFocusComposer?.());
    }
    restoreComposerAfterDeleteRef.current = false;
  };

  const handleDeleteClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const shouldRestoreComposer = restoreComposerAfterDeleteRef.current || onShouldPreserveComposerFocus?.() === true;
    onDelete(message.id);
    closeMenus();
    restoreComposerAfterDeleteRef.current = false;
    if (shouldRestoreComposer) {
      requestAnimationFrame(() => onFocusComposer?.());
    }
  };

  const swipeWrapRef = useRef(null);
  const swipeIconRef = useRef(null);
  const { swipeHandlers } = useSwipeToReply({
    onReply: () => onReply?.(message),
    disabled: isPending || isFailed,
    targetRef: swipeWrapRef,
    iconRef: swipeIconRef,
  });

  return (
    <div
      ref={swipeWrapRef}
      className={`relative flex gap-1.5 md:gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}
      style={{ touchAction: 'pan-y', opacity: isPending ? 0.6 : 1 }}
      {...swipeHandlers}
    >
      <span
        ref={swipeIconRef}
        className="pointer-events-none absolute left-[-30px] top-1/2 text-cyan-300"
        style={{ opacity: 0, transform: 'translateY(-50%) scale(0.6)' }}
        aria-hidden="true"
      >
        <Reply className="w-4 h-4" />
      </span>
      {showAvatar && !isOwn ? (
        <Avatar
          src={getTinyMessageAvatarSrc(message, senderMeta)}
          username={message.sender_username || senderMeta?.username}
          size="sm"
          showStatus={false}
        />
      ) : !isOwn ? (
        <div className="w-8" />
      ) : null}

      <div className={`relative ml-0 flex max-w-[75%] flex-col ${isOwn ? 'ml-auto items-end' : 'items-start'}`}>
        {replyToMessage && (() => {
          const isDeleted = replyToMessage.deleted === true;
          const isUnavailable = replyToMessage.unavailable === true;
          const canNavigate = !isDeleted && !isUnavailable && Boolean(replyToMessage.id) && typeof onQuoteClick === 'function';
          const activate = (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (canNavigate) onQuoteClick(replyToMessage.id);
          };
          const senderName = replyToMessage.sender_id === currentUserId ? 'You' : replyToMessage.sender_username;
          const summary = getReplyPreviewSummary(replyToMessage);
          const thumb = getReplyPreviewThumb(replyToMessage);
          const ariaLabel = canNavigate
            ? `Reply to ${senderName || 'message'}: ${summary || 'message'}. Activate to jump to the original message.`
            : undefined;
          return (
            <div
              className={`mb-0.5 border-l-2 border-cyan-400 bg-zinc-800/50 px-2.5 py-1 text-[11px] rounded-lg ${isOwn ? 'ml-auto' : ''} ${canNavigate ? 'cursor-pointer transition-colors hover:bg-zinc-700/60 focus-visible:ring-2 focus-visible:ring-cyan-500/70 outline-none' : ''}`}
              role={canNavigate ? 'button' : undefined}
              tabIndex={canNavigate ? 0 : undefined}
              aria-label={ariaLabel}
              data-chat-interactive="true"
              onPointerDown={canNavigate ? (e) => e.stopPropagation() : undefined}
              onClick={canNavigate ? activate : undefined}
              onKeyDown={canNavigate ? ((e) => { if (e.key === 'Enter' || e.key === ' ') activate(e); }) : undefined}
            >
              {isDeleted ? (
                <p className="max-w-[180px] truncate italic text-zinc-500">Original message was deleted</p>
              ) : isUnavailable ? (
                <p className="max-w-[180px] truncate italic text-zinc-500">Original message unavailable</p>
              ) : (
                <>
                  <span className="text-cyan-300 font-medium">
                    {senderName}
                  </span>
                  <p className="max-w-[180px] truncate text-zinc-400">
                    <MediaPreview messageType={replyToMessage.message_type} fallbackText={replyToMessage.content} thumbnailUrl={thumb} />
                  </p>
                </>
              )}
            </div>
          );
        })()}

        <div className="relative w-fit max-w-full">
          <motion.div
            ref={bubbleRef}
            whileTap={{ scale: 0.988 }}
            onPointerDown={handleBubblePointerDown}
            onClick={handleBubbleClick}
            className={`cursor-pointer rounded-[22px] px-3.5 py-2 md:px-4 md:py-2.5 shadow-elev-sm transition-shadow ${isOwn ? (isFailed ? 'bg-red-600/90 text-white' : 'bg-brand-gradient text-white') : 'bg-surface-2 text-zinc-50 ring-1 ring-inset ring-white/[0.05]'} ${isOwn ? (isGrouped ? 'rounded-tr-lg' : 'rounded-tr-lg') : (isGrouped ? 'rounded-tl-lg' : 'rounded-tl-lg')}`}
            data-chat-message="true"
            data-testid="message-bubble"
          >
            {isImage ? (
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={imageUrl}
                  alt="Shared"
                  className="max-h-72 max-w-full rounded-xl object-cover"
                  loading="lazy"
                  draggable={false}
                />
                {isPending && !isFailed && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/45 text-xs font-medium text-white backdrop-blur-[1px]">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Uploading...</span>
                  </div>
                )}
                {isFailed && isOwn && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/55 px-3 text-center text-xs font-medium text-white backdrop-blur-[1px]">
                    <span>Failed to send photo</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleImageRetry}
                        onPointerDown={preserveActionFocus}
                        onMouseDown={preserveActionFocus}
                        className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-zinc-950"
                      >
                        Retry
                      </button>
                      <button
                        type="button"
                        onClick={handleImageCancel}
                        onPointerDown={preserveDeleteActionFocus}
                        onMouseDown={preserveDeleteActionFocus}
                        className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold text-white"
                        data-chat-interactive="true"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
                {!isPending && !isFailed && (
                  <button
                    type="button"
                    onClick={openImageActions}
                    onPointerDown={preserveActionFocus}
                    onMouseDown={preserveActionFocus}
                    className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/45 text-white opacity-90 shadow-lg backdrop-blur transition-colors hover:bg-black/65"
                    aria-label="Image message actions"
                    data-chat-interactive="true"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : isVoice ? (
              <VoiceMessagePlayer
                src={message.content}
                isPending={isPending}
                isFailed={isFailed}
                isUploading={message.voice_uploading}
                retryUnavailable={message.voice_retry_unavailable}
              />
            ) : isVideo ? (
              <VideoMessagePlayer
                message={message}
                src={videoUrl}
                isOwn={isOwn}
                isPending={isPending}
                isFailed={isFailed && isOwn}
                isUploading={message.video_uploading}
                onRetry={handleImageRetry}
                onCancel={handleImageCancel}
                onOpen={(playSrc) => { setVideoViewerSrc(playSrc || videoUrl); setShowVideoViewer(true); }}
                canDelete={isOwn && !isPending && !isFailed}
                onDelete={() => onDelete?.(message.id)}
              />
            ) : (
              <p className="whitespace-pre-wrap break-words text-[15px] leading-[1.35rem] md:text-base md:leading-6">{message.content}</p>
            )}
          </motion.div>

          <AnimatePresence>
            {showActionPopup && !isPending && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 420, damping: 28, mass: 0.7 }}
                className={`absolute z-20 ${isOwn ? 'right-0' : 'left-0'} bottom-full mb-2`}
              >
                <div
                  ref={popupRef}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={preserveActionFocus}
                  onMouseDown={preserveActionFocus}
                  data-chat-interactive="true"
                  className="ui-popover min-w-[190px] px-2 py-1.5"
                >
                  <div className="flex items-center gap-1">
                    {QUICK_REACTIONS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleReact(emoji)}
                        className="flex h-8 w-8 items-center justify-center rounded-xl text-[17px] transition-colors hover:bg-zinc-800 active:scale-95"
                      >
                        {emoji}
                      </button>
                    ))}
                    <button
                      onClick={() => setShowFullEmojiPicker((prev) => !prev)}
                      className="ml-0.5 flex h-8 w-8 items-center justify-center rounded-xl transition-colors hover:bg-zinc-800 active:scale-95"
                    >
                      <Plus className="w-4 h-4 text-zinc-400" />
                    </button>
                  </div>

                  <div className="my-1 h-px bg-zinc-800" />

                  <div className="flex items-center gap-1">
                    <button
                      onPointerDown={preserveActionFocus}
                      onMouseDown={preserveActionFocus}
                      onClick={() => {
                        onReply(message);
                        closeMenus();
                        onFocusComposer?.();
                      }}
                      className="inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] text-zinc-300 transition-colors hover:bg-zinc-800"
                    >
                      <Reply className="w-3.5 h-3.5" />
                      Reply
                    </button>
                      {canEdit && (
                      <button
                        onPointerDown={preserveActionFocus}
                        onMouseDown={preserveActionFocus}
                        onClick={() => {
                          onEdit(message);
                          closeMenus();
                          onFocusComposer?.();
                        }}
                        className="inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] text-zinc-300 transition-colors hover:bg-zinc-800"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Edit
                      </button>
                    )}
                    {isOwn && (
                      <button
                        onPointerDown={preserveDeleteActionFocus}
                        onMouseDown={preserveDeleteActionFocus}
                        onClick={handleDeleteClick}
                        className="inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] text-red-300 transition-colors hover:bg-red-500/10"
                        data-chat-interactive="true"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showImageViewer && (
              <ImageLightbox
                src={message.content}
                alt="Shared image"
                onClose={() => setShowImageViewer(false)}
              />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showVideoViewer && (
              <VideoViewer
                src={videoViewerSrc || message.content}
                message={message}
                canDelete={isOwn && !isPending && !isFailed}
                onDelete={() => onDelete?.(message.id)}
                onClose={() => { setShowVideoViewer(false); setVideoViewerSrc(null); }}
              />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showFullEmojiPicker && (
              <motion.div
                ref={fullEmojiPickerRef}
                initial={{ opacity: 0, y: 10, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 360, damping: 28, mass: 0.7 }}
                className={`fixed bottom-24 left-3 right-3 z-30 md:absolute md:bottom-full md:left-auto md:right-auto ${isOwn ? 'md:right-0' : 'md:left-0'} md:mb-[4.75rem]`}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={stopPickerEvent}
                onMouseDown={stopPickerEvent}
                onTouchStart={stopPickerEvent}
                onTouchMove={stopPickerEvent}
                data-chat-interactive="true"
              >
                <div className="relative mx-auto w-fit max-w-full origin-bottom scale-[0.88] md:mx-0 md:scale-[0.9]">
                  <button
                    onClick={() => setShowFullEmojiPicker(false)}
                    className="absolute -top-2 -right-2 z-10 rounded-full bg-zinc-700 p-1"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                  <Suspense fallback={<div className="h-[300px] w-[320px] max-w-[86vw] rounded-xl bg-surface-2 ui-skeleton" />}>
                    <LazyEmojiPicker
                      onEmojiSelect={(e) => handleReact(e?.native)}
                      theme="dark"
                      previewPosition="none"
                      skinTonePosition="none"
                      perLine={8}
                      emojiSize={18}
                      maxFrequentRows={1}
                    />
                  </Suspense>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <ReactionsDisplay reactions={message.reactions} onReact={handleReact} currentUserId={currentUserId} isOwn={isOwn} />

        {showMeta && (
          <span className={`mt-0.5 inline-flex items-center gap-1 text-[11px] leading-none text-zinc-500 ${isOwn ? 'self-end justify-end text-right' : 'self-start'}`}>
            {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            {timeLabel || formatMessageTime(message.created_at)}
            {message.edited && <span className="ml-0.5 text-zinc-600">edited</span>}
            {isOwn && (
              <>
                <span aria-hidden="true" className="text-zinc-600">&bull;</span>
                <span className={`text-[10px] ${isFailed ? 'text-red-400' : 'text-zinc-500/85'}`}>
                  {isFailed ? 'Tap to retry' : (statusLabel || getMessageStatusLabel(message))}
                </span>
              </>
            )}
            {!isPending && <MessageStatus status={message.status} isOwn={isOwn} />}
          </span>
        )}
      </div>
    </div>
  );
});

// Channel Chat Area
const ChannelChatArea = ({
  channel,
  onBack,
  onChannelDeleted,
  refreshSignal = 0,
  onConversationMessage,
  onConversationMessageEdited,
  onConversationMessageDeleted,
}) => {
  const { user } = useAuth();
  const { error: toast } = useToast();
  const { 
    connected, joinChannel, leaveChannel, markMessagesSeen,
    onNewMessage, onMessageEdited, onMessageDeleted, onReactionUpdated, onMessagesSeen, 
    onChannelDeleted: onChannelDeletedSocket, emitTypingStart, emitTypingStop, typingUsers,
    onUserProfileUpdated,
  } = useSocket();
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [channelInfo, setChannelInfo] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  // Latest-value refs so the memoized bubble handlers stay stable (stable deps)
  // while always reading the CURRENT messages/editing/newMessage values.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const editingMessageRef = useRef(editingMessage);
  editingMessageRef.current = editingMessage;
  const newMessageRef = useRef(newMessage);
  newMessageRef.current = newMessage;
  const textareaResizeRafRef = useRef(null);
  const lastSeenEmitRef = useRef(0);
  const lastSeenTimeoutRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const composerTextareaRef = useRef(null);
  const composerEmojiButtonRef = useRef(null);
  const composerEmojiPickerRef = useRef(null);
  const composerFocusedRef = useRef(false);
  const backgroundPointerRef = useRef(null);
  const editDraftBeforeRef = useRef('');
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const cancelRecordingRef = useRef(false);
  const recordingStreamRef = useRef(null);
  const voiceDraftsRef = useRef({});
  const imageDraftsRef = useRef({});
  const videoDraftsRef = useRef({});
  const videoFileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingEmitRef = useRef(0);
  const typingActiveRef = useRef(false);
  const recordingIntervalRef = useRef(null);
  const currentChannelRef = useRef(null);
  const messageCacheRef = useRef({});
  const prependScrollRestoreRef = useRef(null);
  const sendQueueRef = useRef([]);
  const isFlushingSendQueueRef = useRef(false);
  const isLoadingOlderRef = useRef(false);
  const lastOlderCursorRef = useRef(null);
  const latestReactionRequestRef = useRef({});
  const completedReactionRequestIdsRef = useRef(new Set());
  const reactionRequestChainsRef = useRef({});
  const deletedMessageIdsRef = useRef(new Set());
  // FLIP stack-insertion animation state (presentational only, never persisted).
  const flipEntryRefs = useRef(new Map());      // stable key -> inner .msg-entry element
  const flipTypingEntryRef = useRef(null);      // inner typing-entry element
  const composerContainerRef = useRef(null);    // origin for the "rise from composer"

  // Load draft on channel change
  useEffect(() => {
    if (channel?.id !== currentChannelRef.current) {
      setMessages([]);
      setChannelInfo(null);
      setReplyTo(null);
      setEditingMessage(null);
      editDraftBeforeRef.current = '';
      setShowMenu(false);
      setShowEmojiPicker(false);
      setVoiceError('');
      setIsMessagesLoading(true);
      setIsLoadingOlder(false);
      setHasMoreMessages(false);
      prependScrollRestoreRef.current = null;
      isLoadingOlderRef.current = false;
      lastOlderCursorRef.current = null;
      latestReactionRequestRef.current = {};
      completedReactionRequestIdsRef.current = new Set();
      reactionRequestChainsRef.current = {};
      deletedMessageIdsRef.current = new Set();
      flipEntryRefs.current = new Map();
      flipTypingEntryRef.current = null;
      try { cancelAllChatAnimations(); } catch { /* noop */ }
      // Conversation switch: abort in-flight media uploads and free draft blob URLs
      // so switching away mid-upload doesn't leak object URLs or orphan requests.
      Object.values(videoDraftsRef.current).forEach((draft) => {
        if (draft?.abort) { try { draft.abort.abort(); } catch {} }
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      videoDraftsRef.current = {};
      Object.values(imageDraftsRef.current).forEach((draft) => {
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      imageDraftsRef.current = {};
      Object.values(voiceDraftsRef.current).forEach((draft) => {
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      voiceDraftsRef.current = {};
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      typingActiveRef.current = false;
      lastTypingEmitRef.current = 0;
      currentChannelRef.current = channel?.id;
      
      // Load draft
      if (channel?.id) {
        const draft = getDraft('channel', channel.id);
        setNewMessage(draft);
      }
    }
  }, [channel?.id, refreshSignal]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (lastSeenTimeoutRef.current) clearTimeout(lastSeenTimeoutRef.current);
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      if (mediaRecorderRef.current?.state && mediaRecorderRef.current.state !== 'inactive') {
        cancelRecordingRef.current = true;
        try { mediaRecorderRef.current.stop(); } catch (_) {}
      }
      Object.values(voiceDraftsRef.current).forEach((draft) => {
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      voiceDraftsRef.current = {};
      Object.values(imageDraftsRef.current).forEach((draft) => {
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      imageDraftsRef.current = {};
      Object.values(videoDraftsRef.current).forEach((draft) => {
        if (draft?.abort) { try { draft.abort.abort(); } catch {} }
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      videoDraftsRef.current = {};
    };
  }, []);

  const mergeMessages = useCallback((oldMessages, newMessages) => {
    return mergeMessagesByIdentity(oldMessages, newMessages);
  }, []);

  const withoutLocallyDeletedMessages = useCallback((nextMessages) => {
    return filterMessagesByDeletedIds(nextMessages, deletedMessageIdsRef);
  }, []);

  const applyServerReactionUpdate = useCallback((data) => {
    if (!data?.message_id) return;

    const requestKey = getReactionRequestKey(data.message_id, data.emoji);
    const latestRequestId = latestReactionRequestRef.current[requestKey];
    const clientReactionId = data.client_reaction_id;
    if (data.user_id === user?.id && clientReactionId && completedReactionRequestIdsRef.current.has(clientReactionId)) {
      return;
    }

    if (data.user_id === user?.id && latestRequestId) {
      if (clientReactionId !== latestRequestId) {
        if (clientReactionId) completedReactionRequestIdsRef.current.add(clientReactionId);
        return;
      }
      delete latestReactionRequestRef.current[requestKey];
    }
    if (data.user_id === user?.id && clientReactionId) {
      completedReactionRequestIdsRef.current.add(clientReactionId);
    }

    setMessages((prev) => prev.map((message) => (
      message.id === data.message_id
        ? { ...message, reactions: normalizeReactions(data.reactions) }
        : message
    )));
  }, [user?.id]);

  // Save draft on message change (debounced: localStorage writes are synchronous
  // and the effect fires per keystroke)
  useEffect(() => {
    if (channel?.id && !editingMessage) {
      scheduleDraftSave('channel', channel.id, newMessage);
    }
  }, [newMessage, channel?.id, editingMessage]);

  // Autosize the composer. Coalesced to one forced reflow per animation frame:
  // multiple keystrokes within a frame measure exactly once.
  useEffect(() => {
    const textarea = composerTextareaRef.current;
    if (!textarea) return;

    if (textareaResizeRafRef.current) {
      cancelAnimationFrame(textareaResizeRafRef.current);
      textareaResizeRafRef.current = null;
    }
    textareaResizeRafRef.current = requestAnimationFrame(() => {
      textareaResizeRafRef.current = null;
      textarea.style.height = '0px';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 128)}px`;
    });
    return () => {
      if (textareaResizeRafRef.current) {
        cancelAnimationFrame(textareaResizeRafRef.current);
        textareaResizeRafRef.current = null;
      }
    };
  }, [newMessage]);

  useEffect(() => {
    if (!showEmojiPicker) return;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (composerEmojiPickerRef.current?.contains(target) || composerEmojiButtonRef.current?.contains(target)) return;
      setShowEmojiPicker(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowEmojiPicker(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showEmojiPicker]);

  const handleComposerFocus = useCallback(() => {
    composerFocusedRef.current = true;
    scheduleWhisperViewportSync();
  }, []);

  const handleComposerBlur = useCallback(() => {
    composerFocusedRef.current = false;
    scheduleWhisperViewportSync();
  }, []);

  const blurComposerFromBackground = useCallback(() => {
    const textarea = composerTextareaRef.current;
    const wasFocused = composerFocusedRef.current || (typeof document !== 'undefined' && document.activeElement === textarea);
    if (wasFocused) textarea?.blur();
    composerFocusedRef.current = false;
    setShowEmojiPicker(false);
    if (wasFocused) scheduleWhisperViewportSync();
  }, []);

  const handleMessagesBackgroundPointerDown = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const target = event.target;
    if (target?.closest?.('[data-chat-message="true"], [data-chat-interactive="true"]')) {
      backgroundPointerRef.current = null;
      return;
    }
    backgroundPointerRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      shouldBlur: true,
    };
  }, []);

  const handleMessagesBackgroundPointerMove = useCallback((event) => {
    const pointer = backgroundPointerRef.current;
    if (!pointer || pointer.pointerId !== event.pointerId) return;
    if (Math.abs(event.clientX - pointer.x) > 8 || Math.abs(event.clientY - pointer.y) > 8) {
      pointer.shouldBlur = false;
    }
  }, []);

  const handleMessagesBackgroundPointerUp = useCallback((event) => {
    const pointer = backgroundPointerRef.current;
    backgroundPointerRef.current = null;
    if (!pointer || pointer.pointerId !== event.pointerId || !pointer.shouldBlur) return;
    blurComposerFromBackground();
  }, [blurComposerFromBackground]);

  const handleMessagesBackgroundPointerCancel = useCallback(() => {
    backgroundPointerRef.current = null;
  }, []);

  // Load channel info and newest messages
  useEffect(() => {
    if (!channel?.id) return;

    currentChannelRef.current = channel.id;
    let cancelled = false;
    const cacheKey = `whisper_channel_messages_${channel.id}`;

    const loadData = async () => {
      try {
        const rawCachedMessages = messageCacheRef.current[channel.id] || getMessageCache(cacheKey);
        const cachedMessages = sortMessagesForDisplay(withoutLocallyDeletedMessages(
          normalizeMessagesForState(sanitizeMessagesForCache(rawCachedMessages))
        ));

        setChannelInfo(channel);
        setIsMessagesLoading(cachedMessages.length === 0);

        // Only seed from cache if we have no live messages yet - never overwrite pending
        setMessages((prev) => {
          if (prev.length > 0) return withoutLocallyDeletedMessages(prev);
          return cachedMessages;
        });

        const requestStartedAt = Date.now();
        const messagesRes = await axios.get(`${BACKEND_URL}/api/channels/${channel.id}/messages`, {
          withCredentials: true,
          params: { limit: MESSAGE_PAGE_SIZE },
        });
        const { messages: rawLatestMessages, hasMore } = normalizeMessagesResponse(messagesRes.data, MESSAGE_PAGE_SIZE);
        const latestMessages = sortMessagesForDisplay(withoutLocallyDeletedMessages(rawLatestMessages));

        if (cancelled || currentChannelRef.current !== channel.id) return;

        setHasMoreMessages(hasMore);
        setMessages((prev) => {
          const merged = withoutLocallyDeletedMessages(mergeServerMessagesPreservingLocal(
            prev,
            latestMessages,
            mergeMessages,
            { requestStartedAt }
          ));
          const clean = sortMessagesForDisplay(sanitizeMessagesForCache(merged));
          messageCacheRef.current[channel.id] = clean;
          saveMessageCache(cacheKey, clean);
          return merged;
        });

        const latest = latestMessages[latestMessages.length - 1];
        if (latest) {
          await axios.post(`${BACKEND_URL}/api/channels/${channel.id}/mark-read`, { message_id: latest.id }, { withCredentials: true });
        }
      } catch (error) {
        console.error('Load data error:', error);
      } finally {
        if (!cancelled && currentChannelRef.current === channel.id) {
          setIsMessagesLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [channel?.id, refreshSignal, mergeMessages, withoutLocallyDeletedMessages]);

  useEffect(() => {
    if (!channel?.id || !connected) return;

    joinChannel(channel.id);
    markMessagesSeen(channel.id);

    return () => {
      leaveChannel(channel.id);
    };
  }, [channel?.id, connected, joinChannel, leaveChannel, markMessagesSeen]);

  useEffect(() => {
    if (!channel?.id) return;

    const cleanMessages = sortMessagesForDisplay(sanitizeMessagesForCache(withoutLocallyDeletedMessages(messages)));
    messageCacheRef.current[channel.id] = cleanMessages;
    // Debounced persist: this effect runs on EVERY messages change (typing,
    // receipts, reactions), so write localStorage at most once per 800ms of
    // silence instead of once per event.
    const cacheKey = `whisper_channel_messages_${channel.id}`;
    const timer = setTimeout(() => {
      saveMessageCache(cacheKey, cleanMessages);
    }, 800);
    return () => clearTimeout(timer);
  }, [channel?.id, messages, withoutLocallyDeletedMessages]);

  const loadOlderMessages = useCallback(async () => {
    if (!channel?.id || isLoadingOlderRef.current || !hasMoreMessages) return;

    const beforeCursor = getOldestMessageCursor(messages);
    const beforeCursorKey = getOlderCursorKey(beforeCursor);
    if (!beforeCursorKey) {
      setHasMoreMessages(false);
      return;
    }
    if (lastOlderCursorRef.current === beforeCursorKey) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    prependScrollRestoreRef.current = {
      previousHeight: container.scrollHeight,
      previousTop: container.scrollTop,
    };

    isLoadingOlderRef.current = true;
    lastOlderCursorRef.current = beforeCursorKey;
    setIsLoadingOlder(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/channels/${channel.id}/messages`, {
        withCredentials: true,
        params: {
          limit: MESSAGE_PAGE_SIZE,
          before: beforeCursor.before,
          ...(beforeCursor.before_id ? { before_id: beforeCursor.before_id } : {}),
        },
      });

      const { messages: rawOlderMessages, hasMore } = normalizeMessagesResponse(response.data, MESSAGE_PAGE_SIZE);
      const olderMessages = sortMessagesForDisplay(withoutLocallyDeletedMessages(rawOlderMessages));

      if (currentChannelRef.current !== channel.id) return;

      setHasMoreMessages(hasMore);
      if (rawOlderMessages.length === 0) {
        setHasMoreMessages(false);
        return;
      }
      if (olderMessages.length === 0) return;

      setMessages((prev) => withoutLocallyDeletedMessages(mergeMessages(prev, olderMessages)));
    } catch (error) {
      console.error('Load older channel messages error:', error);
      prependScrollRestoreRef.current = null;
      lastOlderCursorRef.current = null;
    } finally {
      if (currentChannelRef.current === channel.id) {
        isLoadingOlderRef.current = false;
        setIsLoadingOlder(false);
      }
    }
  }, [channel?.id, hasMoreMessages, messages, mergeMessages, withoutLocallyDeletedMessages]);

  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || isMessagesLoading || isLoadingOlder || !hasMoreMessages) return;
    if (container.scrollTop <= OLDER_MESSAGES_SCROLL_THRESHOLD) {
      loadOlderMessages();
    }
  }, [hasMoreMessages, isLoadingOlder, isMessagesLoading, loadOlderMessages]);

  // Socket event listeners
  useEffect(() => {
    if (!connected || !channel?.id) return;

    const unsubNewMessage = onNewMessage((message) => {
      if (message.channel_id === channel.id) {
        if (message.sender_id === user?.id) return;
        if (filterMessagesByDeletedIds([message], deletedMessageIdsRef).length === 0) return;
        const stamped = { ...message, _realtime_received_at: Date.now() };
        // Explicit entry eligibility: a genuinely-new incoming socket message is
        // being accepted into the OPEN conversation right here (own echoes and
        // locally-deleted messages already returned above). Client-only metadata.
        registerEntryAnimation(getStableMessageKey(stamped), 'incoming-new');
        setMessages(prev => withoutLocallyDeletedMessages(mergeMessages(prev, [stamped])));
        onConversationMessage?.(message, { clearUnread: true });
        // Mark as read immediately since we're viewing, throttled to at most one
        // seen-update (server DB write) per second — a burst of incoming
        // messages must not fire one per message. Trailing edge guarantees the
        // latest message of a burst is still marked seen.
        const seenNow = Date.now();
        const sinceLastSeen = seenNow - lastSeenEmitRef.current;
        if (sinceLastSeen >= 1000) {
          lastSeenEmitRef.current = seenNow;
          markMessagesSeen(channel.id);
        } else if (!lastSeenTimeoutRef.current) {
          lastSeenTimeoutRef.current = setTimeout(() => {
            lastSeenTimeoutRef.current = null;
            lastSeenEmitRef.current = Date.now();
            markMessagesSeen(channel.id);
          }, 1000 - sinceLastSeen);
        }
      }
    });

    const unsubEdited = onMessageEdited((data) => {
      if (data.channel_id === channel.id) {
        onConversationMessageEdited?.(data);
        setMessages(prev => prev.map(m => 
          m.id === data.id ? { ...m, content: data.content, edited: true, edited_at: data.edited_at } : m
        ));
      }
    });

    const unsubDeleted = onMessageDeleted?.((data) => {
      if (data.channel_id === channel.id) {
        onConversationMessageDeleted?.({ ...data, reconcile: false });
        rememberDeletedMessage(deletedMessageIdsRef, data.message_id);
        setMessages(prev => withoutLocallyDeletedMessages(prev.filter(m => !getMessageIdentityKeys(m).includes(String(data.message_id)))));
        removeMessageFromCache(`whisper_channel_messages_${data.channel_id}`, data.message_id);
      }
    });

    const unsubReaction = onReactionUpdated?.((data) => {
      if (data.channel_id === channel.id) {
        applyServerReactionUpdate(data);
      }
    });

    const unsubSeen = onMessagesSeen?.((data) => {
      if (data.channel_id === channel.id && data.user_id !== user?.id) {
        // Update message statuses
        setMessages(prev => prev.map(m => {
          // Never mark a not-yet-confirmed (optimistic/pending), failed, or
          // temp-id message as seen — only server-confirmed own messages.
          if (m.sender_id === user?.id && !m.pending && !m.failed && m.status !== 'failed' && !isTempMessageId(m.id)) {
            const newSeenCount = (m.seen_count || 0) + 1;
            const memberCount = channelInfo?.members?.length || 2;
            return {
              ...m,
              seen_count: newSeenCount,
              status: newSeenCount >= memberCount - 1 ? 'seen' : m.status
            };
          }
          return m;
        }));
      }
    });

    const unsubChannelDeleted = onChannelDeletedSocket?.((data) => {
      if (data.channel_id === channel.id) {
        onChannelDeleted?.(channel.id);
      }
    });

    const unsubProfileUpdated = onUserProfileUpdated?.((data) => {
      setMessages((prev) => applyProfileUpdateToMessages(prev, data));
      setChannelInfo((prev) => (
        prev?.members
          ? { ...prev, members: applyProfileUpdateToMembers(prev.members, data) }
          : prev
      ));
    });

    return () => {
      unsubNewMessage();
      unsubEdited();
      unsubDeleted?.();
      unsubReaction?.();
      unsubSeen?.();
      unsubChannelDeleted?.();
      unsubProfileUpdated?.();
    };
  }, [channel?.id, onNewMessage, onMessageEdited, onMessageDeleted, onReactionUpdated, onMessagesSeen, onChannelDeletedSocket, onUserProfileUpdated, connected, user?.id, channelInfo?.members?.length, markMessagesSeen, onChannelDeleted, mergeMessages, withoutLocallyDeletedMessages, applyServerReactionUpdate, onConversationMessage, onConversationMessageEdited, onConversationMessageDeleted]);

  // Smart scroll: force on first load, respect position on subsequent updates
  const initialScrollDoneRef = useRef(false);
  useEffect(() => {
    if (channel?.id) initialScrollDoneRef.current = false;
  }, [channel?.id]);

  useEffect(() => {
    if (messages.length === 0) return;
    const el = messagesContainerRef.current;
    const pendingRestore = prependScrollRestoreRef.current;

    if (pendingRestore && el) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight - pendingRestore.previousHeight + pendingRestore.previousTop;
        prependScrollRestoreRef.current = null;
      });
      return;
    }

    if (!initialScrollDoneRef.current) {
      // Force instant scroll on initial load
      if (el) el.scrollTop = el.scrollHeight;
      initialScrollDoneRef.current = true;
    } else {
      // Avoid smooth scrolling during keyboard animation; it can fight visualViewport resizing.
      if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 150) {
        if (composerFocusedRef.current) {
          el.scrollTop = el.scrollHeight;
        } else {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [messages]);

  const handleTyping = useCallback((e) => {
    const value = e.target.value;
    setNewMessage(value);
    if (!channel?.id || !user?.username) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (!value.trim()) {
      if (typingActiveRef.current) emitTypingStop(channel.id);
      typingActiveRef.current = false;
      lastTypingEmitRef.current = 0;
      return;
    }

    const now = Date.now();
    if (!typingActiveRef.current || now - lastTypingEmitRef.current >= TYPING_START_EMIT_INTERVAL_MS) {
      emitTypingStart(channel.id, user.username);
      typingActiveRef.current = true;
      lastTypingEmitRef.current = now;
    }
    typingTimeoutRef.current = setTimeout(() => {
      emitTypingStop(channel.id);
      typingActiveRef.current = false;
      lastTypingEmitRef.current = 0;
      typingTimeoutRef.current = null;
    }, 2000);
  }, [channel?.id, user?.username, emitTypingStart, emitTypingStop]);

  const flushSendQueue = useCallback(async () => {
    if (isFlushingSendQueueRef.current) return;

    isFlushingSendQueueRef.current = true;

    try {
      while (sendQueueRef.current.length > 0) {
        const item = sendQueueRef.current[0];

        try {
          const response = await axios.post(item.url, item.payload, { withCredentials: true });
          const confirmedMessage = normalizeMessageForState({
            ...response.data,
            client_temp_id: response.data.client_temp_id || item.tempId,
            pending: false,
            failed: false,
            localEcho: true,
          });

          if (currentChannelRef.current === item.channelId) {
            setMessages((prev) => mergeMessages(prev, [confirmedMessage]));
            onConversationMessage?.(confirmedMessage, { clearUnread: true });
            if (item.payload?.message_type === 'image') {
              delete imageDraftsRef.current[item.tempId];
            }
          }
        } catch (error) {
          console.error('Send message error:', error);

          if (currentChannelRef.current === item.channelId) {
            setMessages((prev) => prev.map((message) => (
              message.tempId === item.tempId || message.id === item.tempId
                ? { ...message, pending: false, failed: true, status: 'failed' }
                : message
            )));
          }
        } finally {
          sendQueueRef.current.shift();
        }
      }
    } finally {
    isFlushingSendQueueRef.current = false;
    }
  }, [mergeMessages, onConversationMessage]);

  const markVoiceMessageFailed = useCallback((tempId, patch = {}) => {
    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            pending: false,
            failed: true,
            status: 'failed',
            voice_uploading: false,
            ...patch,
          }
        : message
    )));
  }, []);

  const markImageMessageFailed = useCallback((tempId, patch = {}) => {
    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            pending: false,
            failed: true,
            status: 'failed',
            image_uploading: false,
            ...patch,
          }
        : message
    )));
  }, []);

  const removeLocalImageMessage = useCallback((message) => {
    const tempId = message?.client_temp_id || message?.tempId || message?.id;
    if (!tempId) return;
    const draft = imageDraftsRef.current[tempId];
    if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
    delete imageDraftsRef.current[tempId];
    const videoDraft = videoDraftsRef.current[tempId];
    if (videoDraft?.abort) { try { videoDraft.abort.abort(); } catch {} }
    if (videoDraft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(videoDraft.localUrl);
    delete videoDraftsRef.current[tempId];
    setMessages((prev) => prev.filter((item) => !isSameMessageIdentity(item, tempId)));
  }, []);

  const enqueueUploadedImageMessage = useCallback((tempId, uploadedUrl, replyToId) => {
    if (!uploadedUrl || !channel?.id || !user?.id) return;
    if (sendQueueRef.current.some((item) => item.tempId === tempId)) return;

    const draft = imageDraftsRef.current[tempId];
    if (draft) {
      draft.uploadedUrl = uploadedUrl;
      if (draft.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      draft.localUrl = null;
    }

    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            content: uploadedUrl,
            image_upload_url: uploadedUrl,
            image_uploading: false,
            image_retry_unavailable: false,
            pending: true,
            failed: false,
            status: 'sent',
          }
        : message
    )));

    sendQueueRef.current.push({
      tempId,
      channelId: channel.id,
      url: `${BACKEND_URL}/api/channels/${channel.id}/messages`,
      payload: { content: uploadedUrl, message_type: 'image', reply_to: replyToId, client_temp_id: tempId },
    });

    flushSendQueue();
  }, [channel?.id, flushSendQueue, user?.id]);

  const uploadAndQueueImageMessage = useCallback(async (tempId, imageFile, replyToId) => {
    if (!tempId || !imageFile) return;

    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? { ...message, pending: true, failed: false, status: 'sent', image_uploading: true, image_retry_unavailable: false }
        : message
    )));

    try {
      const imageData = await prepareImageForUpload(imageFile);
      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, imageData, {
        withCredentials: true,
        headers: { 'Content-Type': imageData.type || imageFile.type || 'image/jpeg' }
      });
      enqueueUploadedImageMessage(tempId, response.data.url, replyToId);
    } catch (error) {
      console.error('Image upload error:', error);
      markImageMessageFailed(tempId);
    }
  }, [enqueueUploadedImageMessage, markImageMessageFailed]);

  const createPendingImageMessage = useCallback((imageFile) => {
    if (!imageFile || !channel?.id || !user?.id) return;

    const activeReplyTo = replyTo?.id;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const localUrl = URL.createObjectURL(imageFile);
    imageDraftsRef.current[tempId] = { file: imageFile, localUrl, replyTo: activeReplyTo };

    const tempMessage = {
      id: tempId,
      tempId,
      channel_id: channel.id,
      sender_id: user.id,
      sender_username: user.username,
      sender_profile_picture_thumb: user.profile_picture_thumb,
      content: localUrl,
      message_type: 'image',
      reply_to: activeReplyTo,
      client_temp_id: tempId,
      created_at: new Date().toISOString(),
      reactions: {},
      status: 'sent',
      pending: true,
      localEcho: true,
      image_uploading: true,
    };

    registerEntryAnimation(getStableMessageKey(tempMessage), 'outgoing-new');
    setMessages((prev) => [...prev, tempMessage]);
    onConversationMessage?.(tempMessage, { clearUnread: true });
    setReplyTo(null);
    uploadAndQueueImageMessage(tempId, imageFile, activeReplyTo);
  }, [channel?.id, onConversationMessage, replyTo?.id, uploadAndQueueImageMessage, user?.id, user?.profile_picture_thumb, user?.username]);

  const enqueueUploadedVoiceMessage = useCallback((tempId, uploadedUrl, replyToId) => {
    if (!uploadedUrl || !channel?.id || !user?.id) return;
    if (sendQueueRef.current.some((item) => item.tempId === tempId)) return;

    // Free the local recording blob URL now that the server URL is available.
    const vDraft = voiceDraftsRef.current[tempId];
    if (vDraft?.localUrl?.startsWith('blob:')) { URL.revokeObjectURL(vDraft.localUrl); vDraft.localUrl = null; }

    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            voice_upload_url: uploadedUrl,
            voice_uploading: false,
            voice_retry_unavailable: false,
            pending: true,
            failed: false,
            status: 'sent',
          }
        : message
    )));

    sendQueueRef.current.push({
      tempId,
      channelId: channel.id,
      url: `${BACKEND_URL}/api/channels/${channel.id}/messages`,
      payload: { content: uploadedUrl, message_type: 'voice', reply_to: replyToId, client_temp_id: tempId },
    });

    flushSendQueue();
  }, [channel?.id, flushSendQueue, user?.id]);

  const uploadAndQueueVoiceMessage = useCallback(async (tempId, audioBlob, blobType, replyToId) => {
    if (!tempId || !audioBlob) return;

    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? { ...message, pending: true, failed: false, status: 'sent', voice_uploading: true, voice_retry_unavailable: false }
        : message
    )));

    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload/voice`, audioBlob, {
        withCredentials: true,
        headers: { 'Content-Type': blobType || audioBlob.type || 'audio/webm' },
      });
      const uploadedUrl = response.data.url;
      if (voiceDraftsRef.current[tempId]) {
        voiceDraftsRef.current[tempId].uploadedUrl = uploadedUrl;
      }
      enqueueUploadedVoiceMessage(tempId, uploadedUrl, replyToId);
    } catch (error) {
      console.error('Voice upload error:', error);
      setVoiceError(getVoiceUploadErrorMessage(error));
      markVoiceMessageFailed(tempId);
    }
  }, [enqueueUploadedVoiceMessage, markVoiceMessageFailed]);

  const createPendingVoiceMessage = useCallback((audioBlob, blobType) => {
    if (!audioBlob || !channel?.id || !user?.id) return;

    const activeReplyTo = replyTo?.id;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const localUrl = URL.createObjectURL(audioBlob);
    voiceDraftsRef.current[tempId] = { blob: audioBlob, blobType, localUrl, replyTo: activeReplyTo };

    const tempMessage = {
      id: tempId,
      tempId,
      channel_id: channel.id,
      sender_id: user.id,
      sender_username: user.username,
      sender_profile_picture_thumb: user.profile_picture_thumb,
      content: localUrl,
      message_type: 'voice',
      reply_to: activeReplyTo,
      client_temp_id: tempId,
      created_at: new Date().toISOString(),
      reactions: {},
      status: 'sent',
      pending: true,
      localEcho: true,
      voice_uploading: true,
    };

    registerEntryAnimation(getStableMessageKey(tempMessage), 'outgoing-new');
    setMessages((prev) => [...prev, tempMessage]);
    onConversationMessage?.(tempMessage, { clearUnread: true });
    setReplyTo(null);
    uploadAndQueueVoiceMessage(tempId, audioBlob, blobType, activeReplyTo);
  }, [channel?.id, onConversationMessage, replyTo?.id, uploadAndQueueVoiceMessage, user?.id, user?.profile_picture_thumb, user?.username]);

  // ── Video message (mirrors the voice flow: raw blob upload, no compression) ──
  const markVideoMessageFailed = useCallback((tempId, patch = {}) => {
    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? { ...message, pending: false, failed: true, status: 'failed', video_uploading: false, ...patch }
        : message
    )));
  }, []);

  const enqueueUploadedVideoMessage = useCallback((tempId, uploadedUrl, replyToId) => {
    if (!uploadedUrl || !channel?.id || !user?.id) return;
    if (sendQueueRef.current.some((item) => item.tempId === tempId)) return;

    const draft = videoDraftsRef.current[tempId];
    if (draft) {
      draft.uploadedUrl = uploadedUrl;
      if (draft.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      draft.localUrl = null;
    }

    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            content: uploadedUrl,
            video_upload_url: uploadedUrl,
            video_uploading: false,
            video_retry_unavailable: false,
            pending: true,
            failed: false,
            status: 'sent',
          }
        : message
    )));

    sendQueueRef.current.push({
      tempId,
      channelId: channel.id,
      url: `${BACKEND_URL}/api/channels/${channel.id}/messages`,
      payload: {
        content: uploadedUrl,
        message_type: 'video',
        reply_to: replyToId,
        client_temp_id: tempId,
        file_size: draft?.file?.size ?? null,
        original_filename: draft?.file?.name ?? null,
        mime_type: draft?.file?.type ?? null,
        duration: draft?.duration ?? null,
        width: draft?.width ?? null,
        height: draft?.height ?? null,
      },
    });

    flushSendQueue();
  }, [channel?.id, flushSendQueue, user?.id]);

  const uploadAndQueueVideoMessage = useCallback(async (tempId, videoFile, replyToId) => {
    if (!tempId || !videoFile) return;

    // Guard against a double-tap / concurrent retry uploading the same item twice.
    const draft = videoDraftsRef.current[tempId];
    if (draft?.uploading) return;
    const controller = new AbortController();
    if (draft) { draft.uploading = true; draft.abort = controller; }
    // Per-upload progress state (closure, see onUploadProgress throttle below).
    const progressState = { pct: -1, at: 0 };

    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? { ...message, pending: true, failed: false, status: 'sent', video_uploading: true, video_retry_unavailable: false, upload_progress: 0 }
        : message
    )));

    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload/video`, videoFile, {
        withCredentials: true,
        headers: { 'Content-Type': videoFile.type || 'video/mp4' },
        signal: controller.signal,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          // Cap at 99% until the server confirms; the enqueue step marks it done.
          const pct = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
          // Throttle: onUploadProgress fires per network chunk; without this,
          // every chunk triggered a full message-list state update + re-render.
          // Update only when the percent visibly changes, and at most every
          // 150ms mid-upload.
          const now = Date.now();
          if (pct === progressState.pct) return;
          if (pct < 99 && now - progressState.at < 150) return;
          progressState.pct = pct;
          progressState.at = now;
          setMessages((prev) => prev.map((message) => (
            message.tempId === tempId || message.id === tempId || message.client_temp_id === tempId
              ? { ...message, upload_progress: pct }
              : message
          )));
        },
      });
      const uploadedUrl = response.data.url;
      if (videoDraftsRef.current[tempId]) {
        videoDraftsRef.current[tempId].uploadedUrl = uploadedUrl;
        videoDraftsRef.current[tempId].uploading = false;
        videoDraftsRef.current[tempId].abort = null;
      }
      enqueueUploadedVideoMessage(tempId, uploadedUrl, replyToId);
    } catch (error) {
      if (videoDraftsRef.current[tempId]) {
        videoDraftsRef.current[tempId].uploading = false;
        videoDraftsRef.current[tempId].abort = null;
      }
      // A user-initiated cancel/abort is not a failure; the item was already removed.
      if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Video upload error:', error);
      markVideoMessageFailed(tempId);
    }
  }, [enqueueUploadedVideoMessage, markVideoMessageFailed]);

  const createPendingVideoMessage = useCallback((videoFile) => {
    if (!videoFile || !channel?.id || !user?.id) return;

    const activeReplyTo = replyTo?.id;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const localUrl = URL.createObjectURL(videoFile);
    videoDraftsRef.current[tempId] = { file: videoFile, localUrl, replyTo: activeReplyTo };

    const tempMessage = {
      id: tempId,
      tempId,
      channel_id: channel.id,
      sender_id: user.id,
      sender_username: user.username,
      sender_profile_picture_thumb: user.profile_picture_thumb,
      content: localUrl,
      message_type: 'video',
      reply_to: activeReplyTo,
      client_temp_id: tempId,
      created_at: new Date().toISOString(),
      reactions: {},
      status: 'sent',
      pending: true,
      localEcho: true,
      video_uploading: true,
      file_size: videoFile.size,
      original_filename: videoFile.name || null,
      mime_type: videoFile.type || null,
    };

    registerEntryAnimation(getStableMessageKey(tempMessage), 'outgoing-new');
    setMessages((prev) => [...prev, tempMessage]);
    onConversationMessage?.(tempMessage, { clearUnread: true });
    setReplyTo(null);
    // Fill in duration/dimensions asynchronously (metadata only).
    readVideoMetadata(videoFile).then((meta) => {
      if (!meta || (meta.duration == null && meta.width == null)) return;
      setMessages((prev) => prev.map((m) => (
        isSameMessageIdentity(m, tempId) ? { ...m, duration: meta.duration, width: meta.width, height: meta.height } : m
      )));
      if (videoDraftsRef.current[tempId]) Object.assign(videoDraftsRef.current[tempId], meta);
    });
    uploadAndQueueVideoMessage(tempId, videoFile, activeReplyTo);
  }, [channel?.id, onConversationMessage, replyTo?.id, uploadAndQueueVideoMessage, user?.id, user?.profile_picture_thumb, user?.username]);

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      // Count limit: at most 2 videos per selection/batch.
      if (files.length > MAX_VIDEOS_PER_BATCH) {
        window.alert(`You can send up to ${MAX_VIDEOS_PER_BATCH} videos at a time.`);
        return;
      }

      const errors = [];
      const accepted = [];
      let batchBytes = 0;
      for (const file of files) {
        if (!isSupportedVideoFile(file)) {
          errors.push(`${file.name || 'This file'} is not a supported video format.`);
          continue;
        }
        if (file.size === 0) {
          errors.push(`${file.name || 'This file'} is empty and cannot be sent.`);
          continue;
        }
        if (file.size > MAX_VIDEO_SIZE_BYTES) {
          errors.push(`${file.name || 'This video'} is ${formatMb(file.size)}. The maximum allowed size is ${formatMb(MAX_VIDEO_SIZE_BYTES)}.`);
          continue;
        }
        if (batchBytes + file.size > MAX_VIDEO_BATCH_BYTES) {
          errors.push(`Total video size exceeds ${formatMb(MAX_VIDEO_BATCH_BYTES)} per batch.`);
          continue;
        }
        batchBytes += file.size;
        accepted.push(file);
      }

      // Only create optimistic messages (and object URLs) for accepted files.
      accepted.forEach((file) => createPendingVideoMessage(file));

      if (errors.length) window.alert(errors.join('\n'));
    } catch (error) {
      console.error('Video upload error:', error);
      window.alert(getVideoUploadErrorMessage(error));
    } finally {
      if (videoFileInputRef.current) videoFileInputRef.current.value = '';
    }
  };

  const sendMessage = (content, type = 'text') => {
    if (!content.trim() && type === 'text') return;
    if (!channel?.id || !user?.id) return;

    const activeReplyTo = replyTo?.id;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const tempMessage = {
      id: tempId,
      tempId,
      channel_id: channel.id,
      sender_id: user.id,
      sender_username: user.username,
      sender_profile_picture_thumb: user.profile_picture_thumb,
      content,
      message_type: type,
      reply_to: activeReplyTo,
      client_temp_id: tempId,
      created_at: new Date().toISOString(),
      reactions: {},
      status: 'sent',
      pending: true,
      localEcho: true,
    };

    registerEntryAnimation(getStableMessageKey(tempMessage), 'outgoing-new');
    setMessages((prev) => [...prev, tempMessage]);
    onConversationMessage?.(tempMessage, { clearUnread: true });
    setNewMessage('');
    clearScheduledDraft('channel', channel.id);
    saveDraft('channel', channel.id, '');
    setReplyTo(null);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (typingActiveRef.current) emitTypingStop(channel.id);
    typingActiveRef.current = false;
    lastTypingEmitRef.current = 0;

    sendQueueRef.current.push({
      tempId,
      channelId: channel.id,
      url: `${BACKEND_URL}/api/channels/${channel.id}/messages`,
      payload: { content, message_type: type, reply_to: activeReplyTo, client_temp_id: tempId },
    });

    flushSendQueue();
    focusComposer(composerTextareaRef);
  };

  const retryFailedMessage = useCallback((message) => {
    if (!message || !channel?.id || message.sender_id !== user?.id) return;
    if (message.pending || (!message.failed && message.status !== 'failed')) return;

    const tempId = message.client_temp_id || message.tempId || message.id;
    if (!tempId || sendQueueRef.current.some((item) => item.tempId === tempId)) return;

    if (message.message_type === 'voice') {
      const draft = voiceDraftsRef.current[tempId];
      const uploadedUrl = message.voice_upload_url
        || draft?.uploadedUrl
        || (typeof message.content === 'string' && message.content.startsWith('/api/uploads/voice/') ? message.content : null);

      if (uploadedUrl) {
        enqueueUploadedVoiceMessage(tempId, uploadedUrl, message.reply_to);
        focusComposer(composerTextareaRef);
        return;
      }

      if (draft?.blob) {
        uploadAndQueueVoiceMessage(tempId, draft.blob, draft.blobType || draft.blob.type, message.reply_to || draft.replyTo);
        focusComposer(composerTextareaRef);
        return;
      }

      markVoiceMessageFailed(tempId, { voice_retry_unavailable: true });
      focusComposer(composerTextareaRef);
      return;
    }

    if (message.message_type === 'image') {
      const draft = imageDraftsRef.current[tempId];
      const uploadedUrl = message.image_upload_url
        || draft?.uploadedUrl
        || (typeof message.content === 'string' && message.content.startsWith('/api/uploads/images/') ? message.content : null);

      if (uploadedUrl) {
        enqueueUploadedImageMessage(tempId, uploadedUrl, message.reply_to);
        focusComposer(composerTextareaRef);
        return;
      }

      if (draft?.file) {
        uploadAndQueueImageMessage(tempId, draft.file, message.reply_to || draft.replyTo);
        focusComposer(composerTextareaRef);
        return;
      }

      markImageMessageFailed(tempId, { image_retry_unavailable: true });
      focusComposer(composerTextareaRef);
      return;
    }

    if (message.message_type === 'video') {
      const draft = videoDraftsRef.current[tempId];
      const uploadedUrl = message.video_upload_url
        || draft?.uploadedUrl
        || (typeof message.content === 'string' && message.content.startsWith('/api/uploads/video/') ? message.content : null);

      if (uploadedUrl) {
        enqueueUploadedVideoMessage(tempId, uploadedUrl, message.reply_to);
        focusComposer(composerTextareaRef);
        return;
      }

      if (draft?.file) {
        uploadAndQueueVideoMessage(tempId, draft.file, message.reply_to || draft.replyTo);
        focusComposer(composerTextareaRef);
        return;
      }

      markVideoMessageFailed(tempId, { video_retry_unavailable: true });
      focusComposer(composerTextareaRef);
      return;
    }

    setMessages((prev) => prev.map((item) => (
      isSameMessageIdentity(item, tempId)
        ? {
            ...item,
            id: item.id || tempId,
            tempId: item.tempId || tempId,
            client_temp_id: item.client_temp_id || tempId,
            pending: true,
            failed: false,
            status: 'sent',
          }
        : item
    )));

    sendQueueRef.current.push({
      tempId,
      channelId: channel.id,
      url: `${BACKEND_URL}/api/channels/${channel.id}/messages`,
      payload: {
        content: message.content || '',
        message_type: message.message_type || 'text',
        reply_to: message.reply_to,
        client_temp_id: tempId,
      },
    });

    flushSendQueue();
    focusComposer(composerTextareaRef);
  }, [channel?.id, enqueueUploadedImageMessage, enqueueUploadedVoiceMessage, enqueueUploadedVideoMessage, flushSendQueue, markImageMessageFailed, markVoiceMessageFailed, markVideoMessageFailed, uploadAndQueueImageMessage, uploadAndQueueVoiceMessage, uploadAndQueueVideoMessage, user?.id]);


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitComposer();
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      const valid = [];
      const rejected = [];
      for (const file of files) {
        const isImage = file.type?.startsWith('image/') || isLikelyHeicImage(file);
        if (isImage) valid.push(file); else rejected.push(file.name || 'file');
      }

      let capped = false;
      let toSend = valid;
      if (valid.length > MAX_MEDIA_SELECTION) {
        toSend = valid.slice(0, MAX_MEDIA_SELECTION);
        capped = true;
      }

      // Send in displayed order; each becomes its own optimistic image message
      // with a unique client_temp_id (the existing FIFO/upload flow handles them).
      toSend.forEach((file) => createPendingImageMessage(file));

      if (rejected.length || capped) {
        const parts = [];
        if (rejected.length) parts.push(`${rejected.length} file(s) skipped (not a supported image).`);
        if (capped) parts.push(`Only the first ${MAX_MEDIA_SELECTION} photos were added.`);
        window.alert(parts.join(' '));
      }
    } catch (error) {
      console.error('Upload error:', error);
      window.alert(getImageUploadErrorMessage(error));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setVoiceError('Voice recording is not supported in this browser.');
      return;
    }

    let stream;
    try {
      setVoiceError('');
      stream = await getVoiceRecordingStream();
      recordingStreamRef.current = stream;
      const { recorder, mimeType } = createVoiceRecorder(stream);

      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      cancelRecordingRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        mediaRecorderRef.current = null;
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
        setRecordingTime(0);

        if (cancelRecordingRef.current) {
          cancelRecordingRef.current = false;
          audioChunksRef.current = [];
          return;
        }

        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        const blobType = recorder.mimeType || mimeType || chunks[0]?.type || 'audio/webm';
        const audioBlob = new Blob(chunks, { type: blobType });
        if (!audioBlob.size) {
          setVoiceError('No voice audio was captured. Please try again.');
          return;
        }

        createPendingVoiceMessage(audioBlob, blobType);
      };

      recorder.start(250);
      setIsRecording(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Recording error:', error);
      stream?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      setVoiceError(getVoiceRecordingErrorMessage(error));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      cancelRecordingRef.current = false;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      cancelRecordingRef.current = true;
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
      setRecordingTime(0);
      setIsRecording(false);
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
  };

  const addEmoji = (emoji) => {
    const selectedEmoji = emoji?.native || '';
    if (!selectedEmoji) return;
    setNewMessage(prev => prev + selectedEmoji);
    requestAnimationFrame(() => composerTextareaRef.current?.focus());
  };

  const handleReply = useCallback((message) => {
    if (editingMessageRef.current) {
      setEditingMessage(null);
      setNewMessage(editDraftBeforeRef.current || '');
    }
    setReplyTo(message);
    focusComposer(composerTextareaRef);
  }, []);

  const handleStartEdit = useCallback((message) => {
    if (!message?.id || isTempMessageId(message.id) || message.message_type !== 'text') return;
    if (!editingMessageRef.current) {
      editDraftBeforeRef.current = newMessageRef.current;
    }
    setReplyTo(null);
    setShowEmojiPicker(false);
    setEditingMessage(message);
    setNewMessage(message.content || '');
    focusComposer(composerTextareaRef);
  }, []);

  const handleDeleteMessage = useCallback(async (messageId) => {
    const deletedMessage = messagesRef.current.find((message) => getMessageIdentityKeys(message).includes(messageId) || message.id === messageId);
    if (!deletedMessage) return;

    const deletedKeys = rememberDeletedMessage(deletedMessageIdsRef, deletedMessage);
    setMessages((prev) => withoutLocallyDeletedMessages(prev.filter((message) => !getMessageIdentityKeys(message).includes(messageId))));
    onConversationMessageDeleted?.({ channel_id: channel.id, message_id: messageId, reconcile: false });
    removeMessageFromCache(`whisper_channel_messages_${channel.id}`, messageId);
    try {
      await axios.delete(`${BACKEND_URL}/api/channels/${channel.id}/messages/${messageId}`, { withCredentials: true });
    } catch (error) {
      const status = error?.response?.status;
      // 404/410 mean the server already has no such message — deletion is
      // effectively done. Keep it removed; do NOT roll back or alert.
      if (status === 404 || status === 410) {
        setMessages((prev) => withoutLocallyDeletedMessages(prev.filter((message) => !getMessageIdentityKeys(message).includes(messageId))));
        removeMessageFromCache(`whisper_channel_messages_${channel.id}`, messageId);
        return;
      }
      // Genuine failure (401/403/500/network/timeout) — restore the message.
      console.error('Delete message error:', error);
      forgetDeletedMessage(deletedMessageIdsRef, deletedKeys);
      setMessages((prev) => mergeMessages(prev, [deletedMessage]));
      onConversationMessage?.(deletedMessage, { clearUnread: true });
    }
  }, [channel?.id, mergeMessages, withoutLocallyDeletedMessages, onConversationMessage, onConversationMessageDeleted]);

  const handleEditMessage = useCallback(async (messageId, content) => {
    const originalMessage = messagesRef.current.find((message) => message.id === messageId);
    if (!originalMessage) return;

    setMessages((prev) => prev.map((message) => (
      message.id === messageId
        ? { ...message, content, edited: true, edited_at: new Date().toISOString() }
        : message
    )));
    onConversationMessageEdited?.({
      id: messageId,
      channel_id: channel.id,
      content,
      edited_at: new Date().toISOString(),
    });

    try {
      await axios.put(`${BACKEND_URL}/api/channels/${channel.id}/messages/${messageId}`, { content }, { withCredentials: true });
    } catch (error) {
      console.error('Edit message error:', error);
      setMessages((prev) => prev.map((message) => (
        message.id === messageId
          ? originalMessage
          : message
      )));
      onConversationMessage?.(originalMessage, { clearUnread: true });
    }
  }, [channel?.id, onConversationMessageEdited, onConversationMessage]);

  const cancelComposerEdit = useCallback(() => {
    setEditingMessage(null);
    setNewMessage(editDraftBeforeRef.current || '');
    focusComposer(composerTextareaRef);
  }, []);

  const submitComposer = useCallback(() => {
    if (editingMessage) {
      const nextContent = newMessage.trim();
      const draftBeforeEdit = editDraftBeforeRef.current || '';
      if (!nextContent) return;
      if (nextContent !== editingMessage.content) {
        handleEditMessage(editingMessage.id, nextContent);
      }
      setEditingMessage(null);
      setNewMessage(draftBeforeEdit);
      clearScheduledDraft('channel', channel.id);
      saveDraft('channel', channel.id, draftBeforeEdit);
      focusComposer(composerTextareaRef);
      return;
    }
    sendMessage(newMessage);
  }, [channel?.id, editingMessage, handleEditMessage, newMessage, sendMessage]);

  const handleReaction = useCallback(async (messageId, emoji) => {
    if (!messageId || isTempMessageId(messageId)) return;

    const originalMessage = messagesRef.current.find((message) => message.id === messageId);
    if (!originalMessage) return;
    if (!canToggleReaction(originalMessage.reactions, emoji, user?.id)) return;

    const action = getReactionAction(originalMessage.reactions, emoji, user?.id);
    const clientReactionId = createReactionRequestId();
    const requestKey = getReactionRequestKey(messageId, emoji);
    latestReactionRequestRef.current[requestKey] = clientReactionId;

    setMessages((prev) => applyOptimisticReactionUpdate(prev, messageId, emoji, user?.id));

    try {
      const previousRequest = reactionRequestChainsRef.current[requestKey] || Promise.resolve();
      const requestPromise = previousRequest.catch(() => {}).then(() => axios.post(
          `${BACKEND_URL}/api/channels/${channel.id}/messages/${messageId}/reaction`,
          { emoji, action, client_reaction_id: clientReactionId },
          { withCredentials: true }
        ));

      const chainPromise = requestPromise.finally(() => {
        if (reactionRequestChainsRef.current[requestKey] === chainPromise) {
          delete reactionRequestChainsRef.current[requestKey];
        }
      });
      reactionRequestChainsRef.current[requestKey] = chainPromise;

      const response = await requestPromise;
      applyServerReactionUpdate(response.data);
    } catch (error) {
      console.error('Reaction error:', error);
      if (latestReactionRequestRef.current[requestKey] !== clientReactionId) return;
      delete latestReactionRequestRef.current[requestKey];
      setMessages((prev) => prev.map((message) => (
        message.id === messageId ? { ...message, reactions: originalMessage.reactions } : message
      )));
    }
  }, [channel?.id, user?.id, applyServerReactionUpdate]);

  const handleLeaveChannel = async () => {
  if (!window.confirm('Are you sure you want to leave this channel?')) return;

  try {
    await axios.post(
      `${BACKEND_URL}/api/channels/${channel.id}/leave`,
      {},
      { withCredentials: true }
    );

    setShowMenu(false);
    onChannelDeleted?.(channel.id);
    onBack();
  } catch (error) {
    console.error('Leave channel error:', error);
    alert(error?.response?.data?.detail || 'Failed to leave channel');
  }
};

const handleDeleteChannel = async () => {
  if (!window.confirm('Are you sure you want to delete this channel? This action cannot be undone.')) return;

  try {
    await axios.delete(
      `${BACKEND_URL}/api/channels/${channel.id}`,
      { withCredentials: true }
    );

    setShowMenu(false);
    onChannelDeleted?.(channel.id);
    onBack();
  } catch (error) {
    console.error('Delete channel error:', error);
    alert(error?.response?.data?.detail || 'Failed to delete channel');
  }
};

  const typingUser = typingUsers[channel?.id];
  // WhatsApp-style handoff: when the person typing just delivered a message
  // (fresh realtime stamp), hide the dots in the SAME commit the bubble lands —
  // the message takes the indicator's place instead of pushing past it and
  // settling back a beat later. Presentational only; typing socket state is
  // untouched, and if they keep typing the dots return on the next typing event.
  const lastLiveMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const typingHandedOff = Boolean(
    typingUser && lastLiveMessage && lastLiveMessage.sender_id === typingUser.user_id
    && typeof lastLiveMessage._realtime_received_at === 'number'
    && Date.now() - lastLiveMessage._realtime_received_at < 1500
  );
  const showTypingIndicator = Boolean(typingUser && typingUser.user_id !== user?.id && !typingHandedOff);
  const formatTime = (seconds) => `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

  const focusComposerCb = useCallback(() => focusComposer(composerTextareaRef), []);
  const shouldPreserveComposerFocusCb = useCallback(() => composerFocusedRef.current, []);

  const senderMetaById = useMemo(() => {
    const map = new Map();
    if (user?.id) {
      map.set(user.id, {
        username: user.username,
        profile_picture_thumb: user.profile_picture_thumb,
      });
    }
    (channelInfo?.members || []).forEach((member) => {
      if (member?.id) {
        map.set(member.id, {
          username: member.username,
          profile_picture_thumb: member.profile_picture_thumb,
          avatar_thumb: member.avatar_thumb,
          thumbnail_url: member.thumbnail_url,
        });
      }
    });
    return map;
  }, [channelInfo?.members, user?.id, user?.profile_picture_thumb, user?.username]);

  // Resolve a message's reply_to (an immutable original-message id) to the original
  // message object, so the in-bubble quote can render the ORIGINAL sender + preview.
  const messagesById = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (m?.id) map.set(m.id, m);
      if (m?.client_temp_id) map.set(m.client_temp_id, m);
      if (m?.tempId) map.set(m.tempId, m);
    });
    return map;
  }, [messages]);
  const resolveReplyTarget = useCallback((message) => {
    const refId = message?.reply_to;
    if (!refId) return null;
    // A deleted original: show "was deleted" (distinct from generally unavailable).
    // deletedMessageIdsRef is the authoritative deleted-id set and wins over cache.
    // Sentinel constants keep the returned object identity stable across renders
    // so memoized bubbles don't re-render for no reason.
    if (deletedMessageIdsRef.current?.has(String(refId))) return DELETED_REPLY_TARGET;
    return messagesById.get(refId) || UNAVAILABLE_REPLY_TARGET;
  }, [messagesById]);

  // ── Reply-to-original navigation (scroll + highlight) ──
  const messageRefs = useRef(new Map());
  const setMessageRef = useCallback((messageId, element) => {
    if (!messageId) return;
    const key = String(messageId);
    if (element) messageRefs.current.set(key, element);
    else messageRefs.current.delete(key);
  }, []);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const highlightTimeoutRef = useRef(null);
  const navRunIdRef = useRef(0);
  const contextFetchInProgressRef = useRef(new Set()); // dedupe repeat taps on the same target
  const [replyNavTargetId, setReplyNavTargetId] = useState(null); // shows "finding…" on that quote
  useEffect(() => () => { if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current); }, []);

  const highlightMessage = useCallback((messageId) => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    setHighlightedMessageId(String(messageId));
    highlightTimeoutRef.current = setTimeout(() => setHighlightedMessageId(null), 1800);
  }, []);

  const scrollToMessage = useCallback((messageId) => {
    const el = messageRefs.current.get(String(messageId));
    if (!el) return false;
    const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    } catch {
      el.scrollIntoView();
    }
    highlightMessage(messageId);
    return true;
  }, [highlightMessage]);

  const navigateToOriginalMessage = useCallback(async (messageId) => {
    if (!messageId) return;
    const targetKey = String(messageId);
    // Already loaded? Scroll immediately.
    if (messageRefs.current.get(targetKey)) { scrollToMessage(targetKey); return; }
    // Optimistic/local reply targets have no server id to fetch context for.
    if (isTempMessageId(targetKey)) { toast?.('Original message is unavailable.'); return; }
    // Dedupe rapid repeat taps on the same unloaded target.
    if (contextFetchInProgressRef.current.has(targetKey)) return;

    const runId = ++navRunIdRef.current;
    const activeChannelId = channel?.id;
    contextFetchInProgressRef.current.add(targetKey);
    setReplyNavTargetId(targetKey);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/channels/${activeChannelId}/messages/${targetKey}/context`,
        { withCredentials: true, params: { radius: MESSAGE_PAGE_SIZE } }
      );
      // Superseded (another nav started) or conversation switched: drop silently.
      if (navRunIdRef.current !== runId || currentChannelRef.current !== activeChannelId) return;

      const fetched = Array.isArray(response.data?.messages) ? response.data.messages : [];
      if (fetched.length) {
        const container = messagesContainerRef.current;
        if (container) {
          prependScrollRestoreRef.current = {
            previousHeight: container.scrollHeight,
            previousTop: container.scrollTop,
          };
        }
        setMessages((prev) => withoutLocallyDeletedMessages(
          sortMessagesForDisplay(mergeMessages(prev, sortMessagesForDisplay(fetched)))
        ));
      }

      // Let the merged messages render + register refs, then scroll+highlight.
      await new Promise((r) => setTimeout(r, 80));
      if (navRunIdRef.current === runId && currentChannelRef.current === activeChannelId) {
        if (!scrollToMessage(targetKey)) toast?.('Original message is unavailable.');
      }
    } catch (error) {
      // 404/410 (deleted / no longer in convo) or 403 (no access): unavailable.
      if (navRunIdRef.current === runId) toast?.('Original message is unavailable.');
    } finally {
      contextFetchInProgressRef.current.delete(targetKey);
      if (navRunIdRef.current === runId) setReplyNavTargetId(null);
    }
  }, [channel?.id, scrollToMessage, toast, mergeMessages, withoutLocallyDeletedMessages]);


  // Precompute everything derived from (messages, index) once per messages
  // change: date parsing, grouping, meta visibility, time/status labels and
  // reply resolution. A keystroke or a delivery-status update then only re-runs
  // this memo — NOT per-bubble Date/Intl work — and each memoized bubble
  // receives stable primitive/identity props.
  const messageRows = useMemo(() => messages.map((message, index) => {
    const isGrouped = isSameMessageGroup(messages[index - 1], message);
    const showDateDivider = shouldShowDateDivider(messages, index);
    return {
      key: getStableMessageKey(message),
      message,
      index,
      showDateDivider,
      isGrouped,
      showAvatar: !isGrouped,
      showMeta: shouldShowMessageMeta(messages, index),
      timeLabel: formatMessageTime(message.created_at),
      statusLabel: getMessageStatusLabel(message),
      dateDividerLabel: showDateDivider ? formatMessageDateDivider(message.created_at) : null,
      replyTarget: resolveReplyTarget(message),
    };
  }), [messages, resolveReplyTarget]);


  if (!channel) {
    return (
      <div className="flex-1 flex flex-col bg-surface-0">
        <div className="md:hidden shrink-0 flex items-center h-16 px-2 border-b border-border-subtle">
          <button onClick={onBack} className="ui-icon-btn ui-icon-btn-lg" aria-label="Open conversations" data-testid="menu-btn">
            <Menu className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 bg-brand-gradient-soft ring-1 ring-inset ring-white/[0.08] rounded-3xl flex items-center justify-center mx-auto mb-5">
              <MessageCircle className="w-9 h-9 text-cyan-300" />
            </div>
            <h3 className="text-xl font-semibold text-white font-heading tracking-tight">Select a conversation</h3>
            <p className="text-zinc-500 mt-2 text-sm leading-6">Choose a channel or direct message from the sidebar to start chatting.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-surface-0 overflow-hidden">
      {/* Header - pinned */}
      <div className="chat-header-safe shrink-0 sticky top-0 z-20 border-b border-border-subtle bg-surface-1/95 px-2 md:px-5 md:py-3 backdrop-blur-xl flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button onClick={onBack} className="md:hidden ui-icon-btn ui-icon-btn-lg shrink-0" aria-label="Back" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="h-10 w-10 rounded-2xl bg-brand-gradient-soft ring-1 ring-inset ring-white/[0.08] flex items-center justify-center shrink-0">
            <Hash className="w-5 h-5 text-cyan-300" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-[15px] text-white font-heading tracking-tight md:text-base truncate leading-tight">{channel.name}</h2>
            <p className="text-[12px] leading-tight truncate mt-0.5">
              {typingUser && typingUser.user_id !== user?.id ? (
                <span className="text-cyan-300">{typingUser.username} is typing…</span>
              ) : (
                <span className="text-zinc-500 inline-flex items-center gap-1">
                  <Users className="w-3 h-3" /> {channelInfo?.members?.length || 0} {(channelInfo?.members?.length || 0) === 1 ? 'member' : 'members'}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="relative shrink-0">
          <button onClick={() => setShowMenu(!showMenu)} className="ui-icon-btn ui-icon-btn-md" aria-label="Channel options" data-testid="channel-menu-btn">
            <MoreVertical className="w-5 h-5" />
          </button>

          <AnimatePresence>
          {showMenu && (
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.14, ease: 'easeOut' }}
              className="ui-popover absolute right-0 top-12 overflow-hidden z-20 w-[240px]"
            >
              <div className="p-3 border-b border-white/[0.06]">
                <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Channel Code</p>
                <p className="text-sm font-mono text-white mt-1 tracking-wide">{channel.code}</p>
              </div>
              <div className="max-h-52 overflow-y-auto p-1.5 border-b border-white/[0.06]">
                <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider px-2 py-1.5">Members</p>
                {channelInfo?.members?.map((member) => (
                  <button
                    key={member.id}
                    onClick={() => { setViewingUser(member); setShowMenu(false); }}
                    className="ui-menu-item"
                  >
                    <Avatar src={member.profile_picture_thumb || member.avatar_thumb || member.thumbnail_url} username={member.username} size="sm" isOnline={member.is_online} />
                    <span className="truncate">{member.username}</span>
                  </button>
                ))}
              </div>
              <div className="p-1.5">
                {channelInfo?.created_by !== user?.id && (
                  <button onClick={handleLeaveChannel} className="ui-menu-item" data-testid="leave-channel-btn">
                    <DoorOpen className="w-4 h-4 text-zinc-400" /> Leave Channel
                  </button>
                )}
                {channelInfo?.created_by === user?.id && (
                  <button onClick={handleDeleteChannel} className="ui-menu-item ui-menu-item-danger" data-testid="delete-channel-btn">
                    <Trash2 className="w-4 h-4" /> Delete Channel
                  </button>
                )}
              </div>
            </motion.div>
          )}
          </AnimatePresence>
        </div>
      </div>

      {/* Messages - only this div scrolls */}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        onPointerDown={handleMessagesBackgroundPointerDown}
        onPointerMove={handleMessagesBackgroundPointerMove}
        onPointerUp={handleMessagesBackgroundPointerUp}
        onPointerCancel={handleMessagesBackgroundPointerCancel}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain hide-scrollbar"
        data-testid="messages-container"
      >
        <ChatLayoutTransition
          enabled={MESSAGE_ANIMATIONS_ENABLED}
          conversationId={channel?.id}
          messages={messages}
          showTyping={showTypingIndicator}
          scrollerRef={messagesContainerRef}
          entryRefs={flipEntryRefs}
          typingEntryRef={flipTypingEntryRef}
          composerGetter={() => composerContainerRef.current}
        >
        {/* Inner wrapper: pushes messages to the bottom when list is short */}
        <div className="relative flex min-h-full flex-col justify-end px-3 py-3 md:p-4">
          {isLoadingOlder && messages.length > 0 && <OlderMessagesLoader />}
          {isMessagesLoading && messages.length === 0 ? (
            <MessagesLoadingSkeleton />
          ) : messages.length === 0 ? (
            <EmptyConversationState
              title="No messages yet"
              subtitle="Start the conversation in this channel and your messages will show up here."
            />
          ) : (
            messageRows.map(({ key, message, index, showDateDivider, isGrouped, showAvatar, showMeta, timeLabel, statusLabel, dateDividerLabel, replyTarget }) => (
              <React.Fragment key={key}>
                {showDateDivider && <DateDivider label={dateDividerLabel} />}
                <div
                  ref={(el) => setMessageRef(message.id, el)}
                  data-message-id={message.id}
                  data-chat-layout-row={key}
                  className={`${index === 0 ? 'mt-0' : showDateDivider ? 'mt-1.5' : isGrouped ? 'mt-0.5' : 'mt-2.5 md:mt-3.5'}${highlightedMessageId && String(message.id) === highlightedMessageId ? ' reply-target-highlight' : ''}`}
                >
                  <div
                    className="msg-entry"
                    ref={(el) => {
                      if (el) flipEntryRefs.current.set(key, el);
                      else flipEntryRefs.current.delete(key);
                    }}
                  >
                  <MessageBubble
                    message={message}
                    isOwn={message.sender_id === user?.id}
                    showAvatar={showAvatar}
                    isGrouped={isGrouped}
                    showMeta={showMeta}
                    timeLabel={timeLabel}
                    statusLabel={statusLabel}
                    onReply={handleReply}
                    onDelete={handleDeleteMessage}
                    onEdit={handleStartEdit}
                    onReact={handleReaction}
                    onRetry={retryFailedMessage}
                    onCancelLocal={removeLocalImageMessage}
                    onFocusComposer={focusComposerCb}
                    onShouldPreserveComposerFocus={shouldPreserveComposerFocusCb}
                    senderMeta={senderMetaById.get(message.sender_id)}
                    replyToMessage={replyTarget}
                    onQuoteClick={navigateToOriginalMessage}
                    currentUserId={user?.id}
                  />
                  </div>
                </div>
              </React.Fragment>
            ))
          )}
          {showTypingIndicator && (
            <div data-chat-layout-row={TYPING_ROW_KEY} className="mt-3" aria-live="polite">
              <div className="typing-entry flex items-center gap-2" ref={flipTypingEntryRef}>
                <div className="inline-flex items-center gap-1.5 rounded-[20px] rounded-tl-md bg-surface-2 px-3.5 py-2.5 shadow-elev-sm ring-1 ring-inset ring-white/[0.04]">
                  <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-zinc-500">{typingUser.username} is typing</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>{/* end bottom-align inner wrapper */}
        </ChatLayoutTransition>
      </div>

      {editingMessage && (
        <div className="shrink-0 border-t border-border-subtle bg-surface-1 px-3.5 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <Edit2 className="w-4 h-4 text-amber-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-amber-300">Editing message</p>
              <p className="max-w-[240px] truncate text-[12px] text-zinc-400">{editingMessage.content}</p>
            </div>
          </div>
          <button type="button" onPointerDown={preventComposerBlur} onMouseDown={preventComposerBlur} onClick={cancelComposerEdit} className="ui-icon-btn ui-icon-btn-sm" aria-label="Cancel edit">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Reply bar - pinned above composer */}
      {!editingMessage && replyTo && (
        <div className="reply-composer-enter shrink-0 border-t border-border-subtle bg-surface-1 px-3.5 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <Reply className="w-4 h-4 text-cyan-300 shrink-0" />
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-cyan-300">Replying to {replyTo.sender_username}</p>
              <p className="max-w-[240px] truncate text-[12px] text-zinc-400">
                <MediaPreview messageType={replyTo.message_type} fallbackText={replyTo.content} />
              </p>
            </div>
          </div>
          <button type="button" onPointerDown={preventComposerBlur} onMouseDown={preventComposerBlur} onClick={() => setReplyTo(null)} className="ui-icon-btn ui-icon-btn-sm" aria-label="Cancel reply">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Composer - pinned at bottom */}
      <div
        ref={composerContainerRef}
        className="composer-bar shrink-0 border-t border-border-subtle bg-surface-1/95 p-2.5 backdrop-blur-xl"
        style={{ paddingBottom: 'calc(0.625rem + var(--whisper-kb-inset, env(safe-area-inset-bottom)))' }}
      >
        {voiceError && !isRecording && (
          <div className="mb-2 rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-200 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" /> {voiceError}
          </div>
        )}
        {isRecording ? (
          <div className="flex items-center gap-3 rounded-[22px] bg-surface-2 px-4 py-2.5 ring-1 ring-inset ring-red-500/20">
            <div className="flex-1 flex items-center gap-3">
              <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
              <span className="text-white font-mono text-sm tabular-nums">{formatTime(recordingTime)}</span>
              <span className="text-zinc-400 text-sm">Recording…</span>
            </div>
            <button onClick={cancelRecording} className="ui-icon-btn ui-icon-btn-md" title="Cancel recording" aria-label="Cancel recording" data-testid="cancel-recording-btn">
              <Trash2 className="w-5 h-5" />
            </button>
            <button onClick={stopRecording} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gradient shadow-elev-sm transition-transform active:scale-95" title="Send recording" aria-label="Send recording" data-testid="stop-recording-btn">
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>
        ) : (
          <div className="relative flex items-end gap-2">
            {showEmojiPicker && (
              <div ref={composerEmojiPickerRef} className="absolute bottom-full left-0 z-40 mb-2">
                <Suspense fallback={<div className="h-[360px] w-[352px] max-w-[90vw] rounded-xl bg-surface-2 ui-skeleton" />}>
                  <LazyEmojiPicker onEmojiSelect={addEmoji} theme="dark" previewPosition="none" skinTonePosition="none" />
                </Suspense>
              </div>
            )}
            <div className="composer-shell">
              <div className="flex shrink-0 self-end items-center gap-0.5 p-1.5">
                <button ref={composerEmojiButtonRef} onClick={() => setShowEmojiPicker((prev) => !prev)} className="ui-icon-btn ui-icon-btn-sm" aria-label="Emoji" data-testid="emoji-btn">
                  <Smile className="w-5 h-5" />
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowAttachMenu((prev) => !prev)}
                    disabled={isUploading}
                    className="ui-icon-btn ui-icon-btn-sm"
                    aria-label="Attach media"
                    aria-haspopup="menu"
                    aria-expanded={showAttachMenu}
                    data-testid="attach-btn"
                  >
                    {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                  </button>
                  <AnimatePresence>
                    {showAttachMenu && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setShowAttachMenu(false)} aria-hidden="true" />
                        <motion.div
                          initial={{ opacity: 0, y: 6, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 6, scale: 0.97 }}
                          transition={{ duration: 0.14, ease: 'easeOut' }}
                          role="menu"
                          className="ui-popover absolute bottom-full left-0 z-40 mb-2 w-40 p-1.5"
                        >
                          <button type="button" role="menuitem" onClick={() => { setShowAttachMenu(false); fileInputRef.current?.click(); }} className="ui-menu-item" data-testid="attach-photos">
                            <Image className="w-4 h-4 text-zinc-400" /> Photos
                          </button>
                          <button type="button" role="menuitem" onClick={() => { setShowAttachMenu(false); videoFileInputRef.current?.click(); }} className="ui-menu-item" data-testid="attach-videos">
                            <Video className="w-4 h-4 text-zinc-400" /> Videos
                          </button>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
                <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" multiple className="hidden" />
                <input type="file" ref={videoFileInputRef} onChange={handleVideoUpload} accept="video/mp4,video/webm,video/quicktime" multiple className="hidden" />
              </div>
              <textarea
                ref={composerTextareaRef}
                value={newMessage}
                onChange={handleTyping}
                onKeyDown={handleKeyPress}
                onFocus={handleComposerFocus}
                onBlur={handleComposerBlur}
                placeholder={editingMessage ? 'Edit message…' : 'Type a message…'}
                rows={1}
                className="composer-textarea max-h-32 flex-1 self-end resize-none px-1.5 py-3 text-base leading-6 text-white placeholder:text-zinc-500"
                data-testid="message-input"
              />
            </div>
            {newMessage.trim() || editingMessage ? (
              <button type="button" onPointerDown={preventComposerBlur} onMouseDown={preventComposerBlur} onClick={submitComposer} disabled={isUploading || (!!editingMessage && !newMessage.trim())} className="flex h-12 w-12 shrink-0 self-end items-center justify-center rounded-full bg-brand-gradient shadow-elev-sm transition-transform duration-150 active:scale-95 disabled:opacity-50 disabled:pointer-events-none" aria-label="Send message" data-testid="send-btn">
                {isUploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Send className="w-5 h-5 text-white" />}
              </button>
            ) : (
              <button onClick={startRecording} disabled={isUploading} className="flex h-12 w-12 shrink-0 self-end items-center justify-center rounded-full bg-surface-3 transition-colors hover:bg-zinc-700 active:scale-95 disabled:opacity-50" aria-label="Record voice message" data-testid="voice-btn">
                <Mic className="w-5 h-5 text-zinc-300" />
              </button>
            )}
          </div>
        )}
      </div>

      <UserProfileModal
        user={viewingUser}
        isOpen={!!viewingUser}
        onClose={() => setViewingUser(null)}
        onStartDm={null}
      />
    </div>
  );
};

// DM Chat Area
const DmChatArea = ({
  dm,
  onBack,
  refreshSignal = 0,
  readActivationSeq = 0,
  isReadAuthorized,
  requestReadAuthorization,
  onReadConfirmed,
  sidebarCovering = false,
  onConversationMessage,
  onConversationMessageEdited,
  onConversationMessageDeleted,
}) => {
  const { user } = useAuth();
  const { error: toast } = useToast();
  const { 
    connected, joinDm, leaveDm,
    onNewDmMessage, onDmMessageEdited, onDmMessageDeleted, onDmReactionUpdated, onDmMessagesSeen, onDmMessagesDelivered,
    emitDmTypingStart, emitDmTypingStop, dmTypingUsers, presenceByUser, presenceNow,
    onUserProfileUpdated,
  } = useSocket();
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [dmInfo, setDmInfo] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  // Live presence for the other user - updated by socket events and presence refresh
  const [liveOtherUser, setLiveOtherUser] = useState(null);
  // Latest-value refs so the memoized bubble handlers stay stable (stable deps)
  // while always reading the CURRENT messages/editing/newMessage values.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const editingMessageRef = useRef(editingMessage);
  editingMessageRef.current = editingMessage;
  const newMessageRef = useRef(newMessage);
  newMessageRef.current = newMessage;
  const textareaResizeRafRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const composerTextareaRef = useRef(null);
  const composerEmojiButtonRef = useRef(null);
  const composerEmojiPickerRef = useRef(null);
  const composerFocusedRef = useRef(false);
  const backgroundPointerRef = useRef(null);
  const editDraftBeforeRef = useRef('');
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const cancelRecordingRef = useRef(false);
  const recordingStreamRef = useRef(null);
  const voiceDraftsRef = useRef({});
  const imageDraftsRef = useRef({});
  const videoDraftsRef = useRef({});
  const videoFileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingEmitRef = useRef(0);
  const typingActiveRef = useRef(false);
  const recordingIntervalRef = useRef(null);
  const currentDmRef = useRef(null);
  // Track whether the sidebar (DM list) is currently covering this chat, kept in a
  // ref so the long-lived socket effect reads the CURRENT value (no stale closure).
  const sidebarCoveringRef = useRef(sidebarCovering);
  useEffect(() => { sidebarCoveringRef.current = sidebarCovering; }, [sidebarCovering]);
  // "Is THIS DM genuinely being viewed right now?" — only then may we mark it read.
  // On mobile the sidebar overlays the chat, so a covered chat is NOT viewed. The
  // component is only mounted for the selected DM, so selection is already implied.
  const isDmViewed = useCallback((authorizedSeq = readActivationSeq) => {
    if (!isReadAuthorized?.(dm?.id, authorizedSeq)) return false;
    if (!isDocumentActivelyViewed()) return false;
    let isMobile = false;
    try {
      isMobile = typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(max-width: 767px)').matches
        : (typeof window !== 'undefined' && window.innerWidth < 768);
    } catch { isMobile = false; }
    if (isMobile && sidebarCoveringRef.current) return false; // covered by the list
    return true;
  }, [dm?.id, readActivationSeq, isReadAuthorized]);
  const lastReadRequestRef = useRef(null);
  const authorizeAndMarkRef = useRef(null);
  const prependDmScrollRestoreRef = useRef(null);
  const dmSendQueueRef = useRef([]);
  const isFlushingDmSendQueueRef = useRef(false);
  const isLoadingOlderRef = useRef(false);
  const lastOlderCursorRef = useRef(null);
  const latestReactionRequestRef = useRef({});
  const completedReactionRequestIdsRef = useRef(new Set());
  const reactionRequestChainsRef = useRef({});
  const deletedMessageIdsRef = useRef(new Set());
  // FLIP stack-insertion animation state (presentational only, never persisted).
  const flipEntryRefs = useRef(new Map());      // stable key -> inner .msg-entry element
  const flipTypingEntryRef = useRef(null);      // inner typing-entry element
  const composerContainerRef = useRef(null);    // origin for the "rise from composer"

  // Load draft on DM change
  useLayoutEffect(() => {
    if (dm?.id !== currentDmRef.current) {
      const cacheKey = dm?.id ? `whisper_dm_messages_${dm.id}` : null;
      const inMemoryMessages = dm?.id ? getDmSessionMessages(dm.id) : null;
      const cachedMessages = cacheKey
        ? sortMessagesForDisplay(normalizeMessagesForState(
            Array.isArray(inMemoryMessages)
              ? inMemoryMessages
              : sanitizeMessagesForCache(getMessageCache(cacheKey))
          ))
        : [];
      setMessages(cachedMessages);
      setDmInfo(dm || null);
      setReplyTo(null);
      setEditingMessage(null);
      editDraftBeforeRef.current = '';
      setShowEmojiPicker(false);
      setVoiceError('');
      setIsMessagesLoading(Boolean(dm?.id) && cachedMessages.length === 0);
      setIsLoadingOlder(false);
      setHasMoreMessages(false);
      prependDmScrollRestoreRef.current = null;
      isLoadingOlderRef.current = false;
      lastOlderCursorRef.current = null;
      latestReactionRequestRef.current = {};
      completedReactionRequestIdsRef.current = new Set();
      reactionRequestChainsRef.current = {};
      deletedMessageIdsRef.current = new Set();
      flipEntryRefs.current = new Map();
      flipTypingEntryRef.current = null;
      try { cancelAllChatAnimations(); } catch { /* noop */ }
      lastReadRequestRef.current = null;
      // Conversation switch: abort in-flight media uploads and free draft blob URLs
      // so switching away mid-upload doesn't leak object URLs or orphan requests.
      Object.values(videoDraftsRef.current).forEach((draft) => {
        if (draft?.abort) { try { draft.abort.abort(); } catch {} }
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      videoDraftsRef.current = {};
      Object.values(imageDraftsRef.current).forEach((draft) => {
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      imageDraftsRef.current = {};
      Object.values(voiceDraftsRef.current).forEach((draft) => {
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      voiceDraftsRef.current = {};
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      typingActiveRef.current = false;
      lastTypingEmitRef.current = 0;
      currentDmRef.current = dm?.id;
      
      if (dm?.id) {
        const draft = getDraft('dm', dm.id);
        setNewMessage(draft);
      }
    }
  }, [dm?.id]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      if (mediaRecorderRef.current?.state && mediaRecorderRef.current.state !== 'inactive') {
        cancelRecordingRef.current = true;
        try { mediaRecorderRef.current.stop(); } catch (_) {}
      }
      Object.values(voiceDraftsRef.current).forEach((draft) => {
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      voiceDraftsRef.current = {};
      Object.values(imageDraftsRef.current).forEach((draft) => {
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      imageDraftsRef.current = {};
      Object.values(videoDraftsRef.current).forEach((draft) => {
        if (draft?.abort) { try { draft.abort.abort(); } catch {} }
        if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      });
      videoDraftsRef.current = {};
    };
  }, []);

  const mergeDmMessages = useCallback((oldMessages, newMessages) => {
    return mergeMessagesByIdentity(oldMessages, newMessages);
  }, []);

  const withoutLocallyDeletedMessages = useCallback((nextMessages) => {
    return filterMessagesByDeletedIds(nextMessages, deletedMessageIdsRef);
  }, []);

  const applyServerReactionUpdate = useCallback((data) => {
    if (!data?.message_id) return;

    const requestKey = getReactionRequestKey(data.message_id, data.emoji);
    const latestRequestId = latestReactionRequestRef.current[requestKey];
    const clientReactionId = data.client_reaction_id;
    if (data.user_id === user?.id && clientReactionId && completedReactionRequestIdsRef.current.has(clientReactionId)) {
      return;
    }

    if (data.user_id === user?.id && latestRequestId) {
      if (clientReactionId !== latestRequestId) {
        if (clientReactionId) completedReactionRequestIdsRef.current.add(clientReactionId);
        return;
      }
      delete latestReactionRequestRef.current[requestKey];
    }
    if (data.user_id === user?.id && clientReactionId) {
      completedReactionRequestIdsRef.current.add(clientReactionId);
    }

    setMessages((prev) => prev.map((message) => (
      message.id === data.message_id
        ? { ...message, reactions: normalizeReactions(data.reactions) }
        : message
    )));
  }, [user?.id]);

  // Save draft (debounced: localStorage writes are synchronous and the effect
  // fires per keystroke)
  useEffect(() => {
    if (dm?.id && !editingMessage) {
      scheduleDraftSave('dm', dm.id, newMessage);
    }
  }, [newMessage, dm?.id, editingMessage]);

  // Autosize the composer, coalesced to one forced reflow per animation frame.
  useEffect(() => {
    const textarea = composerTextareaRef.current;
    if (!textarea) return;

    if (textareaResizeRafRef.current) {
      cancelAnimationFrame(textareaResizeRafRef.current);
      textareaResizeRafRef.current = null;
    }
    textareaResizeRafRef.current = requestAnimationFrame(() => {
      textareaResizeRafRef.current = null;
      textarea.style.height = '0px';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 128)}px`;
    });
    return () => {
      if (textareaResizeRafRef.current) {
        cancelAnimationFrame(textareaResizeRafRef.current);
        textareaResizeRafRef.current = null;
      }
    };
  }, [newMessage]);

  useEffect(() => {
    if (!showEmojiPicker) return;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (composerEmojiPickerRef.current?.contains(target) || composerEmojiButtonRef.current?.contains(target)) return;
      setShowEmojiPicker(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowEmojiPicker(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showEmojiPicker]);

  const handleComposerFocus = useCallback(() => {
    composerFocusedRef.current = true;
    authorizeAndMarkRef.current?.('composer-focus');
    scheduleWhisperViewportSync();
  }, []);

  const handleComposerBlur = useCallback(() => {
    composerFocusedRef.current = false;
    scheduleWhisperViewportSync();
  }, []);

  const blurComposerFromBackground = useCallback(() => {
    const textarea = composerTextareaRef.current;
    const wasFocused = composerFocusedRef.current || (typeof document !== 'undefined' && document.activeElement === textarea);
    if (wasFocused) textarea?.blur();
    composerFocusedRef.current = false;
    setShowEmojiPicker(false);
    if (wasFocused) scheduleWhisperViewportSync();
  }, []);

  const handleMessagesBackgroundPointerDown = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) return;
    authorizeAndMarkRef.current?.('message-list-interaction');
    const target = event.target;
    if (target?.closest?.('[data-chat-message="true"], [data-chat-interactive="true"]')) {
      backgroundPointerRef.current = null;
      return;
    }
    backgroundPointerRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      shouldBlur: true,
    };
  }, []);

  const handleMessagesBackgroundPointerMove = useCallback((event) => {
    const pointer = backgroundPointerRef.current;
    if (!pointer || pointer.pointerId !== event.pointerId) return;
    if (Math.abs(event.clientX - pointer.x) > 8 || Math.abs(event.clientY - pointer.y) > 8) {
      pointer.shouldBlur = false;
    }
  }, []);

  const handleMessagesBackgroundPointerUp = useCallback((event) => {
    const pointer = backgroundPointerRef.current;
    backgroundPointerRef.current = null;
    if (!pointer || pointer.pointerId !== event.pointerId || !pointer.shouldBlur) return;
    blurComposerFromBackground();
  }, [blurComposerFromBackground]);

  const handleMessagesBackgroundPointerCancel = useCallback(() => {
    backgroundPointerRef.current = null;
  }, []);

  // Load DM info and newest messages
  useEffect(() => {
    if (!dm?.id) return;

    currentDmRef.current = dm.id;
    let cancelled = false;
    const cacheKey = `whisper_dm_messages_${dm.id}`;
    const historyKey = getDmHistorySessionKey(dm.id);
    setDmInfo(dm);

    const loadData = async () => {
      try {
        const inMemoryMessages = getDmSessionMessages(dm.id);
        const rawCachedMessages = Array.isArray(inMemoryMessages)
          ? inMemoryMessages
          : sanitizeMessagesForCache(getMessageCache(cacheKey));
        const cachedMessages = sortMessagesForDisplay(withoutLocallyDeletedMessages(
          normalizeMessagesForState(rawCachedMessages)
        ));

        setIsMessagesLoading(cachedMessages.length === 0);
        const summaryKeys = getPreviewIdentityKeys(dm.last_message);
        const cacheHasSummaryLatest = summaryKeys.length === 0 || cachedMessages.some((message) => (
          getMessageIdentityKeys(message).some((key) => summaryKeys.includes(key))
        ));
        const validatedAt = dmHistoryValidatedAt.get(historyKey) || 0;
        if (cacheHasSummaryLatest && Date.now() - validatedAt < DM_HISTORY_RECENT_MS) {
          setHasMoreMessages(Boolean(dmHistoryHasMore.get(historyKey)));
          setIsMessagesLoading(false);
          return;
        }

        const historyRequest = requestLatestDmHistory(dm.id);
        const messagesRes = await historyRequest.promise;
        const { messages: rawLatestMessages, hasMore } = normalizeMessagesResponse(messagesRes.data, MESSAGE_PAGE_SIZE);
        const latestMessages = sortMessagesForDisplay(withoutLocallyDeletedMessages(rawLatestMessages));

        if (cancelled || currentDmRef.current !== dm.id) return;

        dmHistoryValidatedAt.set(historyKey, Date.now());
        evictOldest(dmHistoryValidatedAt, DM_HISTORY_META_CACHE_MAX);
        dmHistoryHasMore.set(historyKey, hasMore);
        evictOldest(dmHistoryHasMore, DM_HISTORY_META_CACHE_MAX);
        setHasMoreMessages(hasMore);
        setMessages((prev) => {
          const merged = withoutLocallyDeletedMessages(mergeServerMessagesPreservingLocal(
            prev,
            latestMessages,
            mergeDmMessages,
            { requestStartedAt: historyRequest.requestedAt }
          ));
          const clean = sortMessagesForDisplay(sanitizeMessagesForCache(merged));
          setDmSessionMessages(dm.id, merged);
          saveMessageCache(cacheKey, clean);
          return merged;
        });
      } catch (error) {
        console.error('Load DM data error:', error);
      } finally {
        if (!cancelled && currentDmRef.current === dm.id) {
          setIsMessagesLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [dm?.id, dm?.last_message?.id, refreshSignal, mergeDmMessages, withoutLocallyDeletedMessages]);

  const markDmReadThrough = useCallback((message, authorizedSeq = readActivationSeq) => {
    if (!dm?.id || !message?.id || !isDmViewed(authorizedSeq)) return;
    const requestKey = `${authorizedSeq}:${message.id}`;
    if (lastReadRequestRef.current === requestKey) return;
    lastReadRequestRef.current = requestKey;

    onConversationMessage?.(message, { clearUnread: true });
    const requestStartedAt = Date.now();
    axios.post(
      `${BACKEND_URL}/api/dms/${dm.id}/mark-read`,
      { message_id: message.id },
      { withCredentials: true }
    ).then((response) => {
      onReadConfirmed?.(dm.id, response.data, requestStartedAt);
    }).catch((error) => {
      if (lastReadRequestRef.current === requestKey) lastReadRequestRef.current = null;
      console.error('Mark DM read error:', error);
    });
  }, [dm?.id, readActivationSeq, isDmViewed, onConversationMessage, onReadConfirmed]);

  const authorizeAndMarkFromInteraction = useCallback((reason = 'interaction') => {
    if (!dm?.id || !isDocumentActivelyViewed()) return null;
    const authorizedSeq = requestReadAuthorization?.(dm.id, reason);
    if (!authorizedSeq) return null;
    const latestIncoming = getLatestIncomingConfirmedMessage(messages, user?.id);
    if (latestIncoming) markDmReadThrough(latestIncoming, authorizedSeq);
    return authorizedSeq;
  }, [dm?.id, markDmReadThrough, messages, requestReadAuthorization, user?.id]);
  authorizeAndMarkRef.current = authorizeAndMarkFromInteraction;

  // Fetching/reconciling is never a read action by itself. Only a fresh row/new-DM
  // activation in the current foreground session authorizes this exact boundary.
  useEffect(() => {
    const latestIncoming = getLatestIncomingConfirmedMessage(messages, user?.id);
    if (latestIncoming) markDmReadThrough(latestIncoming);
  }, [messages, user?.id, markDmReadThrough]);

  // Lightweight summaries repair a Seen receipt missed while this sender was
  // offline. Applying the peer boundary is monotonic and never fetches history.
  useEffect(() => {
    if (!dm?.peer_read_boundary || !user?.id) return;
    setMessages((previous) => applyDmSeenBoundary(previous, dm.peer_read_boundary, user.id));
  }, [dm?.peer_read_boundary?.last_read_created_at, dm?.peer_read_boundary?.last_read_message_id, user?.id]);

  useEffect(() => {
    if (!dm?.id || !connected) return;

    joinDm(dm.id);

    return () => {
      leaveDm(dm.id);
    };
  }, [dm?.id, connected, joinDm, leaveDm]);

  useEffect(() => {
    if (!dm?.id) return;

    const liveMessages = sortMessagesForDisplay(withoutLocallyDeletedMessages(messages));
    const cleanMessages = sortMessagesForDisplay(sanitizeMessagesForCache(liveMessages));
    setDmSessionMessages(dm.id, liveMessages);
    // Debounced persist: runs on EVERY messages change (receipts, reactions,
    // typing state), so write localStorage at most once per 800ms of silence.
    const cacheKey = `whisper_dm_messages_${dm.id}`;
    const timer = setTimeout(() => {
      saveMessageCache(cacheKey, cleanMessages);
    }, 800);
    return () => clearTimeout(timer);
  }, [dm?.id, messages, withoutLocallyDeletedMessages]);

  const loadOlderMessages = useCallback(async () => {
    if (!dm?.id || isLoadingOlderRef.current || !hasMoreMessages) return;

    const beforeCursor = getOldestMessageCursor(messages);
    const beforeCursorKey = getOlderCursorKey(beforeCursor);
    if (!beforeCursorKey) {
      setHasMoreMessages(false);
      return;
    }
    if (lastOlderCursorRef.current === beforeCursorKey) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    prependDmScrollRestoreRef.current = {
      previousHeight: container.scrollHeight,
      previousTop: container.scrollTop,
    };

    isLoadingOlderRef.current = true;
    lastOlderCursorRef.current = beforeCursorKey;
    setIsLoadingOlder(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dms/${dm.id}/messages`, {
        withCredentials: true,
        params: {
          limit: MESSAGE_PAGE_SIZE,
          before: beforeCursor.before,
          ...(beforeCursor.before_id ? { before_id: beforeCursor.before_id } : {}),
        },
      });

      const { messages: rawOlderMessages, hasMore } = normalizeMessagesResponse(response.data, MESSAGE_PAGE_SIZE);
      const olderMessages = sortMessagesForDisplay(withoutLocallyDeletedMessages(rawOlderMessages));

      if (currentDmRef.current !== dm.id) return;

      setHasMoreMessages(hasMore);
      if (rawOlderMessages.length === 0) {
        setHasMoreMessages(false);
        return;
      }
      if (olderMessages.length === 0) return;

      setMessages((prev) => withoutLocallyDeletedMessages(mergeDmMessages(prev, olderMessages)));
    } catch (error) {
      console.error('Load older DM messages error:', error);
      prependDmScrollRestoreRef.current = null;
      lastOlderCursorRef.current = null;
    } finally {
      if (currentDmRef.current === dm.id) {
        isLoadingOlderRef.current = false;
        setIsLoadingOlder(false);
      }
    }
  }, [dm?.id, hasMoreMessages, messages, mergeDmMessages, withoutLocallyDeletedMessages]);

  const handleMessagesScroll = useCallback(() => {
    authorizeAndMarkRef.current?.('message-list-scroll');
    const container = messagesContainerRef.current;
    if (!container || isMessagesLoading || isLoadingOlder || !hasMoreMessages) return;
    if (container.scrollTop <= OLDER_MESSAGES_SCROLL_THRESHOLD) {
      loadOlderMessages();
    }
  }, [hasMoreMessages, isLoadingOlder, isMessagesLoading, loadOlderMessages]);

  // Sync liveOtherUser from dm prop initially and when dm changes.
  // Merge (don't hard-replace) so a later dm.other_user that lacks avatar fields
  // (e.g. rehydrated from the list cache, which strips profile_picture) can never
  // blank an avatar we already have. Same-user only; keeps existing picture fields
  // when the incoming value is null/undefined.
  useEffect(() => {
    const incoming = dm?.other_user;
    if (!incoming) return;
    setLiveOtherUser((prev) => {
      if (!prev || prev.id !== incoming.id) return incoming;
      const merged = { ...prev, ...incoming };
      const PICTURE_FIELDS = ['profile_picture_thumb', 'avatar_thumb', 'thumbnail_url', 'profile_picture'];
      PICTURE_FIELDS.forEach((field) => {
        if (incoming[field] == null && prev[field] != null) {
          merged[field] = prev[field];
        }
      });
      return merged;
    });
  }, [dm?.id, dm?.other_user]);

  // Update presence from live socket events. onlineUsers updates globally in
  // SocketContext; here we (a) directly patch the open DM header's liveOtherUser
  // for the matching participant, and (b) trigger ONE lightweight API
  // reconciliation for the open DM — but only when the event is for the open
  // participant, so unrelated contacts' presence never causes a network fetch.
  // Socket events
  useEffect(() => {
    if (!connected || !dm?.id) return;

    const unsubNewMessage = onNewDmMessage((message) => {
      if (message.dm_id === dm.id) {
        if (message.sender_id === user?.id) return;
        if (filterMessagesByDeletedIds([message], deletedMessageIdsRef).length === 0) return;
        const stamped = { ...message, _realtime_received_at: Date.now() };
        // Explicit entry eligibility: a genuinely-new incoming socket message is
        // being accepted into the OPEN conversation right here (own echoes and
        // locally-deleted messages already returned above). Client-only metadata.
        registerEntryAnimation(getStableMessageKey(stamped), 'incoming-new');
        const viewed = isDmViewed();
        if (viewed) {
          // Genuinely on screen + explicitly activated: merge immediately. The
          // exact-boundary effect clears unread and posts mark-read from canonical state.
          setMessages(prev => withoutLocallyDeletedMessages(mergeDmMessages(prev, [stamped])));
          onConversationMessage?.(message, { clearUnread: true });
        } else {
          // Mounted but NOT actually viewed (tab hidden/unfocused, or mobile sidebar
          // covering the chat). Insert the message locally so it's there when opened,
          // but do NOT mark it read and do NOT clear the unread badge. The top-level
          // new_dm_message handler owns the unread increment; we pass clearUnread:false
          // (preview refresh only) so we neither clear nor double-count.
          setMessages(prev => withoutLocallyDeletedMessages(mergeDmMessages(prev, [stamped])));
          onConversationMessage?.(message, { clearUnread: false });
        }
      }
    });

    const unsubEdited = onDmMessageEdited((data) => {
      if (data.dm_id === dm.id) {
        onConversationMessageEdited?.(data);
        setMessages(prev => prev.map(m => 
          m.id === data.id ? { ...m, content: data.content, edited: true, edited_at: data.edited_at } : m
        ));
      }
    });

    const unsubDeleted = onDmMessageDeleted?.((data) => {
      if (data.dm_id === dm.id) {
        onConversationMessageDeleted?.({ ...data, reconcile: false });
        rememberDeletedMessage(deletedMessageIdsRef, data.message_id);
        setMessages(prev => withoutLocallyDeletedMessages(prev.filter(m => !getMessageIdentityKeys(m).includes(String(data.message_id)))));
        updateDmCachedMessages(data.dm_id, (cached) => cached.filter(
          (message) => !getMessageIdentityKeys(message).includes(String(data.message_id))
        ));
        removeMessageFromCache(`whisper_dm_messages_${data.dm_id}`, data.message_id);
      }
    });

    const unsubReaction = onDmReactionUpdated?.((data) => {
      if (data.dm_id === dm.id) {
        applyServerReactionUpdate(data);
      }
    });

    const unsubSeen = onDmMessagesSeen?.((data) => {
      if (data.dm_id === dm.id && String(data.user_id) !== String(user?.id)) {
        setMessages(prev => applyDmSeenBoundary(prev, data, user?.id));
      }
    });

    const unsubDelivered = onDmMessagesDelivered?.((data) => {
      if (data.dm_id === dm.id && String(data.user_id) !== String(user?.id)) {
        setMessages(prev => applyDmDeliveredBoundary(prev, data, user?.id));
      }
    });

    const unsubProfileUpdated = onUserProfileUpdated?.((data) => {
      setMessages((prev) => applyProfileUpdateToMessages(prev, data));
      if (data.user_id === dm?.other_user?.id) {
        const patch = getProfileMetaPatch(data);
        setLiveOtherUser((prev) => prev ? { ...prev, ...patch } : prev);
        setDmInfo((prev) => (
          prev?.other_user
            ? { ...prev, other_user: { ...prev.other_user, ...patch } }
            : prev
        ));
      }
    });

    return () => {
      unsubNewMessage();
      unsubEdited();
      unsubDeleted?.();
      unsubReaction?.();
      unsubSeen?.();
      unsubDelivered?.();
      unsubProfileUpdated?.();
    };
  }, [dm?.id, dm?.other_user?.id, onNewDmMessage, onDmMessageEdited, onDmMessageDeleted, onDmReactionUpdated, onDmMessagesSeen, onDmMessagesDelivered, onUserProfileUpdated, connected, user?.id, mergeDmMessages, withoutLocallyDeletedMessages, applyServerReactionUpdate, onConversationMessage, onConversationMessageEdited, onConversationMessageDeleted, isDmViewed]);

  // Smart scroll
  const initialScrollDoneRef = useRef(false);
  useEffect(() => {
    if (dm?.id) initialScrollDoneRef.current = false;
  }, [dm?.id]);

  useEffect(() => {
    if (messages.length === 0) return;
    const el = messagesContainerRef.current;
    const pendingRestore = prependDmScrollRestoreRef.current;

    if (pendingRestore && el) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight - pendingRestore.previousHeight + pendingRestore.previousTop;
        prependDmScrollRestoreRef.current = null;
      });
      return;
    }

    if (!initialScrollDoneRef.current) {
      if (el) el.scrollTop = el.scrollHeight;
      initialScrollDoneRef.current = true;
    } else {
      if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 150) {
        if (composerFocusedRef.current) {
          el.scrollTop = el.scrollHeight;
        } else {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [messages]);

  const handleTyping = useCallback((e) => {
    authorizeAndMarkRef.current?.('composer-typing');
    const value = e.target.value;
    setNewMessage(value);
    if (!dm?.id || !user?.username) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (!value.trim()) {
      if (typingActiveRef.current) emitDmTypingStop(dm.id);
      typingActiveRef.current = false;
      lastTypingEmitRef.current = 0;
      return;
    }

    const now = Date.now();
    if (!typingActiveRef.current || now - lastTypingEmitRef.current >= TYPING_START_EMIT_INTERVAL_MS) {
      emitDmTypingStart(dm.id, user.username);
      typingActiveRef.current = true;
      lastTypingEmitRef.current = now;
    }
    typingTimeoutRef.current = setTimeout(() => {
      emitDmTypingStop(dm.id);
      typingActiveRef.current = false;
      lastTypingEmitRef.current = 0;
      typingTimeoutRef.current = null;
    }, 2000);
  }, [dm?.id, user?.username, emitDmTypingStart, emitDmTypingStop]);

  const flushDmSendQueue = useCallback(async () => {
    if (isFlushingDmSendQueueRef.current) return;

    isFlushingDmSendQueueRef.current = true;

    try {
      while (dmSendQueueRef.current.length > 0) {
        const item = dmSendQueueRef.current[0];

        try {
          logDmSendTiming('post_start', item, item.startedAt, {
            queue_depth: dmSendQueueRef.current.length,
          });
          const response = await axios.post(item.url, item.payload, { withCredentials: true });
          logDmSendTiming('http_response', item, item.startedAt, {
            status: response.status,
          });
          const confirmedMessage = normalizeMessageForState({
            ...response.data,
            client_temp_id: response.data.client_temp_id || item.tempId,
            pending: false,
            failed: false,
            localEcho: true,
          });

          if (currentDmRef.current === item.dmId) {
            setMessages((prev) => mergeDmMessages(prev, [confirmedMessage]));
            onConversationMessage?.(confirmedMessage, { clearUnread: true });
            if (item.payload?.message_type === 'image') {
              delete imageDraftsRef.current[item.tempId];
            }
            requestAnimationFrame(() => {
              logDmSendTiming('merge_painted', item, item.startedAt, {
                message_id: confirmedMessage.id,
              });
            });
          }
        } catch (error) {
          console.error('Send DM error:', error);
          logDmSendTiming('request_failed', item, item.startedAt, {
            status: error?.response?.status,
          });

          if (currentDmRef.current === item.dmId) {
            setMessages((prev) => prev.map((message) => (
              message.tempId === item.tempId || message.id === item.tempId
                ? {
                    ...message,
                    pending: false,
                    failed: true,
                    status: ['delivered', 'seen'].includes(message.status) ? message.status : 'sent',
                  }
                : message
            )));
          }
        } finally {
          dmSendQueueRef.current.shift();
        }
      }
    } finally {
    isFlushingDmSendQueueRef.current = false;
    }
  }, [mergeDmMessages, onConversationMessage]);

  const markVoiceMessageFailed = useCallback((tempId, patch = {}) => {
    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            pending: false,
            failed: true,
            status: ['delivered', 'seen'].includes(message.status) ? message.status : 'sent',
            voice_uploading: false,
            ...patch,
          }
        : message
    )));
  }, []);

  const markImageMessageFailed = useCallback((tempId, patch = {}) => {
    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            pending: false,
            failed: true,
            status: ['delivered', 'seen'].includes(message.status) ? message.status : 'sent',
            image_uploading: false,
            ...patch,
          }
        : message
    )));
  }, []);

  const removeLocalImageMessage = useCallback((message) => {
    const tempId = message?.client_temp_id || message?.tempId || message?.id;
    if (!tempId) return;
    const draft = imageDraftsRef.current[tempId];
    if (draft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
    delete imageDraftsRef.current[tempId];
    const videoDraft = videoDraftsRef.current[tempId];
    if (videoDraft?.abort) { try { videoDraft.abort.abort(); } catch {} }
    if (videoDraft?.localUrl?.startsWith('blob:')) URL.revokeObjectURL(videoDraft.localUrl);
    delete videoDraftsRef.current[tempId];
    setMessages((prev) => prev.filter((item) => !isSameMessageIdentity(item, tempId)));
  }, []);

  const enqueueUploadedImageMessage = useCallback((tempId, uploadedUrl, replyToId) => {
    if (!uploadedUrl || !dm?.id || !user?.id) return;
    if (dmSendQueueRef.current.some((item) => item.tempId === tempId)) return;

    const draft = imageDraftsRef.current[tempId];
    if (draft) {
      draft.uploadedUrl = uploadedUrl;
      if (draft.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      draft.localUrl = null;
    }

    const startedAt = getSendTimingNow();
    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            content: uploadedUrl,
            image_upload_url: uploadedUrl,
            image_uploading: false,
            image_retry_unavailable: false,
            pending: true,
            failed: false,
            status: 'sent',
          }
        : message
    )));

    dmSendQueueRef.current.push({
      tempId,
      dmId: dm.id,
      startedAt,
      url: `${BACKEND_URL}/api/dms/${dm.id}/messages`,
      payload: { content: uploadedUrl, message_type: 'image', reply_to: replyToId, client_temp_id: tempId },
    });

    flushDmSendQueue();
  }, [dm?.id, flushDmSendQueue, user?.id]);

  const uploadAndQueueImageMessage = useCallback(async (tempId, imageFile, replyToId) => {
    if (!tempId || !imageFile) return;

    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? { ...message, pending: true, failed: false, status: 'sent', image_uploading: true, image_retry_unavailable: false }
        : message
    )));

    try {
      const imageData = await prepareImageForUpload(imageFile);
      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, imageData, {
        withCredentials: true,
        headers: { 'Content-Type': imageData.type || imageFile.type || 'image/jpeg' }
      });
      enqueueUploadedImageMessage(tempId, response.data.url, replyToId);
    } catch (error) {
      console.error('Image upload error:', error);
      markImageMessageFailed(tempId);
    }
  }, [enqueueUploadedImageMessage, markImageMessageFailed]);

  const createPendingImageMessage = useCallback((imageFile) => {
    if (!imageFile || !dm?.id || !user?.id) return;
    authorizeAndMarkRef.current?.('send-image');

    const activeReplyTo = replyTo?.id;
    const startedAt = getSendTimingNow();
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const localUrl = URL.createObjectURL(imageFile);
    imageDraftsRef.current[tempId] = { file: imageFile, localUrl, replyTo: activeReplyTo };

    const tempMessage = {
      id: tempId,
      tempId,
      dm_id: dm.id,
      sender_id: user.id,
      sender_username: user.username,
      sender_profile_picture_thumb: user.profile_picture_thumb,
      content: localUrl,
      message_type: 'image',
      reply_to: activeReplyTo,
      client_temp_id: tempId,
      created_at: new Date().toISOString(),
      reactions: {},
      status: 'sent',
      pending: true,
      localEcho: true,
      image_uploading: true,
    };

    flushSync(() => {
      registerEntryAnimation(getStableMessageKey(tempMessage), 'outgoing-new');
      setMessages((prev) => [...prev, tempMessage]);
      setReplyTo(null);
    });
    onConversationMessage?.(tempMessage, { clearUnread: true });
    logDmSendTiming('image_temp_ui_flushed', tempId, startedAt);
    uploadAndQueueImageMessage(tempId, imageFile, activeReplyTo);
  }, [dm?.id, onConversationMessage, replyTo?.id, uploadAndQueueImageMessage, user?.id, user?.profile_picture_thumb, user?.username]);

  const enqueueUploadedVoiceMessage = useCallback((tempId, uploadedUrl, replyToId) => {
    if (!uploadedUrl || !dm?.id || !user?.id) return;
    if (dmSendQueueRef.current.some((item) => item.tempId === tempId)) return;

    // Free the local recording blob URL now that the server URL is available.
    const vDraft = voiceDraftsRef.current[tempId];
    if (vDraft?.localUrl?.startsWith('blob:')) { URL.revokeObjectURL(vDraft.localUrl); vDraft.localUrl = null; }

    const startedAt = getSendTimingNow();
    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            voice_upload_url: uploadedUrl,
            voice_uploading: false,
            voice_retry_unavailable: false,
            pending: true,
            failed: false,
            status: 'sent',
          }
        : message
    )));

    dmSendQueueRef.current.push({
      tempId,
      dmId: dm.id,
      startedAt,
      url: `${BACKEND_URL}/api/dms/${dm.id}/messages`,
      payload: { content: uploadedUrl, message_type: 'voice', reply_to: replyToId, client_temp_id: tempId },
    });
    logDmSendTiming('voice_queue_enqueued', tempId, startedAt, {
      queue_depth: dmSendQueueRef.current.length,
    });

    flushDmSendQueue();
  }, [dm?.id, flushDmSendQueue, user?.id]);

  const uploadAndQueueVoiceMessage = useCallback(async (tempId, audioBlob, blobType, replyToId) => {
    if (!tempId || !audioBlob) return;

    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? { ...message, pending: true, failed: false, status: 'sent', voice_uploading: true, voice_retry_unavailable: false }
        : message
    )));

    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload/voice`, audioBlob, {
        withCredentials: true,
        headers: { 'Content-Type': blobType || audioBlob.type || 'audio/webm' },
      });
      const uploadedUrl = response.data.url;
      if (voiceDraftsRef.current[tempId]) {
        voiceDraftsRef.current[tempId].uploadedUrl = uploadedUrl;
      }
      enqueueUploadedVoiceMessage(tempId, uploadedUrl, replyToId);
    } catch (error) {
      console.error('Voice upload error:', error);
      setVoiceError(getVoiceUploadErrorMessage(error));
      markVoiceMessageFailed(tempId);
    }
  }, [enqueueUploadedVoiceMessage, markVoiceMessageFailed]);

  const createPendingVoiceMessage = useCallback((audioBlob, blobType) => {
    if (!audioBlob || !dm?.id || !user?.id) return;
    authorizeAndMarkRef.current?.('send-voice');

    const activeReplyTo = replyTo?.id;
    const startedAt = getSendTimingNow();
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const localUrl = URL.createObjectURL(audioBlob);
    voiceDraftsRef.current[tempId] = { blob: audioBlob, blobType, localUrl, replyTo: activeReplyTo };

    const tempMessage = {
      id: tempId,
      tempId,
      dm_id: dm.id,
      sender_id: user.id,
      sender_username: user.username,
      sender_profile_picture_thumb: user.profile_picture_thumb,
      content: localUrl,
      message_type: 'voice',
      reply_to: activeReplyTo,
      client_temp_id: tempId,
      created_at: new Date().toISOString(),
      reactions: {},
      status: 'sent',
      pending: true,
      localEcho: true,
      voice_uploading: true,
    };

    flushSync(() => {
      registerEntryAnimation(getStableMessageKey(tempMessage), 'outgoing-new');
      setMessages((prev) => [...prev, tempMessage]);
      setReplyTo(null);
    });
    onConversationMessage?.(tempMessage, { clearUnread: true });
    logDmSendTiming('voice_temp_ui_flushed', tempId, startedAt);
    uploadAndQueueVoiceMessage(tempId, audioBlob, blobType, activeReplyTo);
  }, [dm?.id, onConversationMessage, replyTo?.id, uploadAndQueueVoiceMessage, user?.id, user?.profile_picture_thumb, user?.username]);

  // ── Video message (mirrors the DM voice flow) ──
  const markVideoMessageFailed = useCallback((tempId, patch = {}) => {
    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            pending: false,
            failed: true,
            status: ['delivered', 'seen'].includes(message.status) ? message.status : 'sent',
            video_uploading: false,
            ...patch,
          }
        : message
    )));
  }, []);

  const enqueueUploadedVideoMessage = useCallback((tempId, uploadedUrl, replyToId) => {
    if (!uploadedUrl || !dm?.id || !user?.id) return;
    if (dmSendQueueRef.current.some((item) => item.tempId === tempId)) return;

    const startedAt = getSendTimingNow();
    const draft = videoDraftsRef.current[tempId];
    if (draft) {
      draft.uploadedUrl = uploadedUrl;
      if (draft.localUrl?.startsWith('blob:')) URL.revokeObjectURL(draft.localUrl);
      draft.localUrl = null;
    }
    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? {
            ...message,
            content: uploadedUrl,
            video_upload_url: uploadedUrl,
            video_uploading: false,
            video_retry_unavailable: false,
            pending: true,
            failed: false,
            status: 'sent',
          }
        : message
    )));

    dmSendQueueRef.current.push({
      tempId,
      dmId: dm.id,
      startedAt,
      url: `${BACKEND_URL}/api/dms/${dm.id}/messages`,
      payload: {
        content: uploadedUrl,
        message_type: 'video',
        reply_to: replyToId,
        client_temp_id: tempId,
        file_size: draft?.file?.size ?? null,
        original_filename: draft?.file?.name ?? null,
        mime_type: draft?.file?.type ?? null,
        duration: draft?.duration ?? null,
        width: draft?.width ?? null,
        height: draft?.height ?? null,
      },
    });

    flushDmSendQueue();
  }, [dm?.id, flushDmSendQueue, user?.id]);

  const uploadAndQueueVideoMessage = useCallback(async (tempId, videoFile, replyToId) => {
    if (!tempId || !videoFile) return;

    // Guard against a double-tap / concurrent retry uploading the same item twice.
    const draft = videoDraftsRef.current[tempId];
    if (draft?.uploading) return;
    const controller = new AbortController();
    if (draft) { draft.uploading = true; draft.abort = controller; }
    // Per-upload progress state (closure, see onUploadProgress throttle below).
    const progressState = { pct: -1, at: 0 };

    setMessages((prev) => prev.map((message) => (
      isSameMessageIdentity(message, tempId)
        ? { ...message, pending: true, failed: false, status: 'sent', video_uploading: true, video_retry_unavailable: false, upload_progress: 0 }
        : message
    )));

    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload/video`, videoFile, {
        withCredentials: true,
        headers: { 'Content-Type': videoFile.type || 'video/mp4' },
        signal: controller.signal,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          // Cap at 99% until the server confirms; the enqueue step marks it done.
          const pct = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
          // Throttle: onUploadProgress fires per network chunk; without this,
          // every chunk triggered a full message-list state update + re-render.
          // Update only when the percent visibly changes, and at most every
          // 150ms mid-upload.
          const now = Date.now();
          if (pct === progressState.pct) return;
          if (pct < 99 && now - progressState.at < 150) return;
          progressState.pct = pct;
          progressState.at = now;
          setMessages((prev) => prev.map((message) => (
            message.tempId === tempId || message.id === tempId || message.client_temp_id === tempId
              ? { ...message, upload_progress: pct }
              : message
          )));
        },
      });
      const uploadedUrl = response.data.url;
      if (videoDraftsRef.current[tempId]) {
        videoDraftsRef.current[tempId].uploadedUrl = uploadedUrl;
        videoDraftsRef.current[tempId].uploading = false;
        videoDraftsRef.current[tempId].abort = null;
      }
      enqueueUploadedVideoMessage(tempId, uploadedUrl, replyToId);
    } catch (error) {
      if (videoDraftsRef.current[tempId]) {
        videoDraftsRef.current[tempId].uploading = false;
        videoDraftsRef.current[tempId].abort = null;
      }
      // A user-initiated cancel/abort is not a failure; the item was already removed.
      if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Video upload error:', error);
      markVideoMessageFailed(tempId);
    }
  }, [enqueueUploadedVideoMessage, markVideoMessageFailed]);

  const createPendingVideoMessage = useCallback((videoFile) => {
    if (!videoFile || !dm?.id || !user?.id) return;
    authorizeAndMarkRef.current?.('send-video');

    const activeReplyTo = replyTo?.id;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const localUrl = URL.createObjectURL(videoFile);
    videoDraftsRef.current[tempId] = { file: videoFile, localUrl, replyTo: activeReplyTo };

    const tempMessage = {
      id: tempId,
      tempId,
      dm_id: dm.id,
      sender_id: user.id,
      sender_username: user.username,
      sender_profile_picture_thumb: user.profile_picture_thumb,
      content: localUrl,
      message_type: 'video',
      reply_to: activeReplyTo,
      client_temp_id: tempId,
      created_at: new Date().toISOString(),
      reactions: {},
      status: 'sent',
      pending: true,
      localEcho: true,
      video_uploading: true,
      file_size: videoFile.size,
      original_filename: videoFile.name || null,
      mime_type: videoFile.type || null,
    };

    registerEntryAnimation(getStableMessageKey(tempMessage), 'outgoing-new');
    setMessages((prev) => [...prev, tempMessage]);
    onConversationMessage?.(tempMessage, { clearUnread: true });
    setReplyTo(null);
    readVideoMetadata(videoFile).then((meta) => {
      if (!meta || (meta.duration == null && meta.width == null)) return;
      setMessages((prev) => prev.map((m) => (
        isSameMessageIdentity(m, tempId) ? { ...m, duration: meta.duration, width: meta.width, height: meta.height } : m
      )));
      if (videoDraftsRef.current[tempId]) Object.assign(videoDraftsRef.current[tempId], meta);
    });
    uploadAndQueueVideoMessage(tempId, videoFile, activeReplyTo);
  }, [dm?.id, onConversationMessage, replyTo?.id, uploadAndQueueVideoMessage, user?.id, user?.profile_picture_thumb, user?.username]);

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      // Count limit: at most 2 videos per selection/batch.
      if (files.length > MAX_VIDEOS_PER_BATCH) {
        window.alert(`You can send up to ${MAX_VIDEOS_PER_BATCH} videos at a time.`);
        return;
      }

      const errors = [];
      const accepted = [];
      let batchBytes = 0;
      for (const file of files) {
        if (!isSupportedVideoFile(file)) {
          errors.push(`${file.name || 'This file'} is not a supported video format.`);
          continue;
        }
        if (file.size === 0) {
          errors.push(`${file.name || 'This file'} is empty and cannot be sent.`);
          continue;
        }
        if (file.size > MAX_VIDEO_SIZE_BYTES) {
          errors.push(`${file.name || 'This video'} is ${formatMb(file.size)}. The maximum allowed size is ${formatMb(MAX_VIDEO_SIZE_BYTES)}.`);
          continue;
        }
        if (batchBytes + file.size > MAX_VIDEO_BATCH_BYTES) {
          errors.push(`Total video size exceeds ${formatMb(MAX_VIDEO_BATCH_BYTES)} per batch.`);
          continue;
        }
        batchBytes += file.size;
        accepted.push(file);
      }

      // Only create optimistic messages (and object URLs) for accepted files.
      accepted.forEach((file) => createPendingVideoMessage(file));

      if (errors.length) window.alert(errors.join('\n'));
    } catch (error) {
      console.error('Video upload error:', error);
      window.alert(getVideoUploadErrorMessage(error));
    } finally {
      if (videoFileInputRef.current) videoFileInputRef.current.value = '';
    }
  };

  const sendMessage = (content, type = 'text') => {
    if (!content.trim() && type === 'text') return;
    if (!dm?.id || !user?.id) return;
    // Read authorization and the exact-boundary POST start before the outgoing
    // send queue, but do not block fast optimistic sending on that request.
    authorizeAndMarkRef.current?.('send-message');

    const startedAt = getSendTimingNow();
    const activeReplyTo = replyTo?.id;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const tempMessage = {
      id: tempId,
      tempId,
      dm_id: dm.id,
      sender_id: user.id,
      sender_username: user.username,
      sender_profile_picture_thumb: user.profile_picture_thumb,
      content,
      message_type: type,
      reply_to: activeReplyTo,
      client_temp_id: tempId,
      created_at: new Date().toISOString(),
      reactions: {},
      status: 'sent',
      pending: true,
      localEcho: true,
    };

    flushSync(() => {
      registerEntryAnimation(getStableMessageKey(tempMessage), 'outgoing-new');
      setMessages((prev) => [...prev, tempMessage]);
      setNewMessage('');
      setReplyTo(null);
    });
    onConversationMessage?.(tempMessage, { clearUnread: true });
    logDmSendTiming('temp_ui_flushed', tempId, startedAt);

    dmSendQueueRef.current.push({
      tempId,
      dmId: dm.id,
      startedAt,
      url: `${BACKEND_URL}/api/dms/${dm.id}/messages`,
      payload: { content, message_type: type, reply_to: activeReplyTo, client_temp_id: tempId },
    });
    logDmSendTiming('queue_enqueued', tempId, startedAt, {
      queue_depth: dmSendQueueRef.current.length,
    });

    flushDmSendQueue();
    clearScheduledDraft('dm', dm.id);
    saveDraft('dm', dm.id, '');
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (typingActiveRef.current) emitDmTypingStop(dm.id);
    typingActiveRef.current = false;
    lastTypingEmitRef.current = 0;
    focusComposer(composerTextareaRef);
  };

  const retryFailedMessage = useCallback((message) => {
    if (!message || !dm?.id || message.sender_id !== user?.id) return;
    if (message.pending || (!message.failed && message.status !== 'failed')) return;
    authorizeAndMarkRef.current?.('retry-send');

    const tempId = message.client_temp_id || message.tempId || message.id;
    if (!tempId || dmSendQueueRef.current.some((item) => item.tempId === tempId)) return;

    const startedAt = getSendTimingNow();
    if (message.message_type === 'voice') {
      const draft = voiceDraftsRef.current[tempId];
      const uploadedUrl = message.voice_upload_url
        || draft?.uploadedUrl
        || (typeof message.content === 'string' && message.content.startsWith('/api/uploads/voice/') ? message.content : null);

      if (uploadedUrl) {
        enqueueUploadedVoiceMessage(tempId, uploadedUrl, message.reply_to);
        focusComposer(composerTextareaRef);
        return;
      }

      if (draft?.blob) {
        uploadAndQueueVoiceMessage(tempId, draft.blob, draft.blobType || draft.blob.type, message.reply_to || draft.replyTo);
        focusComposer(composerTextareaRef);
        return;
      }

      markVoiceMessageFailed(tempId, { voice_retry_unavailable: true });
      focusComposer(composerTextareaRef);
      return;
    }

    if (message.message_type === 'image') {
      const draft = imageDraftsRef.current[tempId];
      const uploadedUrl = message.image_upload_url
        || draft?.uploadedUrl
        || (typeof message.content === 'string' && message.content.startsWith('/api/uploads/images/') ? message.content : null);

      if (uploadedUrl) {
        enqueueUploadedImageMessage(tempId, uploadedUrl, message.reply_to);
        focusComposer(composerTextareaRef);
        return;
      }

      if (draft?.file) {
        uploadAndQueueImageMessage(tempId, draft.file, message.reply_to || draft.replyTo);
        focusComposer(composerTextareaRef);
        return;
      }

      markImageMessageFailed(tempId, { image_retry_unavailable: true });
      focusComposer(composerTextareaRef);
      return;
    }

    if (message.message_type === 'video') {
      const draft = videoDraftsRef.current[tempId];
      const uploadedUrl = message.video_upload_url
        || draft?.uploadedUrl
        || (typeof message.content === 'string' && message.content.startsWith('/api/uploads/video/') ? message.content : null);

      if (uploadedUrl) {
        enqueueUploadedVideoMessage(tempId, uploadedUrl, message.reply_to);
        focusComposer(composerTextareaRef);
        return;
      }

      if (draft?.file) {
        uploadAndQueueVideoMessage(tempId, draft.file, message.reply_to || draft.replyTo);
        focusComposer(composerTextareaRef);
        return;
      }

      markVideoMessageFailed(tempId, { video_retry_unavailable: true });
      focusComposer(composerTextareaRef);
      return;
    }

    setMessages((prev) => prev.map((item) => (
      isSameMessageIdentity(item, tempId)
        ? {
            ...item,
            id: item.id || tempId,
            tempId: item.tempId || tempId,
            client_temp_id: item.client_temp_id || tempId,
            pending: true,
            failed: false,
            status: 'sent',
          }
        : item
    )));

    dmSendQueueRef.current.push({
      tempId,
      dmId: dm.id,
      startedAt,
      url: `${BACKEND_URL}/api/dms/${dm.id}/messages`,
      payload: {
        content: message.content || '',
        message_type: message.message_type || 'text',
        reply_to: message.reply_to,
        client_temp_id: tempId,
      },
    });
    logDmSendTiming('retry_enqueued', tempId, startedAt, {
      queue_depth: dmSendQueueRef.current.length,
    });

    flushDmSendQueue();
    focusComposer(composerTextareaRef);
  }, [dm?.id, enqueueUploadedImageMessage, enqueueUploadedVoiceMessage, enqueueUploadedVideoMessage, flushDmSendQueue, markImageMessageFailed, markVoiceMessageFailed, markVideoMessageFailed, uploadAndQueueImageMessage, uploadAndQueueVoiceMessage, uploadAndQueueVideoMessage, user?.id]);


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitComposer();
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      const valid = [];
      const rejected = [];
      for (const file of files) {
        const isImage = file.type?.startsWith('image/') || isLikelyHeicImage(file);
        if (isImage) valid.push(file); else rejected.push(file.name || 'file');
      }

      let capped = false;
      let toSend = valid;
      if (valid.length > MAX_MEDIA_SELECTION) {
        toSend = valid.slice(0, MAX_MEDIA_SELECTION);
        capped = true;
      }

      // Send in displayed order; each becomes its own optimistic image message
      // with a unique client_temp_id (the existing FIFO/upload flow handles them).
      toSend.forEach((file) => createPendingImageMessage(file));

      if (rejected.length || capped) {
        const parts = [];
        if (rejected.length) parts.push(`${rejected.length} file(s) skipped (not a supported image).`);
        if (capped) parts.push(`Only the first ${MAX_MEDIA_SELECTION} photos were added.`);
        window.alert(parts.join(' '));
      }
    } catch (error) {
      console.error('Upload error:', error);
      window.alert(getImageUploadErrorMessage(error));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startRecording = async () => {
    authorizeAndMarkRef.current?.('voice-recording');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setVoiceError('Voice recording is not supported in this browser.');
      return;
    }

    let stream;
    try {
      setVoiceError('');
      stream = await getVoiceRecordingStream();
      recordingStreamRef.current = stream;
      const { recorder, mimeType } = createVoiceRecorder(stream);

      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      cancelRecordingRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        mediaRecorderRef.current = null;
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
        setRecordingTime(0);

        if (cancelRecordingRef.current) {
          cancelRecordingRef.current = false;
          audioChunksRef.current = [];
          return;
        }

        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        const blobType = recorder.mimeType || mimeType || chunks[0]?.type || 'audio/webm';
        const audioBlob = new Blob(chunks, { type: blobType });
        if (!audioBlob.size) {
          setVoiceError('No voice audio was captured. Please try again.');
          return;
        }

        createPendingVoiceMessage(audioBlob, blobType);
      };

      recorder.start(250);
      setIsRecording(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Recording error:', error);
      stream?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      setVoiceError(getVoiceRecordingErrorMessage(error));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      cancelRecordingRef.current = false;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      cancelRecordingRef.current = true;
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
      setRecordingTime(0);
      setIsRecording(false);
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
  };

  const addEmoji = (emoji) => {
    const selectedEmoji = emoji?.native || '';
    if (!selectedEmoji) return;
    setNewMessage(prev => prev + selectedEmoji);
    requestAnimationFrame(() => composerTextareaRef.current?.focus());
  };

  const handleReply = useCallback((message) => {
    authorizeAndMarkRef.current?.('reply');
    if (editingMessageRef.current) {
      setEditingMessage(null);
      setNewMessage(editDraftBeforeRef.current || '');
    }
    setReplyTo(message);
    focusComposer(composerTextareaRef);
  }, []);

  const handleStartEdit = useCallback((message) => {
    if (!message?.id || isTempMessageId(message.id) || message.message_type !== 'text') return;
    if (!editingMessageRef.current) {
      editDraftBeforeRef.current = newMessageRef.current;
    }
    setReplyTo(null);
    setShowEmojiPicker(false);
    setEditingMessage(message);
    setNewMessage(message.content || '');
    focusComposer(composerTextareaRef);
  }, []);

  const handleDeleteMessage = useCallback(async (messageId) => {
    const deletedMessage = messagesRef.current.find((message) => getMessageIdentityKeys(message).includes(messageId) || message.id === messageId);
    if (!deletedMessage) return;

    const deletedKeys = rememberDeletedMessage(deletedMessageIdsRef, deletedMessage);
    setMessages((prev) => withoutLocallyDeletedMessages(prev.filter((message) => !getMessageIdentityKeys(message).includes(messageId))));
    onConversationMessageDeleted?.({ dm_id: dm.id, message_id: messageId, reconcile: false });
    removeMessageFromCache(`whisper_dm_messages_${dm.id}`, messageId);
    try {
      await axios.delete(`${BACKEND_URL}/api/dms/${dm.id}/messages/${messageId}`, { withCredentials: true });
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404 || status === 410) {
        setMessages((prev) => withoutLocallyDeletedMessages(prev.filter((message) => !getMessageIdentityKeys(message).includes(messageId))));
        removeMessageFromCache(`whisper_dm_messages_${dm.id}`, messageId);
        return;
      }
      console.error('Delete DM message error:', error);
      forgetDeletedMessage(deletedMessageIdsRef, deletedKeys);
      setMessages((prev) => mergeDmMessages(prev, [deletedMessage]));
      onConversationMessage?.(deletedMessage, { clearUnread: true });
    }
  }, [dm?.id, mergeDmMessages, withoutLocallyDeletedMessages, onConversationMessage, onConversationMessageDeleted]);

  const handleEditMessage = useCallback(async (messageId, content) => {
    const originalMessage = messagesRef.current.find((message) => message.id === messageId);
    if (!originalMessage) return;

    setMessages((prev) => prev.map((message) => (
      message.id === messageId
        ? { ...message, content, edited: true, edited_at: new Date().toISOString() }
        : message
    )));
    onConversationMessageEdited?.({
      id: messageId,
      dm_id: dm.id,
      content,
      edited_at: new Date().toISOString(),
    });

    try {
      await axios.put(`${BACKEND_URL}/api/dms/${dm.id}/messages/${messageId}`, { content }, { withCredentials: true });
    } catch (error) {
      console.error('Edit DM message error:', error);
      setMessages((prev) => prev.map((message) => (
        message.id === messageId
          ? originalMessage
          : message
      )));
      onConversationMessage?.(originalMessage, { clearUnread: true });
    }
  }, [dm?.id, onConversationMessageEdited, onConversationMessage]);

  const cancelComposerEdit = useCallback(() => {
    setEditingMessage(null);
    setNewMessage(editDraftBeforeRef.current || '');
    focusComposer(composerTextareaRef);
  }, []);

  const submitComposer = useCallback(() => {
    if (editingMessage) {
      const nextContent = newMessage.trim();
      const draftBeforeEdit = editDraftBeforeRef.current || '';
      if (!nextContent) return;
      if (nextContent !== editingMessage.content) {
        handleEditMessage(editingMessage.id, nextContent);
      }
      setEditingMessage(null);
      setNewMessage(draftBeforeEdit);
      clearScheduledDraft('dm', dm.id);
      saveDraft('dm', dm.id, draftBeforeEdit);
      focusComposer(composerTextareaRef);
      return;
    }
    sendMessage(newMessage);
  }, [dm?.id, editingMessage, handleEditMessage, newMessage, sendMessage]);

  const handleReaction = useCallback(async (messageId, emoji) => {
    if (!messageId || isTempMessageId(messageId)) return;
    authorizeAndMarkRef.current?.('message-reaction');

    const originalMessage = messagesRef.current.find((message) => message.id === messageId);
    if (!originalMessage) return;
    if (!canToggleReaction(originalMessage.reactions, emoji, user?.id)) return;

    const action = getReactionAction(originalMessage.reactions, emoji, user?.id);
    const clientReactionId = createReactionRequestId();
    const requestKey = getReactionRequestKey(messageId, emoji);
    latestReactionRequestRef.current[requestKey] = clientReactionId;

    setMessages((prev) => applyOptimisticReactionUpdate(prev, messageId, emoji, user?.id));

    try {
      const previousRequest = reactionRequestChainsRef.current[requestKey] || Promise.resolve();
      const requestPromise = previousRequest.catch(() => {}).then(() => axios.post(
          `${BACKEND_URL}/api/dms/${dm.id}/messages/${messageId}/reaction`,
          { emoji, action, client_reaction_id: clientReactionId },
          { withCredentials: true }
        ));

      const chainPromise = requestPromise.finally(() => {
        if (reactionRequestChainsRef.current[requestKey] === chainPromise) {
          delete reactionRequestChainsRef.current[requestKey];
        }
      });
      reactionRequestChainsRef.current[requestKey] = chainPromise;

      const response = await requestPromise;
      applyServerReactionUpdate(response.data);
    } catch (error) {
      console.error('DM Reaction error:', error);
      if (latestReactionRequestRef.current[requestKey] !== clientReactionId) return;
      delete latestReactionRequestRef.current[requestKey];
      setMessages((prev) => prev.map((message) => (
        message.id === messageId ? { ...message, reactions: originalMessage.reactions } : message
      )));
    }
  }, [dm?.id, user?.id, applyServerReactionUpdate]);

  const typingUser = dmTypingUsers[dm?.id];
  // WhatsApp-style handoff — see the channel-side comment.
  const lastLiveMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const typingHandedOff = Boolean(
    typingUser && lastLiveMessage && lastLiveMessage.sender_id === typingUser.user_id
    && typeof lastLiveMessage._realtime_received_at === 'number'
    && Date.now() - lastLiveMessage._realtime_received_at < 1500
  );
  const showTypingIndicator = Boolean(typingUser && typingUser.user_id !== user?.id && !typingHandedOff);
  const formatTime = (seconds) => `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  const otherUser = overlayUserPresence(
    liveOtherUser || dm?.other_user || dmInfo?.other_user,
    presenceByUser
  );
  const isOtherUserOnline = Boolean(otherUser?.is_online);
  const senderMetaById = useMemo(() => {
    const map = new Map();
    if (user?.id) {
      map.set(user.id, {
        username: user.username,
        profile_picture_thumb: user.profile_picture_thumb,
      });
    }
    if (otherUser?.id) {
      map.set(otherUser.id, {
        username: otherUser.username,
        profile_picture_thumb: otherUser.profile_picture_thumb,
        avatar_thumb: otherUser.avatar_thumb,
        thumbnail_url: otherUser.thumbnail_url,
      });
    }
    return map;
  }, [otherUser?.avatar_thumb, otherUser?.id, otherUser?.profile_picture_thumb, otherUser?.thumbnail_url, otherUser?.username, user?.id, user?.profile_picture_thumb, user?.username]);

  const focusComposerCb = useCallback(() => focusComposer(composerTextareaRef), []);
  const shouldPreserveComposerFocusCb = useCallback(() => composerFocusedRef.current, []);

  // Resolve a message's reply_to (immutable original-message id) -> original message.
  const messagesById = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (m?.id) map.set(m.id, m);
      if (m?.client_temp_id) map.set(m.client_temp_id, m);
      if (m?.tempId) map.set(m.tempId, m);
    });
    return map;
  }, [messages]);
  const resolveReplyTarget = useCallback((message) => {
    const refId = message?.reply_to;
    if (!refId) return null;
    // A deleted original: show "was deleted" (distinct from generally unavailable).
    // deletedMessageIdsRef is the authoritative deleted-id set and wins over cache.
    // Sentinel constants keep the returned object identity stable across renders
    // so memoized bubbles don't re-render for no reason.
    if (deletedMessageIdsRef.current?.has(String(refId))) return DELETED_REPLY_TARGET;
    return messagesById.get(refId) || UNAVAILABLE_REPLY_TARGET;
  }, [messagesById]);

  // ── Reply-to-original navigation (scroll + highlight) ──
  const messageRefs = useRef(new Map());
  const setMessageRef = useCallback((messageId, element) => {
    if (!messageId) return;
    const key = String(messageId);
    if (element) messageRefs.current.set(key, element);
    else messageRefs.current.delete(key);
  }, []);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const highlightTimeoutRef = useRef(null);
  const navRunIdRef = useRef(0);
  const contextFetchInProgressRef = useRef(new Set()); // dedupe repeat taps on the same target
  const [replyNavTargetId, setReplyNavTargetId] = useState(null); // shows "finding…" on that quote
  useEffect(() => () => { if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current); }, []);

  const highlightMessage = useCallback((messageId) => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    setHighlightedMessageId(String(messageId));
    highlightTimeoutRef.current = setTimeout(() => setHighlightedMessageId(null), 1800);
  }, []);

  const scrollToMessage = useCallback((messageId) => {
    const el = messageRefs.current.get(String(messageId));
    if (!el) return false;
    const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    } catch {
      el.scrollIntoView();
    }
    highlightMessage(messageId);
    return true;
  }, [highlightMessage]);

  const navigateToOriginalMessage = useCallback(async (messageId) => {
    if (!messageId) return;
    const targetKey = String(messageId);
    // Already loaded? Scroll immediately.
    if (messageRefs.current.get(targetKey)) { scrollToMessage(targetKey); return; }
    // Optimistic/local reply targets have no server id to fetch context for.
    if (isTempMessageId(targetKey)) { toast?.('Original message is unavailable.'); return; }
    // Dedupe rapid repeat taps on the same unloaded target.
    if (contextFetchInProgressRef.current.has(targetKey)) return;

    const runId = ++navRunIdRef.current;
    const activeDmId = dm?.id;
    contextFetchInProgressRef.current.add(targetKey);
    setReplyNavTargetId(targetKey);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/dms/${activeDmId}/messages/${targetKey}/context`,
        { withCredentials: true, params: { radius: MESSAGE_PAGE_SIZE } }
      );
      // Superseded (another nav started) or conversation switched: drop silently.
      if (navRunIdRef.current !== runId || currentDmRef.current !== activeDmId) return;

      const fetched = Array.isArray(response.data?.messages) ? response.data.messages : [];
      if (fetched.length) {
        const container = messagesContainerRef.current;
        if (container) {
          prependDmScrollRestoreRef.current = {
            previousHeight: container.scrollHeight,
            previousTop: container.scrollTop,
          };
        }
        setMessages((prev) => withoutLocallyDeletedMessages(
          sortMessagesForDisplay(mergeDmMessages(prev, sortMessagesForDisplay(fetched)))
        ));
      }

      // Let the merged messages render + register refs, then scroll+highlight.
      await new Promise((r) => setTimeout(r, 80));
      if (navRunIdRef.current === runId && currentDmRef.current === activeDmId) {
        if (!scrollToMessage(targetKey)) toast?.('Original message is unavailable.');
      }
    } catch (error) {
      // 404/410 (deleted / no longer in convo) or 403 (no access): unavailable.
      if (navRunIdRef.current === runId) toast?.('Original message is unavailable.');
    } finally {
      contextFetchInProgressRef.current.delete(targetKey);
      if (navRunIdRef.current === runId) setReplyNavTargetId(null);
    }
  }, [dm?.id, scrollToMessage, toast, mergeDmMessages, withoutLocallyDeletedMessages]);


  // Precompute everything derived from (messages, index) once per messages
  // change (see the channel-area counterpart): date parsing, grouping, meta
  // visibility, time/status labels and reply resolution. Memoized bubbles then
  // receive stable primitive/identity props and skip re-rendering.
  const messageRows = useMemo(() => messages.map((message, index) => {
    const isGrouped = isSameMessageGroup(messages[index - 1], message);
    const showDateDivider = shouldShowDateDivider(messages, index);
    return {
      key: getStableMessageKey(message),
      message,
      index,
      showDateDivider,
      isGrouped,
      showAvatar: !isGrouped,
      showMeta: shouldShowMessageMeta(messages, index),
      timeLabel: formatMessageTime(message.created_at),
      statusLabel: getMessageStatusLabel(message),
      dateDividerLabel: showDateDivider ? formatMessageDateDivider(message.created_at) : null,
      replyTarget: resolveReplyTarget(message),
    };
  }), [messages, resolveReplyTarget]);


  if (!dm) return null;

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-surface-0 overflow-hidden">
      {/* Header - pinned */}
      <div className="chat-header-safe shrink-0 sticky top-0 z-20 border-b border-border-subtle bg-surface-1/95 px-2 md:px-5 md:py-3 backdrop-blur-xl flex items-center gap-2">
        <button onClick={onBack} className="md:hidden ui-icon-btn ui-icon-btn-lg shrink-0" aria-label="Back" data-testid="dm-back-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <Avatar src={otherUser?.profile_picture_thumb || otherUser?.avatar_thumb || otherUser?.thumbnail_url || otherUser?.profile_picture} username={otherUser?.username} size="md" isOnline={isOtherUserOnline} />
        <div className="ml-1 min-w-0 flex-1">
          <h2 className="font-semibold text-[15px] text-white font-heading tracking-tight md:text-base truncate leading-tight">{otherUser?.username}</h2>
          <p className="text-[12px] leading-tight truncate mt-0.5">
            {typingUser && typingUser.user_id !== user?.id ? (
              <span className="text-cyan-300">typing…</span>
            ) : isOtherUserOnline ? (
              <span className="text-emerald-400">Online</span>
            ) : (
              <span className="text-zinc-500">Last seen {formatLastSeen(otherUser?.last_seen, presenceNow)}</span>
            )}
          </p>
        </div>
      </div>

      {/* Messages - only this scrolls */}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        onPointerDown={handleMessagesBackgroundPointerDown}
        onPointerMove={handleMessagesBackgroundPointerMove}
        onPointerUp={handleMessagesBackgroundPointerUp}
        onPointerCancel={handleMessagesBackgroundPointerCancel}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain hide-scrollbar"
        data-testid="dm-messages-container"
      >
        <ChatLayoutTransition
          enabled={MESSAGE_ANIMATIONS_ENABLED}
          conversationId={dm?.id}
          messages={messages}
          showTyping={showTypingIndicator}
          scrollerRef={messagesContainerRef}
          entryRefs={flipEntryRefs}
          typingEntryRef={flipTypingEntryRef}
          composerGetter={() => composerContainerRef.current}
        >
        <div className="relative flex min-h-full flex-col justify-end px-3 py-3 md:p-4">
          {isLoadingOlder && messages.length > 0 && <OlderMessagesLoader />}
          {isMessagesLoading && messages.length === 0 ? (
            <MessagesLoadingSkeleton />
          ) : messages.length === 0 ? (
            <EmptyConversationState
              title="No messages yet"
              subtitle={`Say hi to ${otherUser?.username || 'them'} and start the conversation.`}
            />
          ) : (
            messageRows.map(({ key, message, index, showDateDivider, isGrouped, showAvatar, showMeta, timeLabel, statusLabel, dateDividerLabel, replyTarget }) => (
              <React.Fragment key={key}>
                {showDateDivider && <DateDivider label={dateDividerLabel} />}
                <div
                  ref={(el) => setMessageRef(message.id, el)}
                  data-message-id={message.id}
                  data-chat-layout-row={key}
                  className={`${index === 0 ? 'mt-0' : showDateDivider ? 'mt-1.5' : isGrouped ? 'mt-0.5' : 'mt-2.5 md:mt-3.5'}${highlightedMessageId && String(message.id) === highlightedMessageId ? ' reply-target-highlight' : ''}`}
                >
                  <div
                    className="msg-entry"
                    ref={(el) => {
                      if (el) flipEntryRefs.current.set(key, el);
                      else flipEntryRefs.current.delete(key);
                    }}
                  >
                  <MessageBubble
                    message={message}
                    isOwn={message.sender_id === user?.id}
                    showAvatar={showAvatar}
                    isGrouped={isGrouped}
                    showMeta={showMeta}
                    timeLabel={timeLabel}
                    statusLabel={statusLabel}
                    onReply={handleReply}
                    onDelete={handleDeleteMessage}
                    onEdit={handleStartEdit}
                    onReact={handleReaction}
                    onRetry={retryFailedMessage}
                    onCancelLocal={removeLocalImageMessage}
                    onFocusComposer={focusComposerCb}
                    onShouldPreserveComposerFocus={shouldPreserveComposerFocusCb}
                    senderMeta={senderMetaById.get(message.sender_id)}
                    replyToMessage={replyTarget}
                    onQuoteClick={navigateToOriginalMessage}
                    currentUserId={user?.id}
                  />
                  </div>
                </div>
              </React.Fragment>
            ))
          )}
          {showTypingIndicator && (
            <div data-chat-layout-row={TYPING_ROW_KEY} className="mt-3" aria-live="polite">
              <div className="typing-entry flex items-center gap-2" ref={flipTypingEntryRef}>
                <div className="inline-flex items-center gap-1.5 rounded-[20px] rounded-tl-md bg-surface-2 px-3.5 py-2.5 shadow-elev-sm ring-1 ring-inset ring-white/[0.04]">
                  <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-zinc-500">{typingUser.username} is typing</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>{/* end bottom-align inner wrapper */}
        </ChatLayoutTransition>
      </div>

      {editingMessage && (
        <div className="shrink-0 border-t border-border-subtle bg-surface-1 px-3.5 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <Edit2 className="w-4 h-4 text-amber-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-amber-300">Editing message</p>
              <p className="max-w-[240px] truncate text-[12px] text-zinc-400">{editingMessage.content}</p>
            </div>
          </div>
          <button type="button" onPointerDown={preventComposerBlur} onMouseDown={preventComposerBlur} onClick={cancelComposerEdit} className="ui-icon-btn ui-icon-btn-sm" aria-label="Cancel edit">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Reply bar - pinned */}
      {!editingMessage && replyTo && (
        <div className="reply-composer-enter shrink-0 border-t border-border-subtle bg-surface-1 px-3.5 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <Reply className="w-4 h-4 text-cyan-300 shrink-0" />
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-cyan-300">Replying to {replyTo.sender_username}</p>
              <p className="max-w-[240px] truncate text-[12px] text-zinc-400">
                <MediaPreview messageType={replyTo.message_type} fallbackText={replyTo.content} />
              </p>
            </div>
          </div>
          <button type="button" onPointerDown={preventComposerBlur} onMouseDown={preventComposerBlur} onClick={() => setReplyTo(null)} className="ui-icon-btn ui-icon-btn-sm" aria-label="Cancel reply">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Composer - pinned at bottom */}
      <div
        ref={composerContainerRef}
        className="composer-bar shrink-0 border-t border-border-subtle bg-surface-1/95 p-2.5 backdrop-blur-xl"
        style={{ paddingBottom: 'calc(0.625rem + var(--whisper-kb-inset, env(safe-area-inset-bottom)))' }}
      >
        {voiceError && !isRecording && (
          <div className="mb-2 rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-200 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" /> {voiceError}
          </div>
        )}
        {isRecording ? (
          <div className="flex items-center gap-3 rounded-[22px] bg-surface-2 px-4 py-2.5 ring-1 ring-inset ring-red-500/20">
            <div className="flex-1 flex items-center gap-3">
              <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
              <span className="text-white font-mono text-sm tabular-nums">{formatTime(recordingTime)}</span>
              <span className="text-zinc-400 text-sm">Recording…</span>
            </div>
            <button onClick={cancelRecording} className="ui-icon-btn ui-icon-btn-md" title="Cancel recording" aria-label="Cancel recording" data-testid="dm-cancel-recording-btn">
              <Trash2 className="w-5 h-5" />
            </button>
            <button onClick={stopRecording} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gradient shadow-elev-sm transition-transform active:scale-95" title="Send recording" aria-label="Send recording" data-testid="dm-stop-recording-btn">
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>
        ) : (
          <div className="relative flex items-end gap-2">
            {showEmojiPicker && (
              <div ref={composerEmojiPickerRef} className="absolute bottom-full left-0 z-40 mb-2">
                <Suspense fallback={<div className="h-[360px] w-[352px] max-w-[90vw] rounded-xl bg-surface-2 ui-skeleton" />}>
                  <LazyEmojiPicker onEmojiSelect={addEmoji} theme="dark" previewPosition="none" skinTonePosition="none" />
                </Suspense>
              </div>
            )}
            <div className="composer-shell">
              <div className="flex shrink-0 self-end items-center gap-0.5 p-1.5">
                <button ref={composerEmojiButtonRef} onClick={() => setShowEmojiPicker((prev) => !prev)} className="ui-icon-btn ui-icon-btn-sm" aria-label="Emoji">
                  <Smile className="w-5 h-5" />
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowAttachMenu((prev) => !prev)}
                    disabled={isUploading}
                    className="ui-icon-btn ui-icon-btn-sm"
                    aria-label="Attach media"
                    aria-haspopup="menu"
                    aria-expanded={showAttachMenu}
                    data-testid="dm-attach-btn"
                  >
                    {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                  </button>
                  <AnimatePresence>
                    {showAttachMenu && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setShowAttachMenu(false)} aria-hidden="true" />
                        <motion.div
                          initial={{ opacity: 0, y: 6, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 6, scale: 0.97 }}
                          transition={{ duration: 0.14, ease: 'easeOut' }}
                          role="menu"
                          className="ui-popover absolute bottom-full left-0 z-40 mb-2 w-40 p-1.5"
                        >
                          <button type="button" role="menuitem" onClick={() => { setShowAttachMenu(false); fileInputRef.current?.click(); }} className="ui-menu-item" data-testid="dm-attach-photos">
                            <Image className="w-4 h-4 text-zinc-400" /> Photos
                          </button>
                          <button type="button" role="menuitem" onClick={() => { setShowAttachMenu(false); videoFileInputRef.current?.click(); }} className="ui-menu-item" data-testid="dm-attach-videos">
                            <Video className="w-4 h-4 text-zinc-400" /> Videos
                          </button>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
                <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" multiple className="hidden" />
                <input type="file" ref={videoFileInputRef} onChange={handleVideoUpload} accept="video/mp4,video/webm,video/quicktime" multiple className="hidden" />
              </div>
              <textarea
                ref={composerTextareaRef}
                value={newMessage}
                onChange={handleTyping}
                onKeyDown={handleKeyPress}
                onFocus={handleComposerFocus}
                onBlur={handleComposerBlur}
                placeholder={editingMessage ? 'Edit message…' : 'Type a message…'}
                rows={1}
                className="composer-textarea max-h-32 flex-1 self-end resize-none px-1.5 py-3 text-base leading-6 text-white placeholder:text-zinc-500"
                data-testid="dm-message-input"
              />
            </div>
            {newMessage.trim() || editingMessage ? (
              <button
                type="button"
                onPointerDown={preventComposerBlur}
                onMouseDown={preventComposerBlur}
                onClick={submitComposer}
                disabled={!!editingMessage && !newMessage.trim()}
                className="flex h-12 w-12 shrink-0 self-end items-center justify-center rounded-full bg-brand-gradient shadow-elev-sm transition-transform duration-150 active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                aria-label="Send message"
                data-testid="dm-send-btn"
              >
                <Send className="w-5 h-5 text-white" />
              </button>
            ) : (
              <button onClick={startRecording} disabled={isUploading} className="flex h-12 w-12 shrink-0 self-end items-center justify-center rounded-full bg-surface-3 transition-colors hover:bg-zinc-700 active:scale-95 disabled:opacity-50" aria-label="Record voice message">
                <Mic className="w-5 h-5 text-zinc-300" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Chat Page
const ChatPage = () => {
  useWhisperViewportHeight();
  // On unmount (logout), drop any pending debounced draft writes instead of
  // flushing them: logout wipes whisper_* storage, so a flushed stale draft
  // would resurrect the previous account's data.
  useEffect(() => () => {
    cancelPendingDraftSaves();
  }, []);
  const { user, logout, refreshUser } = useAuth();
  // Scope all localStorage caches to this account's immutable id before any
  // cached lists/messages are read or written this session.
  if (typeof window !== 'undefined') setCacheUserId(user?.id || null);
  const {
    connected,
    authenticated,
    joinDm,
    setTotalUnreadCount,
    onNewMessage,
    onMessageEdited,
    onMessageDeleted,
    onNewDmMessage,
    onDmMessageEdited,
    onDmMessageDeleted,
    onDmUnreadUpdate,
    onDmCreated,
    onDmMessagesSeen,
    onDmMessagesDelivered,
    onUserProfileUpdated,
    presenceByUser,
    noteUserOnline,
  } = useSocket();
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedDm, setSelectedDm] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showNewDmModal, setShowNewDmModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [activeTab, setActiveTab] = useState('channels'); // 'channels' or 'dms'
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [dmReadActivation, setDmReadActivation] = useState({ dmId: null, seq: 0 });
  const profileInputRef = useRef(null);
  const unreadClearUntilRef = useRef({ channel: {}, dm: {} });
  const syncInFlightRef = useRef(null);
  const syncTrailingReasonRef = useRef(null);
  const reconcileAppStateRef = useRef(null);
  const syncGenerationRef = useRef(0);
  const lastForegroundSyncAtRef = useRef(0);
  const dmReadAuthorizationRef = useRef({ dmId: null, seq: 0 });
  const dmsByIdRef = useRef(new Map());
  // Per-DM timestamp of the last realtime unread create/preserve (new_dm_message for
  // an inactive DM, or an inactive dm_unread_update). Used to decide, during a
  // loadDms() merge, whether a lower server unread_count is stale: if realtime unread
  // changed AFTER the request began, the local count is kept; otherwise the server
  // count wins (so cross-device reads still zero it). Never persisted.
  const dmRealtimeUnreadAtRef = useRef({});
  // Canonical IDs, not event counts, are the realtime unread ledger. Socket
  // duplicates therefore cannot increment twice; each DM is bounded in memory.
  const dmRealtimeUnreadIdsRef = useRef({});
  const channelRealtimeUnreadAtRef = useRef({});

  const rememberRealtimeDmUnread = useCallback((message) => {
    if (!message?.dm_id || !message?.id) return false;
    const dmId = String(message.dm_id);
    if (!(dmRealtimeUnreadIdsRef.current[dmId] instanceof Map)) {
      dmRealtimeUnreadIdsRef.current[dmId] = new Map();
    }
    return rememberBoundedRealtimeId(dmRealtimeUnreadIdsRef.current[dmId], message.id, {
      receivedAt: Date.now(),
      boundary: normalizeBoundary(message),
    });
  }, []);

  const getRealtimeUnreadDelta = useCallback((dmId, snapshotBoundary, requestStartedAt) => {
    const ledger = dmRealtimeUnreadIdsRef.current[String(dmId)];
    const serverBoundary = normalizeBoundary(snapshotBoundary);
    let delta = 0;
    if (ledger instanceof Map) {
      ledger.forEach((entry) => {
        if ((entry?.receivedAt || 0) < requestStartedAt) return;
        if (!serverBoundary || compareBoundaries(entry?.boundary, serverBoundary) > 0) delta += 1;
      });
    }
    return delta;
  }, []);

  const getRealtimeUnreadResolution = useCallback((dmId, serverRow, requestStartedAt) => {
    const delta = getRealtimeUnreadDelta(dmId, serverRow?.last_message, requestStartedAt);
    if (delta > 0) return delta;
    return (dmRealtimeUnreadAtRef.current[dmId] || 0) >= requestStartedAt;
  }, [getRealtimeUnreadDelta]);

  const pruneRealtimeUnreadLedger = useCallback((dmId, boundaryValue) => {
    const ledger = dmRealtimeUnreadIdsRef.current[String(dmId)];
    const boundary = normalizeBoundary(boundaryValue);
    if (!(ledger instanceof Map) || !boundary) return;
    ledger.forEach((entry, id) => {
      if (entry?.boundary && compareBoundaries(entry.boundary, boundary) <= 0) ledger.delete(id);
    });
    if (ledger.size === 0) delete dmRealtimeUnreadIdsRef.current[String(dmId)];
  }, []);

  // Refs mirror the current selected DM + sidebar state so the long-lived socket
  // effect reads live values (no stale closure) when deciding if a DM is viewed.
  const selectedDmIdRef = useRef(null);
  const showSidebarRef = useRef(true);
  const activeTabRef = useRef('channels');
  const wasBackgroundedRef = useRef(false);
  const foregroundResumeTokenRef = useRef(0);
  useEffect(() => { selectedDmIdRef.current = selectedDm?.id || null; }, [selectedDm?.id]);
  useEffect(() => { showSidebarRef.current = showSidebar; }, [showSidebar]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => {
    dmsByIdRef.current = new Map(dms.map((row) => [String(row.id), row]));
  }, [dms]);

  const revokeDmReadAuthorization = useCallback(() => {
    const next = { dmId: null, seq: dmReadAuthorizationRef.current.seq };
    dmReadAuthorizationRef.current = next;
    setDmReadActivation(next);
    // A local clear is only a short request-race guard. Never carry it across a
    // background/foreground boundary where authoritative offline unread may exist.
    unreadClearUntilRef.current = { channel: {}, dm: {} };
  }, []);

  const activateDmRead = useCallback((dmId, options = {}) => {
    if (!dmId || !isDocumentActivelyViewed()) return;
    const forceNew = Boolean(options.forceNew);
    if (!forceNew && dmReadAuthorizationRef.current.dmId === dmId) {
      return dmReadAuthorizationRef.current.seq;
    }
    const next = { dmId, seq: dmReadAuthorizationRef.current.seq + 1 };
    dmReadAuthorizationRef.current = next;
    setDmReadActivation(next);
    return next.seq;
  }, []);

  const requestDmReadAuthorization = useCallback((dmId) => activateDmRead(dmId), [activateDmRead]);

  const isDmReadAuthorized = useCallback((dmId, activationSeq) => (
    Boolean(dmId)
    && dmReadAuthorizationRef.current.dmId === dmId
    && dmReadAuthorizationRef.current.seq === activationSeq
  ), []);
  // A DM is "actively viewed" (eligible to be marked read) only when it is the
  // selected DM, the tab is visible + focused, and — on mobile — the sidebar is not
  // covering the chat. Selection alone is NOT enough (desktop shows list + chat side
  // by side; a message to the open DM while the window is unfocused must stay unread).
  const isDmActivelyViewed = useCallback((dmId) => {
    const targetId = dmId != null ? dmId : selectedDmIdRef.current;
    if (!targetId || selectedDmIdRef.current !== targetId) return false;
    if (dmReadAuthorizationRef.current.dmId !== targetId) return false;
    if (!isDocumentActivelyViewed()) return false;
    let isMobile = false;
    try {
      isMobile = typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(max-width: 767px)').matches
        : (typeof window !== 'undefined' && window.innerWidth < 768);
    } catch { isMobile = false; }
    if (isMobile && showSidebarRef.current) return false;
    return true;
  }, []);

  const markUnreadCleared = useCallback((type, conversationId) => {
    if (!conversationId) return;
    unreadClearUntilRef.current[type][conversationId] = Date.now() + UNREAD_CLEAR_GUARD_MS;
  }, []);

  const isUnreadCleared = useCallback((type, conversationId) => {
    const clearUntil = unreadClearUntilRef.current[type]?.[conversationId];
    if (!clearUntil) return false;
    if (Date.now() <= clearUntil) return true;
    delete unreadClearUntilRef.current[type][conversationId];
    return false;
  }, []);

  const reconcileAppState = useCallback((reason = 'manual') => {
    if (!user?.id) return Promise.resolve(null);
    if (syncInFlightRef.current) {
      syncTrailingReasonRef.current = reason;
      return syncInFlightRef.current;
    }

    const generation = ++syncGenerationRef.current;
    const requestStartedAt = Date.now();
    const channelsController = new AbortController();
    const dmsController = new AbortController();
    setSyncStatus('updating');
    logBootTiming('app-sync:start', { reason });

    const task = Promise.allSettled([
      withRequestTimeout(
        axios.get(`${BACKEND_URL}/api/channels`, {
          withCredentials: true,
          signal: channelsController.signal,
        }),
        APP_SYNC_TIMEOUT_MS,
        'Channel summary request',
        () => channelsController.abort()
      ),
      withRequestTimeout(
        axios.get(`${BACKEND_URL}/api/dms`, {
          withCredentials: true,
          signal: dmsController.signal,
        }),
        APP_SYNC_TIMEOUT_MS,
        'DM summary request',
        () => dmsController.abort()
      ),
      withRequestTimeout(refreshUser(), APP_SYNC_TIMEOUT_MS, 'Profile refresh'),
    ]).then(([channelsResult, dmsResult, userResult]) => {
      if (generation !== syncGenerationRef.current) return null;

      if (channelsResult.status === 'fulfilled') {
        setChannels((previous) => mergeConversationLists(
          previous,
          channelsResult.value.data || [],
          (channelId) => isUnreadCleared('channel', channelId),
          (channelId) => (channelRealtimeUnreadAtRef.current[channelId] || 0) > requestStartedAt,
          requestStartedAt
        ));
      } else {
        console.error('Channel summary reconciliation failed:', channelsResult.reason);
      }

      if (dmsResult.status === 'fulfilled') {
        const serverDmRows = dmsResult.value.data || [];
        const realtimeResolutionByDm = new Map(serverDmRows.map((row) => [
          row.id,
          getRealtimeUnreadResolution(row.id, row, requestStartedAt),
        ]));
        setDms((previous) => mergeConversationLists(
          previous,
          serverDmRows,
          (dmId) => isUnreadCleared('dm', dmId),
          (dmId) => realtimeResolutionByDm.get(dmId) || false,
          requestStartedAt
        ));
        serverDmRows.forEach((row) => {
          // The summary includes every message at/before its latest preview. Events
          // beyond that preview remain in the ledger and are added as a precise delta.
          pruneRealtimeUnreadLedger(row.id, row.last_message);
          if (row.peer_read_boundary) {
            updateDmCachedMessages(row.id, (cached) => applyDmSeenBoundary(cached, row.peer_read_boundary, user?.id));
          }
        });
      } else {
        console.error('DM summary reconciliation failed:', dmsResult.reason);
      }

      if (userResult.status === 'rejected') {
        console.error('Current-user reconciliation failed:', userResult.reason);
      }

      const summariesReady = channelsResult.status === 'fulfilled' && dmsResult.status === 'fulfilled';
      setSyncStatus(summariesReady ? 'ready' : 'error');
      logBootTiming('app-sync:complete', { reason, summariesReady });
      return { summariesReady };
    }).catch((error) => {
      if (generation === syncGenerationRef.current) setSyncStatus('error');
      console.error('App reconciliation failed:', error);
      return null;
    });

    const trackedTask = task.finally(() => {
      if (syncInFlightRef.current === trackedTask) {
        syncInFlightRef.current = null;
        const trailingReason = syncTrailingReasonRef.current;
        syncTrailingReasonRef.current = null;
        if (trailingReason) {
          Promise.resolve().then(() => reconcileAppStateRef.current?.(`trailing-${trailingReason}`));
        }
      }
    });
    syncInFlightRef.current = trackedTask;
    return trackedTask;
  }, [user?.id, refreshUser, isUnreadCleared, getRealtimeUnreadResolution, pruneRealtimeUnreadLedger]);
  reconcileAppStateRef.current = reconcileAppState;

  const loadChannels = useCallback(() => reconcileAppState('channel-reconcile'), [reconcileAppState]);
  const loadDms = useCallback(() => reconcileAppState('dm-reconcile'), [reconcileAppState]);

  const updateChannelListFromMessage = useCallback((message, options = {}) => {
    if (!message?.channel_id) return;
    const preview = buildConversationPreview(message);
    if (!preview) return;

    setChannels((prev) => {
      let found = false;
      const next = prev.map((channel) => {
        if (channel.id !== message.channel_id) return channel;
        found = true;
        const samePreview = isSameConversationPreview(channel.last_message, preview);
        const shouldUsePreview = isConversationPreviewNewer(preview, channel.last_message);
        const unreadShouldClear = options.clearUnread || isUnreadCleared('channel', channel.id);
        const shouldIncrementUnread = options.incrementUnread && shouldUsePreview && !samePreview;

        return {
          ...channel,
          last_message: shouldUsePreview ? preview : channel.last_message,
          unread_count: unreadShouldClear
            ? 0
            : shouldIncrementUnread
              ? (channel.unread_count || 0) + 1
              : (channel.unread_count || 0),
        };
      });

      return found ? sortConversationsByActivity(next) : prev;
    });
  }, [isUnreadCleared]);

  const updateDmListFromMessage = useCallback((message, options = {}) => {
    if (!message?.dm_id) return;
    const preview = buildConversationPreview(message);
    if (!preview) return;

    setDms((prev) => {
      let found = false;
      const next = prev.map((dm) => {
        if (dm.id !== message.dm_id) return dm;
        found = true;
        const samePreview = isSameConversationPreview(dm.last_message, preview);
        const shouldUsePreview = isConversationPreviewNewer(preview, dm.last_message);
        const unreadShouldClear = options.clearUnread || isUnreadCleared('dm', dm.id);
        const shouldIncrementUnread = options.incrementUnread && shouldUsePreview && !samePreview;

        return {
          ...dm,
          last_message: shouldUsePreview ? preview : dm.last_message,
          unread_count: unreadShouldClear
            ? 0
            : shouldIncrementUnread
              ? (dm.unread_count || 0) + 1
              : (dm.unread_count || 0),
        };
      });

      return found ? sortConversationsByActivity(next) : prev;
    });
  }, [isUnreadCleared]);

  const clearChannelUnread = useCallback((channelId) => {
    if (!channelId) return;
    markUnreadCleared('channel', channelId);
    setChannels((prev) => prev.map((channel) => (
      channel.id === channelId ? { ...channel, unread_count: 0 } : channel
    )));
  }, [markUnreadCleared]);

  const clearDmUnread = useCallback((dmId) => {
    if (!dmId) return;
    markUnreadCleared('dm', dmId);
    // Reading clears the realtime-unread freshness marker so a later legitimate
    // server response is not blocked from reflecting the read state.
    delete dmRealtimeUnreadAtRef.current[dmId];
    setDms((prev) => prev.map((dm) => (
      dm.id === dmId ? { ...dm, unread_count: 0 } : dm
    )));
  }, [markUnreadCleared]);

  const handleDmReadConfirmed = useCallback((dmId, data, requestStartedAt = Date.now()) => {
    if (!dmId || !data) return;
    markUnreadCleared('dm', dmId);
    pruneRealtimeUnreadLedger(dmId, data);
    const responseUnread = Number.isFinite(Number(data.unread_count)) ? Number(data.unread_count) : 0;
    const unreadAfterSnapshot = getRealtimeUnreadDelta(
      dmId,
      data.unread_snapshot_boundary || data,
      requestStartedAt
    );
    const resolvedUnread = responseUnread + unreadAfterSnapshot;
    if (resolvedUnread === 0) delete dmRealtimeUnreadAtRef.current[dmId];
    else delete unreadClearUntilRef.current.dm[dmId];
    setDms((previous) => previous.map((row) => (
      row.id === dmId
        ? {
            ...row,
            my_read_boundary: advanceBoundary(row.my_read_boundary, data),
            unread_count: resolvedUnread,
          }
        : row
    )));
  }, [getRealtimeUnreadDelta, markUnreadCleared, pruneRealtimeUnreadLedger]);

  // Ensure an INACTIVE DM shows as unread without a network refetch. `new_dm_message`
  // already incremented the count; this is the reconciliation fallback for the
  // dm_unread_update event so we never trigger a full loadDms() (whose slower merge
  // was overwriting the freshly-incremented unread ~1s later). It only guarantees
  // the row is at least 1 unread when the DM is not open and was not just read; it
  // never lowers a higher count and never fires when the DM is the active one.
  const ensureDmUnread = useCallback((dmId) => {
    if (!dmId) return;
    if (isUnreadCleared('dm', dmId)) return; // user just opened/read it — respect that
    // Mark realtime unread freshness so an in-flight loadDms() cannot roll this back.
    dmRealtimeUnreadAtRef.current[dmId] = Date.now();
    setDms((prev) => {
      let changed = false;
      const next = prev.map((dm) => {
        if (dm.id !== dmId) return dm;
        if ((dm.unread_count || 0) >= 1) return dm; // new_dm_message already counted it
        changed = true;
        return { ...dm, unread_count: 1 };
      });
      return changed ? next : prev;
    });
  }, [isUnreadCleared]);

  const updateChannelPreviewEdit = useCallback((data) => {
    if (!data?.channel_id || !data?.id) return;
    setChannels((prev) => prev.map((channel) => (
      channel.id === data.channel_id && isSameConversationPreview(channel.last_message, { id: data.id })
        ? {
            ...channel,
            last_message: {
              ...channel.last_message,
              content: data.content,
              edited: true,
              edited_at: data.edited_at,
              message_type: channel.last_message?.message_type || 'text',
            },
          }
        : channel
    )));
  }, []);

  const updateDmPreviewEdit = useCallback((data) => {
    if (!data?.dm_id || !data?.id) return;
    setDms((prev) => prev.map((dm) => (
      dm.id === data.dm_id && isSameConversationPreview(dm.last_message, { id: data.id })
        ? {
            ...dm,
            last_message: {
              ...dm.last_message,
              content: data.content,
              edited: true,
              edited_at: data.edited_at,
              message_type: dm.last_message?.message_type || 'text',
            },
          }
        : dm
    )));
  }, []);

  const clearChannelPreviewForDeletedMessage = useCallback((data) => {
    if (!data?.channel_id || !data?.message_id) return false;
    let cleared = false;
    setChannels((prev) => prev.map((channel) => {
      const previewKeys = getPreviewIdentityKeys(channel.last_message);
      if (
        channel.id !== data.channel_id
        || (previewKeys.length > 0 && !isSameConversationPreview(channel.last_message, { id: data.message_id }))
      ) {
        return channel;
      }
      cleared = true;
      return { ...channel, last_message: null };
    }));
    return cleared;
  }, []);

  const clearDmPreviewForDeletedMessage = useCallback((data) => {
    if (!data?.dm_id || !data?.message_id) return false;
    let cleared = false;
    setDms((prev) => prev.map((dm) => {
      const previewKeys = getPreviewIdentityKeys(dm.last_message);
      if (
        dm.id !== data.dm_id
        || (previewKeys.length > 0 && !isSameConversationPreview(dm.last_message, { id: data.message_id }))
      ) {
        return dm;
      }
      cleared = true;
      return { ...dm, last_message: null };
    }));
    return cleared;
  }, []);

  useEffect(() => {
    const cachedChannels = getListCache('whisper_channels_cache');
    const cachedDms = getListCache('whisper_dms_cache');

    if (cachedChannels.length) {
      setChannels(cachedChannels);
    }

    if (cachedDms.length) {
      setDms(cachedDms);
    }

    reconcileAppState('startup');
  }, [reconcileAppState]);

  // Update total unread count
  useEffect(() => {
    const channelUnread = channels.reduce((sum, ch) => sum + (ch.unread_count || 0), 0);
    const dmUnread = dms.reduce((sum, dm) => sum + (dm.unread_count || 0), 0);
    setTotalUnreadCount(channelUnread + dmUnread);
  }, [channels, dms, setTotalUnreadCount]);

  // Total unread DMs for the Messages-tab indicator. Driven by the `dms` list, so it
  // updates instantly on realtime arrival and clears only when every DM is read.
  const totalDmUnread = useMemo(
    () => dms.reduce((sum, dm) => sum + (dm.unread_count || 0), 0),
    [dms]
  );


  useEffect(() => {
    saveListCache('whisper_channels_cache', channels);
  }, [channels]);

  useEffect(() => {
    saveListCache('whisper_dms_cache', dms);
  }, [dms]);

  // Keep the open DM's summary/profile/presence aligned with the batched list
  // reconciliation without remounting its message history.
  useEffect(() => {
    setSelectedDm((previous) => {
      if (!previous?.id) return previous;
      const fresh = dms.find((row) => row.id === previous.id);
      return fresh ? { ...previous, ...fresh } : previous;
    });
  }, [dms]);

  useEffect(() => {
    const reconcileForeground = (reason) => {
      // Coalesce burst focus/visibility/online events. Each reconcile fetches
      // /api/channels + /api/dms + /api/auth/me AND bumps refreshSignal, which
      // re-fetches the open conversation's history. Alt-tabbing around used to
      // fire a whole batch of HTTP requests per event; now at most one per
      // FOREGROUND_SYNC_MIN_INTERVAL_MS. Realtime delivery is unaffected (the
      // socket owns live updates).
      const now = Date.now();
      if (now - lastForegroundSyncAtRef.current < FOREGROUND_SYNC_MIN_INTERVAL_MS) return;
      lastForegroundSyncAtRef.current = now;
      reconcileAppState(reason);
      setRefreshSignal((prev) => prev + 1);
    };

    const scheduleStableForegroundAuthorization = () => {
      if (!wasBackgroundedRef.current) return;
      const token = ++foregroundResumeTokenRef.current;
      const scheduleFrame = typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => window.setTimeout(callback, 0);
      scheduleFrame(() => scheduleFrame(() => {
        if (token !== foregroundResumeTokenRef.current) return;
        if (!wasBackgroundedRef.current || !isDocumentActivelyViewed()) return;
        const dmId = selectedDmIdRef.current;
        const isMobile = window.matchMedia
          ? window.matchMedia('(max-width: 767px)').matches
          : window.innerWidth < 768;
        if (!dmId || activeTabRef.current !== 'dms' || (isMobile && showSidebarRef.current)) return;
        wasBackgroundedRef.current = false;
        activateDmRead(dmId, { forceNew: true });
      }));
    };

    const noteBackgrounded = () => {
      wasBackgroundedRef.current = true;
      foregroundResumeTokenRef.current += 1;
      revokeDmReadAuthorization();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reconcileForeground('visibility');
        scheduleStableForegroundAuthorization();
      } else {
        noteBackgrounded();
      }
    };

    const handleFocus = () => {
      reconcileForeground('focus');
      scheduleStableForegroundAuthorization();
    };
    const handleBlur = () => noteBackgrounded();
    const handlePageHide = () => noteBackgrounded();
    const handlePageShow = () => {
      reconcileForeground('pageshow');
      scheduleStableForegroundAuthorization();
    };
    const handleOnline = () => reconcileForeground('online');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('online', handleOnline);
      foregroundResumeTokenRef.current += 1;
      dmReadAuthorizationRef.current = { dmId: null, seq: dmReadAuthorizationRef.current.seq };
    };
  }, [activateDmRead, reconcileAppState, revokeDmReadAuthorization]);

  // On every connect EDGE (false→true), reconcile once: reload the DM/channel lists
  // and refresh the open conversation. This covers both the FIRST authenticated
  // connect (so DMs received while offline surface as soon as the socket is ready,
  // even if the mount-time loadDms() raced session readiness) AND later reconnects.
  // The generation guard in loadDms/loadChannels drops any superseded response, so
  // this can't fight the mount fetch.
  const prevAuthenticatedRef = useRef(false);
  useEffect(() => {
    const becameAuthenticated = authenticated && !prevAuthenticatedRef.current;
    if (becameAuthenticated) {
      reconcileAppState('socket-authenticated');
      setRefreshSignal((prev) => prev + 1);
    }
    prevAuthenticatedRef.current = authenticated;
  }, [authenticated, reconcileAppState]);

  // Keep sidebar rows fresh from realtime events; fetches are reconciliation only.
  useEffect(() => {
    const unsubNewMessage = onNewMessage((message) => {
      if (!message?.channel_id) return;
      // A realtime message is proof the sender is online: heal any stale
      // presence state immediately (authoritative user_offline still wins).
      if (message.sender_id !== user?.id) noteUserOnline(message.sender_id);
      const isActiveChannel = message.channel_id === selectedChannel?.id;
      if (message.sender_id !== user?.id && !isActiveChannel) {
        channelRealtimeUnreadAtRef.current[message.channel_id] = Date.now();
      }
      updateChannelListFromMessage(message, {
        clearUnread: isActiveChannel,
        incrementUnread: message.sender_id !== user?.id && !isActiveChannel,
      });
      if (isActiveChannel) clearChannelUnread(message.channel_id);
    });

    const unsubNewDmMessage = onNewDmMessage?.((message) => {
      if (!message?.dm_id) return;
      // "Active" for READ purposes means the DM is not just selected but genuinely
      // on screen and focused (and not covered by the mobile sidebar). A selected-
      // but-hidden/unfocused DM must stay unread.
      const isSelectedDm = message.dm_id === selectedDm?.id;
      const isActiveDm = isSelectedDm && isDmActivelyViewed(message.dm_id);
      const isIncoming = message.sender_id !== user?.id;
      // A realtime message is proof the sender is online: heal any stale
      // presence state immediately (authoritative user_offline still wins).
      if (isIncoming) noteUserOnline(message.sender_id);

      if (isActiveDm && isIncoming) {
        // The open DmChatArea merges this message into state and refreshes the
        // list preview itself (its own handler calls onConversationMessage with
        // clearUnread). Previously this page-scope handler ALSO did a
        // synchronous localStorage cache write + a second list update for the
        // same message. Only guard the unread here so a racing list reconcile
        // can't resurrect the badge before the mark-read round-trip confirms.
        clearDmUnread(message.dm_id);
        return;
      }

      // Keep INACTIVE DMs' session cache warm, and also own echoes on the
      // SELECTED DM (its chat-area handler deliberately ignores own echoes, so
      // nothing else would record a message sent from another device). Skipped
      // for INCOMING messages on a merely-selected DM because its mounted chat
      // area already merges them into state and refreshes the session cache.
      if (!isSelectedDm || !isIncoming) {
        updateDmCachedMessages(message.dm_id, (cached) => mergeMessagesByIdentity(cached, [{
          ...message,
          _realtime_received_at: Date.now(),
        }]));
      }

      const messageBoundary = normalizeBoundary(message);
      const knownReadBoundary = dmsByIdRef.current.get(String(message.dm_id))?.my_read_boundary;
      const isAfterKnownReadBoundary = Boolean(messageBoundary) && (
        !knownReadBoundary || compareBoundaries(messageBoundary, knownReadBoundary) > 0
      );
      const isNewCanonicalUnread = isIncoming && isAfterKnownReadBoundary
        ? rememberRealtimeDmUnread(message)
        : false;
      const incrementUnread = isIncoming && !isActiveDm && isNewCanonicalUnread;
      // Realtime unread just created for a non-actively-viewed DM → mark freshness so
      // an in-flight loadDms() cannot roll it back down.
      if (incrementUnread) dmRealtimeUnreadAtRef.current[message.dm_id] = Date.now();
      updateDmListFromMessage(message, {
        clearUnread: isActiveDm,
        incrementUnread,
      });
      if (isActiveDm) clearDmUnread(message.dm_id);
    });

    const unsubMessageEdited = onMessageEdited?.((data) => {
      updateChannelPreviewEdit(data);
    });

    const unsubDmMessageEdited = onDmMessageEdited?.((data) => {
      updateDmPreviewEdit(data);
      updateDmCachedMessages(data?.dm_id, (cached) => cached.map((message) => (
        message.id === data?.id
          ? { ...message, content: data.content, edited: true, edited_at: data.edited_at }
          : message
      )));
    });

    const unsubMessageDeleted = onMessageDeleted?.((data) => {
      clearChannelPreviewForDeletedMessage(data);
      loadChannels();
    });

    const unsubDmMessageDeleted = onDmMessageDeleted?.((data) => {
      clearDmPreviewForDeletedMessage(data);
      updateDmCachedMessages(data?.dm_id, (cached) => cached.filter(
        (message) => !getMessageIdentityKeys(message).includes(String(data?.message_id))
      ));
      removeMessageFromCache(`whisper_dm_messages_${data?.dm_id}`, data?.message_id);
      loadDms();
    });

    const unsubDmUnread = onDmUnreadUpdate?.((data) => {
      if (data.dm_id === selectedDm?.id && isDmActivelyViewed(data.dm_id)) {
        // The correct DM is genuinely open AND on screen → mark read.
        clearDmUnread(data.dm_id);
        return;
      }
      // Not actively viewed (different DM, Channels tab, hidden/unfocused tab, or the
      // mobile sidebar covering the chat): keep it unread WITHOUT a network refetch.
      // Receiving a message is not reading it. new_dm_message already incremented the
      // count; this only guarantees the unread state survives. Ignore our own sends.
      if (data.from_user_id && String(data.from_user_id) === String(user?.id)) return;
      ensureDmUnread(data.dm_id);
    });

    // A brand-new DM (first-ever message from someone new) did not exist when we
    // authenticated, so no new_dm_message reaches us and the list-updaters (which
    // only UPDATE existing rows) can't surface it. The backend now emits `dm_created`
    // to our personal room with a full conversation row — insert it if we don't
    // already have it. Dedup by id so repeat first-messages / reconnects never create
    // duplicates. unread stays as the server sent it (0); the accompanying
    // dm_unread_update → ensureDmUnread marks it unread, keeping it unread until the
    // DM is genuinely opened.
    const unsubDmCreated = onDmCreated?.((row) => {
      if (!row?.id) return;
      joinDm(row.id);
      setDms((prev) => {
        if (prev.some((dm) => dm.id === row.id)) return prev; // already present
        return sortConversationsByActivity([
          { ...row, unread_count: row.unread_count || 0, _local_created_at: Date.now() },
          ...prev,
        ]);
      });
    });

    // Receipt listeners live at page scope as well as in the mounted conversation,
    // so inactive-DM caches reconcile immediately and reopen with current status.
    const unsubSeenCache = onDmMessagesSeen?.((data) => {
      if (!data?.dm_id) return;
      const readerIsCurrentUser = String(data.user_id) === String(user?.id);
      if (readerIsCurrentUser) {
        pruneRealtimeUnreadLedger(data.dm_id, data);
        const responseUnread = Number.isFinite(Number(data.unread_count)) ? Number(data.unread_count) : 0;
        const unreadAfterSnapshot = getRealtimeUnreadDelta(
          data.dm_id,
          data.unread_snapshot_boundary || data,
          0
        );
        const resolvedUnread = responseUnread + unreadAfterSnapshot;
        if (resolvedUnread === 0) {
          delete dmRealtimeUnreadAtRef.current[data.dm_id];
        } else {
          delete unreadClearUntilRef.current.dm[data.dm_id];
        }
        setDms((previous) => previous.map((row) => (
          row.id === data.dm_id
            ? {
                ...row,
                my_read_boundary: advanceBoundary(row.my_read_boundary, data),
                unread_count: resolvedUnread,
              }
            : row
        )));
        return;
      }
      updateDmCachedMessages(data.dm_id, (cached) => applyDmSeenBoundary(cached, data, user?.id));
      setDms((previous) => previous.map((row) => (
        row.id === data.dm_id
          ? { ...row, peer_read_boundary: advanceBoundary(row.peer_read_boundary, data) }
          : row
      )));
    });

    const unsubDeliveredCache = onDmMessagesDelivered?.((data) => {
      if (String(data?.user_id) === String(user?.id)) return;
      const dmIds = Array.isArray(data?.dm_ids) && data.dm_ids.length
        ? data.dm_ids
        : [data?.dm_id].filter(Boolean);
      dmIds.forEach((dmId) => {
        updateDmCachedMessages(dmId, (cached) => applyDmDeliveredBoundary(cached, data, user?.id));
      });
    });

    const unsubProfileUpdated = onUserProfileUpdated?.((data) => {
      setDms((prev) => applyProfileUpdateToConversationUsers(prev, data));
      setSelectedDm((prev) => (
        prev?.other_user?.id === data.user_id
          ? { ...prev, other_user: { ...prev.other_user, ...getProfileMetaPatch(data) } }
          : prev
      ));
    });

    return () => {
      unsubNewMessage();
      unsubNewDmMessage?.();
      unsubMessageEdited?.();
      unsubDmMessageEdited?.();
      unsubMessageDeleted?.();
      unsubDmMessageDeleted?.();
      unsubDmUnread?.();
      unsubDmCreated?.();
      unsubSeenCache?.();
      unsubDeliveredCache?.();
      unsubProfileUpdated?.();
    };
  }, [
    onNewMessage,
    onNewDmMessage,
    onMessageEdited,
    onDmMessageEdited,
    onMessageDeleted,
    onDmMessageDeleted,
    onDmUnreadUpdate,
    onDmCreated,
    joinDm,
    onDmMessagesSeen,
    onDmMessagesDelivered,
    onUserProfileUpdated,
    connected,
    user?.id,
    selectedChannel?.id,
    selectedDm?.id,
    updateChannelListFromMessage,
    updateDmListFromMessage,
    updateChannelPreviewEdit,
    updateDmPreviewEdit,
    clearChannelPreviewForDeletedMessage,
    clearDmPreviewForDeletedMessage,
    clearChannelUnread,
    clearDmUnread,
    ensureDmUnread,
    isDmActivelyViewed,
    noteUserOnline,
    rememberRealtimeDmUnread,
    pruneRealtimeUnreadLedger,
    getRealtimeUnreadDelta,
  ]);

  const handleChannelPreviewDeleted = useCallback((data) => {
    clearChannelPreviewForDeletedMessage(data);
    if (data?.reconcile !== false) loadChannels();
  }, [clearChannelPreviewForDeletedMessage, loadChannels]);

  const handleDmPreviewDeleted = useCallback((data) => {
    clearDmPreviewForDeletedMessage(data);
    if (data?.reconcile !== false) loadDms();
  }, [clearDmPreviewForDeletedMessage, loadDms]);

  const handleChannelCreated = (channel) => {
    revokeDmReadAuthorization();
    selectedDmIdRef.current = null;
    const localChannel = { ...channel, _local_created_at: Date.now() };
    setChannels(prev => sortConversationsByActivity([...prev, localChannel]));
    setSelectedChannel(localChannel);
    setSelectedDm(null);
    setShowCreateModal(false);
  };

  const handleChannelJoined = (channel) => {
    revokeDmReadAuthorization();
    selectedDmIdRef.current = null;
    if (!channels.find(c => c.id === channel.id)) {
      setChannels(prev => sortConversationsByActivity([...prev, { ...channel, _local_created_at: Date.now() }]));
    }
    setSelectedChannel(channel);
    setSelectedDm(null);
    setShowJoinModal(false);
  };

  const handleDmCreated = (dm) => {
    if (!dm?.id) return;
    // Canonical single-row insert: dedup by id INSIDE the functional updater so this
    // (HTTP POST /api/dms response) can never race the dm_created socket event into a
    // duplicate row. If the row already exists, merge the fresh fields over it (keep a
    // newer last_message/unread if present) instead of appending a second row.
    setDms((prev) => {
      const idx = prev.findIndex((d) => d.id === dm.id);
      if (idx === -1) {
        return sortConversationsByActivity([...prev, { ...dm, _local_created_at: Date.now() }]);
      }
      const next = prev.slice();
      const existingRow = next[idx];
      next[idx] = {
        ...existingRow,
        ...dm,
        // Never regress a preview/unread the realtime path may have already set.
        last_message: dm.last_message || existingRow.last_message,
        unread_count: existingRow.unread_count || dm.unread_count || 0,
      };
      return sortConversationsByActivity(next);
    });
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    selectedDmIdRef.current = dm.id;
    if (isMobile) {
      showSidebarRef.current = false;
      setShowSidebar(false);
    }
    activateDmRead(dm.id, { forceNew: true });
    setSelectedDm(dm);
    setSelectedChannel(null);
    setActiveTab('dms');
    clearDmUnread(dm.id);
  };

  const handleSelectChannel = (channel) => {
    revokeDmReadAuthorization();
    selectedDmIdRef.current = null;
    setSelectedChannel(channel);
    setSelectedDm(null);
    clearChannelUnread(channel.id);
    if (window.innerWidth < 768) setShowSidebar(false);
  };

  const handleSelectDm = (dm) => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    selectedDmIdRef.current = dm.id;
    if (isMobile) showSidebarRef.current = false;
    activateDmRead(dm.id, { forceNew: true });
    setSelectedDm(dm);
    setSelectedChannel(null);
    clearDmUnread(dm.id);
    if (isMobile) setShowSidebar(false);
  };

  const handleBack = () => {
    revokeDmReadAuthorization();
    selectedDmIdRef.current = null;
    setSelectedChannel(null);
    setSelectedDm(null);
    setShowSidebar(true);
    reconcileAppState('back');
  };

  const handleShowChannelsTab = () => {
    revokeDmReadAuthorization();
    setActiveTab('channels');
  };

  const handleShowDmsTab = () => {
    setActiveTab('dms');
  };

  const handleLogout = () => {
    revokeDmReadAuthorization();
    selectedDmIdRef.current = null;
    dmRealtimeUnreadIdsRef.current = {};
    logout();
  };

  const handleChannelDeleted = (channelId) => {
    setChannels(prev => prev.filter(c => c.id !== channelId));
    if (selectedChannel?.id === channelId) {
      setSelectedChannel(null);
    }
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredChannels = useMemo(() => {
    if (!normalizedQuery) return channels;
    return channels.filter((c) => (c.name || '').toLowerCase().includes(normalizedQuery));
  }, [channels, normalizedQuery]);
  const dmsWithPresence = useMemo(() => dms.map((dm) => ({
    ...dm,
    other_user: overlayUserPresence(dm.other_user, presenceByUser),
  })), [dms, presenceByUser]);
  const filteredDms = useMemo(() => {
    if (!normalizedQuery) return dmsWithPresence;
    return dmsWithPresence.filter((d) => (d.other_user?.username || '').toLowerCase().includes(normalizedQuery));
  }, [dmsWithPresence, normalizedQuery]);
  const selectedDmWithPresence = useMemo(() => selectedDm ? {
    ...selectedDm,
    other_user: overlayUserPresence(selectedDm.other_user, presenceByUser),
  } : null, [selectedDm, presenceByUser]);

  return (
    <div
      className="fixed bg-surface-0 flex overflow-hidden overscroll-none"
      style={{
        top: 'var(--whisper-viewport-top, 0px)',
        left: 0,
        width: '100%',
        maxWidth: '100%',
        height: 'var(--whisper-app-height, 100dvh)',
      }}
      data-testid="chat-page"
    >
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            initial={{ x: -320 }}
            animate={{ x: 0 }}
            exit={{ x: -320 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="w-full md:w-[340px] h-full bg-surface-1 border-r border-border-subtle flex flex-col absolute md:relative z-20 pt-safe"
          >
            {/* Brand + profile header */}
            <div className="px-3 pt-3 pb-2 border-b border-border-subtle">
              <div className="flex items-center gap-2.5 px-1.5 mb-3">
                <div className="w-9 h-9 bg-brand-gradient rounded-2xl flex items-center justify-center shadow-elev-sm">
                  <MessageCircle className="w-[18px] h-[18px] text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg font-bold text-white font-heading tracking-tight leading-tight">Whisper</h1>
                  {syncStatus === 'updating' && (
                    <p className="text-[11px] leading-tight text-cyan-300" role="status" data-testid="app-sync-status">Updating…</p>
                  )}
                  {syncStatus === 'error' && (
                    <button
                      type="button"
                      onClick={() => reconcileAppState('retry')}
                      className="text-[11px] leading-tight text-amber-300 hover:text-amber-200"
                      data-testid="app-sync-retry"
                    >
                      Update failed · Retry
                    </button>
                  )}
                </div>
              </div>

              {/* User Profile */}
              <div className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/[0.03] transition-colors">
                <Avatar src={user?.profile_picture_thumb || user?.avatar_thumb || user?.thumbnail_url} username={user?.username} size="md" isOnline={true} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[15px] text-white truncate leading-tight">{user?.username}</p>
                  <p className="text-xs text-emerald-400 flex items-center gap-1 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online
                  </p>
                </div>
                <button onClick={() => setShowSettingsModal(true)} className="ui-icon-btn ui-icon-btn-sm" aria-label="Settings" data-testid="settings-btn">
                  <Settings className="w-[18px] h-[18px]" />
                </button>
                <button onClick={handleLogout} className="ui-icon-btn ui-icon-btn-sm" aria-label="Log out" data-testid="logout-btn">
                  <LogOut className="w-[18px] h-[18px]" />
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="px-3 pt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={activeTab === 'channels' ? 'Search channels' : 'Search messages'}
                  className="ui-input h-10 pl-9 pr-9 rounded-xl text-sm"
                  data-testid="sidebar-search"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="ui-icon-btn ui-icon-btn-sm absolute right-1 top-1/2 -translate-y-1/2"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Segmented tabs */}
            <div className="px-3 pt-3">
              <div className="flex gap-1 p-1 bg-surface-2 rounded-xl">
                <button
                  onClick={handleShowChannelsTab}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'channels' ? 'bg-surface-3 text-white shadow-elev-sm' : 'text-zinc-400 hover:text-zinc-200'}`}
                  data-testid="channels-tab"
                >
                  <Hash className="w-4 h-4" /> Channels
                </button>
                <button
                  onClick={handleShowDmsTab}
                  className={`relative flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'dms' ? 'bg-surface-3 text-white shadow-elev-sm' : 'text-zinc-400 hover:text-zinc-200'}`}
                  data-testid="dms-tab"
                >
                  <MessageSquare className="w-4 h-4" /> Messages
                  {totalDmUnread > 0 && (
                    <span
                      className="ml-1 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-cyan-500 px-1.5 text-[11px] font-semibold text-white leading-none"
                      data-testid="messages-unread-badge"
                      aria-label={`${totalDmUnread} unread direct messages`}
                    >
                      {totalDmUnread > 99 ? '99+' : totalDmUnread}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="px-3 pt-3 flex gap-2">
              {activeTab === 'channels' ? (
                <>
                  <button onClick={() => setShowCreateModal(true)} className="ui-btn ui-btn-primary flex-1" data-testid="create-channel-modal-btn">
                    <Plus className="w-4 h-4" /> Create
                  </button>
                  <button onClick={() => setShowJoinModal(true)} className="ui-btn ui-btn-secondary flex-1" data-testid="join-channel-modal-btn">
                    <Search className="w-4 h-4" /> Join
                  </button>
                </>
              ) : (
                <button onClick={() => setShowNewDmModal(true)} className="ui-btn ui-btn-primary w-full" data-testid="new-dm-btn">
                  <Plus className="w-4 h-4" /> New Message
                </button>
              )}
            </div>

            {/* List */}
            {/* key={activeTab} forces a full remount when switching tabs so React
                never reconciles a Channels row into a DMs row (or vice-versa) across
                the ternary — which briefly showed a DM participant inside Channels. */}
            <div key={activeTab} className="flex-1 overflow-y-auto px-2 pt-3 pb-2 mt-1">
              {activeTab === 'channels' ? (
                <>
                  {filteredChannels.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <div className="w-14 h-14 bg-surface-2 rounded-2xl flex items-center justify-center mx-auto mb-3">
                        <Hash className="w-6 h-6 text-zinc-600" />
                      </div>
                      <p className="text-zinc-300 text-sm font-medium">{normalizedQuery ? 'No channels found' : 'No channels yet'}</p>
                      <p className="text-zinc-500 text-xs mt-1">{normalizedQuery ? 'Try a different search' : 'Create or join a channel to start'}</p>
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {filteredChannels.map((channel, index) => {
                        const isSelected = selectedChannel?.id === channel.id;
                        return (
                        <motion.button
                          key={channel.id}
                          layout="position"
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.16, delay: Math.min(index * 0.015, 0.08), ease: 'easeOut' }}
                          whileTap={{ scale: 0.985 }}
                          onClick={() => handleSelectChannel(channel)}
                          className={`group relative w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-colors text-left ${isSelected ? 'bg-surface-3 text-white' : 'hover:bg-white/[0.04] text-zinc-300'}`}
                          data-testid={`channel-${channel.id}`}
                        >
                          {isSelected && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-brand-gradient" />}
                          <div className="w-11 h-11 bg-brand-gradient-soft rounded-2xl flex items-center justify-center flex-shrink-0 ring-1 ring-inset ring-white/[0.08]">
                            <Hash className="w-5 h-5 text-cyan-300" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`flex-1 truncate text-[15px] ${isSelected || channel.unread_count > 0 ? 'font-semibold text-white' : 'font-medium'}`}>{channel.name}</p>
                              {channel.last_message?.created_at && (
                                <span className={`text-[11px] shrink-0 ${channel.unread_count > 0 ? 'text-cyan-300 font-medium' : 'text-zinc-500'}`}>
                                  {formatConversationTimestamp(channel.last_message.created_at)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {channel.last_message ? (
                                <p className={`flex-1 text-[13px] truncate ${channel.unread_count > 0 ? 'text-zinc-300' : 'text-zinc-500'}`}>
                                  <MediaPreview messageType={channel.last_message.message_type} fallbackText={channel.last_message.content} />
                                </p>
                              ) : (
                                <p className="flex-1 text-[13px] text-zinc-600 truncate">No messages yet</p>
                              )}
                              <AnimatePresence initial={false}>
                                {channel.unread_count > 0 && (
                                  <motion.span
                                    key={`channel-unread-${channel.unread_count}`}
                                    initial={{ opacity: 0, scale: 0.7 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.7 }}
                                    transition={{ duration: 0.14, ease: 'easeOut' }}
                                    className="shrink-0 min-w-[20px] h-5 px-1.5 bg-brand-gradient rounded-full text-[11px] text-white flex items-center justify-center font-semibold shadow-elev-sm"
                                  >
                                    {channel.unread_count > 99 ? '99+' : channel.unread_count}
                                  </motion.span>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </motion.button>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {filteredDms.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <div className="w-14 h-14 bg-surface-2 rounded-2xl flex items-center justify-center mx-auto mb-3">
                        <MessageSquare className="w-6 h-6 text-zinc-600" />
                      </div>
                      <p className="text-zinc-300 text-sm font-medium">{normalizedQuery ? 'No conversations found' : 'No messages yet'}</p>
                      <p className="text-zinc-500 text-xs mt-1">{normalizedQuery ? 'Try a different search' : 'Start a conversation with someone'}</p>
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {filteredDms.map((dm, index) => {
                        const isSelected = selectedDm?.id === dm.id;
                        return (
                        <motion.button
                          key={dm.id}
                          layout="position"
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.16, delay: Math.min(index * 0.015, 0.08), ease: 'easeOut' }}
                          whileTap={{ scale: 0.985 }}
                          onClick={() => handleSelectDm(dm)}
                          className={`group relative w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-colors text-left ${isSelected ? 'bg-surface-3 text-white' : 'hover:bg-white/[0.04] text-zinc-300'}`}
                          data-testid={`dm-${dm.id}`}
                        >
                          {isSelected && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-brand-gradient" />}
                          <Avatar src={dm.other_user?.profile_picture_thumb || dm.other_user?.avatar_thumb || dm.other_user?.thumbnail_url} username={dm.other_user?.username} size="lg" isOnline={dm.other_user?.is_online} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`flex-1 truncate text-[15px] ${isSelected || dm.unread_count > 0 ? 'font-semibold text-white' : 'font-medium'}`}>{dm.other_user?.username}</p>
                              {dm.last_message?.created_at && (
                                <span className={`text-[11px] shrink-0 ${dm.unread_count > 0 ? 'text-cyan-300 font-medium' : 'text-zinc-500'}`}>
                                  {formatConversationTimestamp(dm.last_message.created_at)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {dm.last_message ? (
                                <p className={`flex-1 text-[13px] truncate ${dm.unread_count > 0 ? 'text-zinc-300' : 'text-zinc-500'}`}>
                                  <MediaPreview messageType={dm.last_message.message_type} fallbackText={dm.last_message.content} youPrefix={dm.last_message.sender_id === user?.id} />
                                </p>
                              ) : (
                                <p className="flex-1 text-[13px] text-zinc-600 truncate">No messages yet</p>
                              )}
                              <AnimatePresence initial={false}>
                                {dm.unread_count > 0 && (
                                  <motion.span
                                    key={`dm-unread-${dm.unread_count}`}
                                    initial={{ opacity: 0, scale: 0.7 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.7 }}
                                    transition={{ duration: 0.14, ease: 'easeOut' }}
                                    className="shrink-0 min-w-[20px] h-5 px-1.5 bg-brand-gradient rounded-full text-[11px] text-white flex items-center justify-center font-semibold shadow-elev-sm"
                                  >
                                    {dm.unread_count > 99 ? '99+' : dm.unread_count}
                                  </motion.span>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </motion.button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Area */}
      {selectedDm ? (
        <DmChatArea
          dm={selectedDmWithPresence}
          onBack={handleBack}
          refreshSignal={refreshSignal}
          readActivationSeq={dmReadActivation.seq}
          isReadAuthorized={isDmReadAuthorized}
          requestReadAuthorization={requestDmReadAuthorization}
          onReadConfirmed={handleDmReadConfirmed}
          sidebarCovering={showSidebar}
          onConversationMessage={updateDmListFromMessage}
          onConversationMessageEdited={updateDmPreviewEdit}
          onConversationMessageDeleted={handleDmPreviewDeleted}
        />
      ) : (
        <ChannelChatArea
          channel={selectedChannel}
          onBack={handleBack}
          onChannelDeleted={handleChannelDeleted}
          refreshSignal={refreshSignal}
          onConversationMessage={updateChannelListFromMessage}
          onConversationMessageEdited={updateChannelPreviewEdit}
          onConversationMessageDeleted={handleChannelPreviewDeleted}
        />
      )}

      {/* Modals */}
      <CreateChannelModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onCreated={handleChannelCreated} />
      <JoinChannelModal isOpen={showJoinModal} onClose={() => setShowJoinModal(false)} onJoined={handleChannelJoined} />
      <NewDmModal isOpen={showNewDmModal} onClose={() => setShowNewDmModal(false)} onDmCreated={handleDmCreated} />
      <ProfileSettingsModal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)} />
    </div>
  );
};

export default ChatPage;
